// liquid-pdp

const logPDP = true

// Shared size-preference utilities (window.TcSizePreferences) now live in global.js —
// it loads site-wide ahead of this file, sections/clearance-v3.liquid, and
// cart-drawer-clearance-addon.js, so it's a single reusable source of truth for all three.

function getPdpTranslations() {
  const el = document.querySelector('.js-pdp-translations')
  if (!el) return {}
  try {
    return JSON.parse(el.textContent)
  } catch {
    return {}
  }
}

function getPdpTranslation(key, fallback = '') {
  const value = getPdpTranslations()?.[key]
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

function escapeHtmlText(text) {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

// "<strong>XX% OFF</strong> [with CODE]" (HTML) from the translation templates; '' when unavailable
// plus " - dealText" when the promo has holiday deal text. Dynamic values are escaped.
function buildManualDiscountAppliedLabel(percentage, code, dealText = '') {
  const key = code ? 'manual_discount_applied_offer' : 'manual_discount_applied_offer_no_code'
  const template = getPdpTranslation(key)
  if (!template) return ''
  const label = template
    .replace('__PERCENTAGE__', () => escapeHtmlText(String(percentage)))
    .replace('__CODE__', () => escapeHtmlText(code || ''))
  return dealText ? `${label} ${escapeHtmlText(dealText)}` : label
}

// Fontana OOS for curated packs (native Shopify bundles). Liquid can't read
// bundle components, so the Storefront API supplies them: one request per
// pack id, cached for the page. A pack variant is Fontana-OOS when ANY of its
// component variants has custom.warehouse_inventory.shipbob_fontana = false.
// TEMP QA (Fontana-OOS): red X/strikethrough for Fontana-only OOS. Off = same white
// X as any OOS (sold-out logic unchanged). Mirrors qa_show_fontana_oos_style in Liquid.
const SHOW_FONTANA_OOS_STYLE = false
const PDP_STOREFRONT_ENDPOINT = '/api/2025-07/graphql.json'
const PDP_STOREFRONT_TOKEN = 'b7cddfa2ac815edc1ce3062ba667fe38'
const PDP_PACK_FONTANA_QUERY = `
  query ($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        handle
        variants(first: 100) {
          nodes {
            id
            requiresComponents
            components(first: 20) {
              nodes {
                productVariant {
                  title
                  availableForSale
                  product { handle }
                  wh: metafield(namespace: "custom", key: "warehouse_inventory") { value }
                }
              }
            }
          }
        }
      }
    }
  }`
// Native bundle pack variant id -> boolean (from components). Plain (non-bundle)
// pack variant ids go in the Set and use their own is_fontana_oos. A bundle
// variant's own metafield is never trusted (blank / {} / stale).
const packVariantFontanaOos = new Map()
const plainPackVariantIds = new Set()
// Pack product id -> in-flight/finished request
const packFontanaRequests = new Map()
// Pack product ids whose lookup has settled (success or failure; failure fails open)
const settledPackFontanaIds = new Set()

function isFontanaOosMetafieldValue(value) {
  try {
    return JSON.parse(value)?.shipbob_fontana === false
  } catch {
    return false
  }
}

function getIdFromGid(gid) {
  return Number(String(gid).split('/').pop())
}

// Component variants of a bundle variant that are Fontana-OOS
function getFontanaOosComponents(variant) {
  return variant.components.nodes
    .filter((component) => isFontanaOosMetafieldValue(component.productVariant?.wh?.value))
    .map((component) => component.productVariant)
}

function isPackVariantFontanaOos(variant) {
  return getFontanaOosComponents(variant).length > 0
}

// Native bundle -> { component handle: [OOS sizes] } (sizes deduped, in order found)
function collectBundleFontanaOos(node) {
  const byComponent = {}
  node.variants.nodes.forEach((variant) => {
    if (!variant.requiresComponents) return
    getFontanaOosComponents(variant).forEach((component) => {
      if (!component.availableForSale) return
      const sizes = (byComponent[component.product.handle] ||= [])
      if (!sizes.includes(component.title)) sizes.push(component.title)
    })
  })
  return byComponent
}

// Plain (non-bundle) pack -> OOS sizes from its own flag (packs_data_json)
function collectPlainPackFontanaOos(pack) {
  return pack.variants
    .filter(
      (variant) =>
        plainPackVariantIds.has(variant.id) && variant.is_fontana_oos && variant.available,
    )
    .map((variant) => variant.title)
}

// Singles -> { handle: [sizes OOS only in Fontana] } from singles_data_json flags
function collectSinglesFontanaOos(singlesData) {
  const bySingle = {}
  singlesData.forEach((single) => {
    const sizes = single.variants
      .filter((variant) => variant.is_fontana_oos && variant.available)
      .map((variant) => variant.title)
    if (sizes.length) bySingle[single.handle] = sizes
  })
  return bySingle
}

// Expandable console object of every Fontana-OOS variant found by a lookup
// Shape: { singles: { handle: [sizes] }, bundles: { bundleHandle: { componentHandle: [sizes] } }, packs: { handle: [sizes] } }
function logFontanaOosSummary(nodes, packsData, singlesData) {
  const summary = { singles: collectSinglesFontanaOos(singlesData), bundles: {}, packs: {} }
  nodes.forEach((node) => {
    if (!node) return
    const components = collectBundleFontanaOos(node)
    if (Object.keys(components).length) summary.bundles[node.handle] = components
    const pack = packsData.find((p) => p.id === getIdFromGid(node.id))
    const plainSizes = pack ? collectPlainPackFontanaOos(pack) : []
    if (plainSizes.length) summary.packs[node.handle] = plainSizes
  })
  console.log(
    `%c[Storefront API] Fontana OOS (${nodes.length} packs checked)`,
    'color: #39ff14; font-weight: bold',
    summary,
  )
}

async function fetchPackFontanaOos(productIds, packsData = [], singlesData = []) {
  const response = await fetch(PDP_STOREFRONT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': PDP_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({
      query: PDP_PACK_FONTANA_QUERY,
      variables: { ids: productIds.map((id) => `gid://shopify/Product/${id}`) },
    }),
  })
  const json = await response.json()
  if (json.errors || !json.data) throw new Error('Pack Fontana query failed')
  json.data.nodes.forEach((node) => {
    node?.variants.nodes.forEach((variant) => {
      const variantId = getIdFromGid(variant.id)
      if (variant.requiresComponents)
        packVariantFontanaOos.set(variantId, isPackVariantFontanaOos(variant))
      else plainPackVariantIds.add(variantId)
    })
  })
  logFontanaOosSummary(json.data.nodes, packsData, singlesData)
}

// Fetches only pack ids not yet requested; failed ids are released to retry later.
function ensurePackFontanaOosLoaded(productIds, packsData, singlesData) {
  const missing = productIds.filter((id) => !packFontanaRequests.has(id))
  if (missing.length) {
    const request = fetchPackFontanaOos(missing, packsData, singlesData)
      .catch((error) => {
        console.error(error)
        missing.forEach((id) => packFontanaRequests.delete(id))
      })
      .finally(() => missing.forEach((id) => settledPackFontanaIds.add(id)))
    missing.forEach((id) => packFontanaRequests.set(id, request))
  }
  return Promise.all(new Set(productIds.map((id) => packFontanaRequests.get(id))))
}

function escapePdpHtml(value) {
  return `${value || ''}`.replace(
    /[&<>"']/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[char],
  )
}

// Mode check that tolerates the shared resolver in snippets/global-js.liquid not
// having run. Quantity mode is the safe default: it is what the PDP rendered
// before tag mode existed.
function isClearanceTagModeSafe() {
  return typeof isClearanceTagMode === 'function' ? isClearanceTagMode() : false
}

// Quantity tiers from the shared config (theme settings > Clearance discount).
// Only meaningful in quantity mode; tag mode has no tiers to progress through.
function getClearanceProgressDiscountTiers() {
  return getClearanceDiscountTierList().map(function (tier) {
    return { quantity: tier.quantity, discount_percentage: tier.percentage }
  })
}

function updateClearanceProgressBar(root = document) {
  const productInfo = root.matches?.('product-info') ? root : root.querySelector?.('product-info')
  if (!productInfo) return

  const banner = productInfo.querySelector('.js-menu-with-pdp-clearance-progress')
  if (!banner) return

  const discountTiers = getClearanceProgressDiscountTiers()
  const clearanceCount = Number(banner.dataset.clearanceCount || 0)
  const bannerText = banner.querySelector('.menu-with-pdp__inline-clearance-progress__text')
  const progressBar = banner.querySelector(
    '.menu-with-pdp__inline-clearance-progress__progress-bar-inner',
  )
  if (!bannerText || !progressBar || discountTiers.length === 0) return

  // Tag mode: the discount is fixed per product, so there is no progress to
  // show. Swap in the static savings line and drop the whole bar.
  const progressBarWrapper = banner.querySelector(
    '.menu-with-pdp__inline-clearance-progress__progress-bar',
  )
  if (isClearanceTagModeSafe()) {
    bannerText.innerHTML = `<span>${window.TC_CLEARANCE_CONFIG?.savingsMessageHtml || ''}</span>`
    if (progressBarWrapper) progressBarWrapper.hidden = true
    return
  }
  if (progressBarWrapper) progressBarWrapper.hidden = false

  const lastDiscountTier = discountTiers[discountTiers.length - 1]
  const t = getPdpTranslations()
  if (clearanceCount >= lastDiscountTier.quantity) {
    bannerText.innerHTML = `<span>${(t.clearance_unlocked_html || 'Clearance: <strong>Extra __PCT__% OFF unlocked!</strong>').replace('__PCT__', lastDiscountTier.discount_percentage)}</span>`
  } else {
    const nextTier = discountTiers.find((tier) => clearanceCount < tier.quantity)
    if (nextTier) {
      const count = nextTier.quantity - clearanceCount
      const template =
        clearanceCount === 0
          ? t.clearance_add_more_html ||
            'Clearance: Add __COUNT__ to get an <strong>Extra __PCT__% OFF</strong>'
          : t.clearance_add_more_with_more_html ||
            'Clearance: Add __COUNT__ more to get an <strong>Extra __PCT__% OFF</strong>'
      bannerText.innerHTML = `<span>${template.replace('__COUNT__', count).replace('__PCT__', nextTier.discount_percentage)}</span>`
    }
  }

  const percentageForEachTier = 100 / discountTiers.length
  let progressBarPercentage = 0

  discountTiers.forEach((tier, index) => {
    if (clearanceCount >= tier.quantity) {
      progressBarPercentage += percentageForEachTier
      return
    }

    if (index === 0) {
      progressBarPercentage += (percentageForEachTier * clearanceCount) / tier.quantity
      return
    }

    const previousTier = discountTiers[index - 1]
    if (clearanceCount > previousTier.quantity) {
      progressBarPercentage +=
        (percentageForEachTier * (clearanceCount - previousTier.quantity)) /
        (tier.quantity - previousTier.quantity)
    }
  })

  progressBar.style.setProperty('--percentage', `${Math.min(progressBarPercentage, 100)}%`)
}

async function refreshClearanceProgressBarCount(root = document) {
  const productInfo = root.matches?.('product-info') ? root : root.querySelector?.('product-info')
  if (!productInfo) return

  const banner = productInfo.querySelector('.js-menu-with-pdp-clearance-progress')
  if (!banner) return

  // Tag mode prices from the product's tags, so the cart count is irrelevant.
  if (isClearanceTagModeSafe()) return updateClearanceProgressBar(productInfo)

  try {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`)
    const cart = await response.json()
    const clearanceCount = Array.isArray(cart.items)
      ? cart.items.reduce((total, item) => {
          return item.properties?._clearance === 'true' ? total + item.quantity : total
        }, 0)
      : 0

    banner.dataset.clearanceCount = String(clearanceCount)
    updateClearanceProgressBar(productInfo)
  } catch (error) {
    console.error('Failed to refresh clearance count:', error)
  }
}

if (!customElements.get('product-style')) {
  class ProductStyles extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback() {
      this.productInfo = this.closest('product-info')
      this.addEventListener('change', (event) => {
        const target = event.target
        const isClearanceMode = this.productInfo?.dataset.showOnlyClearance === 'true'
        const isClearance = isClearanceMode
          ? !!target.dataset.clearanceProductUrl?.trim()
          : target.classList.contains('js-is-clearance')
        if (isClearance) {
          this.productInfo.dataset.productClearance = 'true'
        } else {
          this.productInfo.dataset.productClearance = 'false'
        }
        this.productInfo?.syncPdpQuantitySelector()
        const label = target.parentNode
        if (label) {
          label.classList.add('loading')
          label.querySelector('.loading__spinner')?.classList.remove('hidden')
        }
        publish(PUB_SUB_EVENTS.onStyleChange, {
          data: {
            event,
            target,
            label,
          },
        })
      })
      this.initCompareModalButtons()
    }

    initCompareModalButtons() {
      this.querySelectorAll('.js-open-modal').forEach((button) => {
        button.addEventListener('click', () => {
          const modalTrigger = button.querySelector('general-modal .js-modal-trigger')
          if (modalTrigger) {
            modalTrigger.click()
          }
        })
      })
    }
  }
  customElements.define('product-style', ProductStyles)
}

if (!customElements.get('product-pack-selector')) {
  class ProductPackSelector extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback() {
      this.productInfo = this.closest('product-info')
      // Pointer intent (hover, or the touch that precedes the click) starts the section
      // render early.
      const prefetchFromPointer = (event) => {
        const label = event.target.closest?.('label')
        if (label) this.productInfo?.prefetchPackSection?.(this.getLabelRadio(label))
      }
      this.addEventListener('pointerover', prefetchFromPointer)
      this.addEventListener('pointerdown', prefetchFromPointer)
      this.addEventListener('change', (event) => {
        const target = event.target
        const isClearanceMode = this.productInfo?.dataset.showOnlyClearance === 'true'
        const isClearance = isClearanceMode
          ? !!target.dataset.clearanceProductUrl?.trim()
          : target.classList.contains('js-is-clearance')
        if (isClearance) {
          this.productInfo.dataset.productClearance = 'true'
        } else {
          this.productInfo.dataset.productClearance = 'false'
        }
        this.productInfo?.syncPdpQuantitySelector(null, target.dataset.size)
        const label = target.parentNode
        if (label) {
          label.classList.add('loading')
          label.querySelector('.loading__spinner')?.classList.remove('hidden')
        }
        publish(PUB_SUB_EVENTS.onPackSizeChange, {
          data: {
            event,
            target,
          },
        })
      })
    }

    getLabelRadio(label) {
      return label.htmlFor
        ? this.querySelector(`input[id="${CSS.escape(label.htmlFor)}"]`)
        : label.querySelector('input[type="radio"]')
    }
  }
  customElements.define('product-pack-selector', ProductPackSelector)
}

if (!customElements.get('manual-discount-code')) {
  // cart.js is the source of truth for "is this code applied". The stored flag only bridges
  // the gap while the cart is empty (nothing to verify against), so it expires.
  const MANUAL_DISCOUNT_STATE_TTL_MS = 24 * 60 * 60 * 1000
  const MANUAL_DISCOUNT_EVENT_SOURCE = 'manual-discount-code'

  class ManualDiscountCode extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.pdp-discount-code__button')
      this.status = this.querySelector('.pdp-discount-code__status')
      this.discountCode = this.dataset.discountCode
      this.discountPercentage = Number(this.dataset.discountPercentage || 0)
      this.isApplied = this.dataset.applied === 'true'
      this.applyLabel = this.dataset.applyLabel || ''
      this.appliedLabel = this.dataset.appliedLabel || ''
      this.errorMessage = this.dataset.errorMessage || ''

      if (!this.button) return

      // Auto-apply promo: always shown applied; no apply action, no cart sync.
      // May be codeless (tag-tiered promos), so this runs before the code check.
      if (this.dataset.autoApplied === 'true') {
        this.isApplied = true
        this.updateState()
        this.applyManualDiscountPriceUi()
        return
      }

      if (!this.discountCode) return

      if (this.isApplied) {
        this.persistAppliedState()
      } else if (this.getPersistedAppliedState()) {
        this.isApplied = true
        this.dataset.applied = 'true'
      }

      this.updateState()
      if (this.isApplied) this.applyManualDiscountPriceUi()
      this.button.addEventListener('click', this.handleApplyDiscount, { capture: true })

      // Whatever the PDP was rendered or restored with, the cart gets the last word: right
      // away, after every cart change (an add can land on a cart that never got the code),
      // and when the page comes back from the back/forward cache.
      this.handleCartChange = (event) => {
        if (event?.detail?.source === MANUAL_DISCOUNT_EVENT_SOURCE) return
        this.syncWithCart({ reapply: true })
      }
      this.handlePageShow = (event) => {
        if (event.persisted) this.syncWithCart()
      }
      document.addEventListener('cartaddsuccess', this.handleCartChange)
      window.addEventListener('pageshow', this.handlePageShow)
      this.syncWithCart()
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleApplyDiscount, { capture: true })
      if (this.handleCartChange) document.removeEventListener('cartaddsuccess', this.handleCartChange)
      if (this.handlePageShow) window.removeEventListener('pageshow', this.handlePageShow)
    }

    handleApplyDiscount = async (event) => {
      event?.preventDefault?.()
      event?.stopPropagation?.()
      event?.stopImmediatePropagation?.()

      if (this.isApplied || !this.discountCode) return

      this.button.disabled = true
      this.button.classList.add('is-loading')
      this.button.setAttribute('aria-busy', 'true')

      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(this.discountCode).catch(() => {})
      }

      const MAX_ATTEMPTS = 3
      let lastError

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          await this.requestDiscountCode()

          // /discount/<code> answers 200 even when nothing gets attached to the cart, so
          // this is optimistic: the cart check after the next cart change confirms it.
          this.markAsApplied()

          document.dispatchEvent(
            new CustomEvent('cartaddsuccess', {
              detail: {
                openCart: false,
                refreshCart: true,
                source: MANUAL_DISCOUNT_EVENT_SOURCE,
              },
            }),
          )

          break
        } catch (error) {
          lastError = error
          if (attempt < MAX_ATTEMPTS) {
            await new Promise((resolve) => window.setTimeout(resolve, 1000))
            continue
          }
          console.error('[manual-discount-code]', lastError)
          this.button.disabled = false
          this.button.classList.remove('is-loading')
          this.button.removeAttribute('aria-busy')
          // this.setStatus(this.errorMessage, true)
        }
      }
    }

    async requestDiscountCode() {
      const response = await fetch(
        `${window.Shopify.routes.root}discount/${encodeURIComponent(this.discountCode)}`,
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )

      if (!response.ok) {
        throw new Error(`Discount request failed with status ${response.status}`)
      }
    }

    markAsApplied() {
      this.isApplied = true
      this.dataset.applied = 'true'
      this.persistAppliedState()
      this.updateState()
      this.applyManualDiscountPriceUi()
      requestAnimationFrame(() => this.applyManualDiscountPriceUi())
      window.setTimeout(() => this.applyManualDiscountPriceUi(), 250)
    }

    // Flips the component (and the PDP prices that depend on it) to what the cart says.
    setApplied(applied) {
      if (this.isApplied === applied) {
        if (applied) this.persistAppliedState()
        else this.clearPersistedAppliedState()
        return
      }

      this.isApplied = applied
      this.dataset.applied = applied ? 'true' : 'false'

      if (applied) {
        this.persistAppliedState()
        this.updateState()
        this.applyManualDiscountPriceUi()
        return
      }

      this.clearPersistedAppliedState()
      this.updateState()
      this.closest('product-info')?.resetManualDiscountPriceUi?.()
      this.dispatchEvent(new CustomEvent('manual-discount-code:removed', { bubbles: true }))
    }

    updateState() {
      if (!this.button) return

      this.button.classList.remove('is-loading')
      this.button.removeAttribute('aria-busy')

      if (this.isApplied) {
        this.button.disabled = true
        this.setButtonLabel(this.appliedLabel)
        this.button.classList.add('pdp-discount-code__button--applied')
        this.clearStatus()
      } else {
        this.button.disabled = false
        this.setButtonLabel(this.applyLabel)
        this.button.classList.remove('pdp-discount-code__button--applied')
      }
    }

    // Rebuilds the "Getting XX% OFF" text with the combined discount percentage
    setAppliedLabelPercentage(percentage, savingsFormatted = '') {
      if (savingsFormatted) this.setSavings(savingsFormatted)
      const label = buildManualDiscountAppliedLabel(
        percentage,
        this.discountCode,
        this.dataset.dealText || '',
      )
      if (!label) return

      this.appliedLabel = label
      this.dataset.appliedLabel = label
      if (this.isApplied) this.setButtonLabel(label)
    }

    // "Saving $XX.XX" (applied row + variants): fills the amounts and un-hides their wrappers
    setSavings(savingsFormatted) {
      this.querySelectorAll('.js-discount-code-savings').forEach((el) => {
        el.textContent = savingsFormatted
      })
      this.querySelectorAll('.js-discount-code-savings-wrap').forEach((el) => {
        el.hidden = false
      })
    }

    // Writes into the text span so the icon/check markup is preserved.
    // Labels are trusted/escaped HTML (applied label bolds the percentage).
    setButtonLabel(label) {
      const buttonText = this.button.querySelector('.js-discount-code-button-text')
      if (buttonText) {
        buttonText.innerHTML = label
      } else {
        this.button.textContent = label
      }
    }

    setStatus(message, isError) {
      if (!this.status) return

      this.status.hidden = false
      this.status.classList.remove('visually-hidden')
      this.status.classList.toggle('is-error', isError)
      this.status.textContent = message
    }

    clearStatus() {
      if (!this.status) return

      this.status.hidden = true
      this.status.classList.add('visually-hidden')
      this.status.classList.remove('is-error')
      this.status.textContent = ''
    }

    async readCart() {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      })
      if (!response.ok) return null

      const cart = await response.json()
      return {
        itemCount: Number(cart?.item_count || 0),
        hasCode: this.cartCarriesDiscountCode(cart),
      }
    }

    // A code can sit at cart level (order discounts) or only on lines (product/collection
    // discounts), and the two live in different parts of cart.js.
    cartCarriesDiscountCode(cart) {
      const code = this.discountCode.trim().toLowerCase()
      const matches = (application) =>
        `${application?.title || ''}`.split(':')[0].trim().toLowerCase() === code

      if ((cart?.cart_level_discount_applications || []).some(matches)) return true

      return (cart?.items || []).some(
        (item) =>
          (item?.discounts || []).some(matches) ||
          (item?.discount_allocations || []).some((allocation) =>
            matches(allocation?.discount_application),
          ) ||
          (item?.line_level_discount_allocations || []).some((allocation) =>
            matches(allocation?.discount_application),
          ),
      )
    }

    // One check at a time; a request that arrives meanwhile (e.g. the add-to-cart event while
    // the initial check is in flight) runs once more afterwards instead of being dropped.
    syncWithCart({ reapply = false } = {}) {
      if (this.syncInFlight) {
        this.syncAgain = { reapply: Boolean(this.syncAgain?.reapply || reapply) }
        return this.syncInFlight
      }

      this.syncInFlight = this.runCartSync(reapply).finally(() => {
        this.syncInFlight = null
        const again = this.syncAgain
        this.syncAgain = null
        if (again) this.syncWithCart(again)
      })
      return this.syncInFlight
    }

    async runCartSync(reapply) {
      if (!this.discountCode) return

      try {
        let cartState = await this.readCart()
        if (!cartState) return

        const expectsCode = this.isApplied || this.getPersistedAppliedState()

        // The code is only attached to a cart that exists, so a code applied while the cart was
        // empty can be missing once something is added. Send it again before giving up on it.
        if (reapply && expectsCode && !cartState.hasCode && cartState.itemCount > 0) {
          try {
            await this.requestDiscountCode()
            cartState = (await this.readCart()) || cartState
            if (cartState.hasCode) {
              document.dispatchEvent(
                new CustomEvent('cartaddsuccess', {
                  detail: {
                    openCart: false,
                    refreshCart: true,
                    source: MANUAL_DISCOUNT_EVENT_SOURCE,
                  },
                }),
              )
            }
          } catch (error) {
            console.warn('[manual-discount-code] Failed to re-apply code', error)
          }
        }

        if (cartState.hasCode) {
          this.setApplied(true)
        } else if (cartState.itemCount > 0) {
          this.setApplied(false)
        }
        // Empty cart: nothing to verify against, so keep what the PDP shows for now.
      } catch (error) {
        console.warn('[manual-discount-code] Failed to sync applied state with the cart', error)
      }
    }

    applyManualDiscountPriceUi() {
      if (!this.isApplied) return

      this.closest('product-info')?.applyManualDiscountPriceUi?.(this.discountPercentage)
      this.dispatchEvent(
        new CustomEvent('manual-discount-code:applied', {
          bubbles: true,
          detail: {
            discountPercentage: this.discountPercentage,
          },
        }),
      )
    }

    get storageKey() {
      return `manual-discount-code:${this.discountCode?.trim()?.toUpperCase?.() || ''}`
    }

    persistAppliedState() {
      try {
        window.localStorage?.setItem(this.storageKey, JSON.stringify({ appliedAt: Date.now() }))
      } catch (error) {
        console.warn('[manual-discount-code] Failed to persist applied state', error)
      }
    }

    // Also drops the previous format (a bare 'true' with no timestamp).
    getPersistedAppliedState() {
      try {
        const raw = window.localStorage?.getItem(this.storageKey)
        if (!raw) return false

        const { appliedAt } = JSON.parse(raw) || {}
        if (Date.now() - Number(appliedAt) < MANUAL_DISCOUNT_STATE_TTL_MS) return true

        this.clearPersistedAppliedState()
        return false
      } catch (error) {
        console.warn('[manual-discount-code] Failed to read applied state', error)
        return false
      }
    }

    clearPersistedAppliedState() {
      try {
        window.localStorage?.removeItem(this.storageKey)
      } catch (error) {
        console.warn('[manual-discount-code] Failed to clear applied state', error)
      }
    }
  }

  customElements.define('manual-discount-code', ManualDiscountCode)
}

if (!customElements.get('product-info')) {
  class ProductInfo extends HTMLElement {
    onVariantChangeUnsubscriber = undefined
    onStyleChangeUnsubscriber = undefined
    onPackColorsChangeUnsubscriber = undefined
    onPackChangeUnsubscriber = undefined
    onTypeChangeUnsubscriber = undefined
    abortController = null
    currentRender = null // the latest renderProductInfo call, see keepOnSupersede
    responseCache = new Map() // Cache to store responses
    responseCacheEpoch = 0 // bumped whenever the cache is cleared, so late prefetches don't repopulate it
    packPrefetches = new Set() // request URLs currently being prefetched
    lastSingleHandles = {} // family's first single handle -> last single the user selected
    preProcessHtmlCallbacks = []
    postProcessHtmlCallbacks = []
    allOptionsSelected = false
    firstLoadLimit = 9 // How many swatches to show by default
    firstLoadLimitClearance = 6 // How many clearance swatches to show by default
    handleManualDiscountApplied = (event) => {
      const discountPercentage = Number(event.detail?.discountPercentage || 0)
      if (discountPercentage > 0) this.applyManualDiscountPriceUi(discountPercentage)
    }
    handleClearanceQuantityChange = (event) => {
      if (!event.target?.matches('[data-clearance-quantity-input]')) return
      this.updateSelectedClearanceFinalPrice()
    }

    constructor() {
      super()
    }

    connectedCallback() {
      updateClearanceProgressBar(this)
      this.addEventListener('manual-discount-code:applied', this.handleManualDiscountApplied)
      this.addEventListener('change', this.handleClearanceQuantityChange)
      this.onVariantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this),
      )
      this.onStyleChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.onStyleChange,
        this.handleStyleChange.bind(this),
      )
      this.onPackColorsChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.onPackColorsChange,
        this.handleProductChange.bind(this),
      )
      this.onPackChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.onPackSizeChange,
        this.handlePackSizeChange.bind(this),
      )
      this.onTypeChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.onTypeChange,
        this.handleTypeChange.bind(this),
      )
      this.symbol = this.dataset.currencySymbol
      this.syncPdpQuantitySelector()
      this.hydrateBelowPdpAppContent()

      if (!this.dataset.liveStream) {
        const liveStreamParam = new URLSearchParams(window.location.search).get('live_stream')
        if (liveStreamParam) this.dataset.liveStream = liveStreamParam
      }

      this.metafieldSizes = window.TcSizePreferences.parseMetafieldSizes(
        this.dataset.metafieldSizes,
      )
      if (!this.applyUrlSizeParam()) this.restoreSizePreference()

      if (this.dataset.pdpType === 'page' && this.dataset.galleryLayout === 'grid') {
        this.header = document.querySelector('.main-nav__wrapper')
        this.announcementBar = document.querySelector('.new-announcement-bar__wrapper')
        this.pdpUi = this.querySelector('.liquid-pdp__ui')
        if (this.header && this.announcementBar && this.pdpUi) {
          window.addEventListener('scroll', this.stickyProductInfo.bind(this), { passive: true })
        }
      }

      if (document.querySelector('input[name="insider-club-upsell"]')?.checked) {
        document.querySelector('product-form shopify-accelerated-checkout')?.classList.add('hidden')
      } else {
        document
          .querySelector('product-form shopify-accelerated-checkout')
          ?.classList.remove('hidden')
      }

      // Set up collapsible button click listener
      // this.setupCollapsibleButton()

      // Afterpay modal functionality
      this.setupPayLaterModal()

      this.setupCustomPackButtonDelegation()
      this.setupUpgradeBarDelegation()
      this.setupInsiderUpsellDelegation()

      if (this.dataset.productId && RecentlyViewed) {
        RecentlyViewed.addProduct(this.dataset.productId, this.dataset.productCategory5)
      }

      // Apply clearance / weekly-deal boost overlay on initial render.
      // Runs on next tick so the animation fires after the bar is visible.
      if (this.packProgress) requestAnimationFrame(() => this.applyPackProgressBoost())
      requestAnimationFrame(() => this.syncManualDiscountPriceUi())

      if (this.isCustomPackEligible()) this.initializeBundleState()

      // Non-US: a preselected pack variant (?variant=) needs its Fontana lookup on load
      this.holdAtcForPendingPackFontana()
      this.loadPackFontanaOosStates()
    }

    setupCustomPackButtonDelegation() {
      this.addEventListener('click', (event) => {
        // "Customize" pack-tile edit button. Reads the CLICKED tile's color
        // list (clicking the button doesn't change the active pack since
        // it's a sibling of the radio input).
        if (!this.isCustomPackEligible()) return
        const tcpEditTrigger = event.target.closest('[data-tcp-edit]')
        if (tcpEditTrigger) {
          event.preventDefault()
          if (this.querySelector('product-form')?.classList.contains('loading')) return
          const wrapper = tcpEditTrigger.closest('.liquid-pdp__product-colors-button-wrapper')
          const packInput = wrapper?.querySelector('input[data-color-list]')
          const packColorsRaw = packInput?.dataset.colorList || ''
          this.revealTCustomPack(packColorsRaw)
          return
        }

        // A/B test "Build Your Own" tile (snippets/product-color-buttons).
        // Enters bundle mode at the active pack-size with EMPTY slots
        // (no pre-fill from a sibling pack).
        const byoTrigger = event.target.closest('[data-tcp-byo]')
        if (byoTrigger) {
          event.preventDefault()
          this.revealTCustomPackEmpty()
        }
      })
    }

    // Upgrade bar (snippets/upgrade-bar.liquid) — clicking the bar dispatches
    // a `change` on the matching pack-size radio so handlePackSizeChange
    // owns the bundle-mode + section-fetch logic. Singles → smallest pack;
    // multi-pack → next-larger pack.
    setupUpgradeBarDelegation() {
      this.addEventListener('click', (event) => {
        const bar = event.target.closest('[data-upgrade-bar]')
        if (!bar || !this.contains(bar)) return
        event.preventDefault()

        const targetSize = bar.dataset.upgradePackSize
        if (!targetSize) return
        const radio = this.querySelector(
          `product-pack-selector input[type="radio"][data-size="${targetSize}"]`,
        )
        if (!radio) return
        radio.checked = true
        radio.dispatchEvent(new Event('change', { bubbles: true }))
      })
    }

    setupInsiderUpsellDelegation() {
      this.addEventListener('click', (event) => {
        const insiderWrapper = event.target.closest(
          '.liquid-pdp__insider-wrapper.js-insider-club-upsell',
        )
        if (!insiderWrapper || !this.contains(insiderWrapper)) return

        const insider = insiderWrapper.querySelector('.liquid-pdp__insider')
        if (!insider || !insider.classList.contains('active')) return

        event.preventDefault()
        this.openMiniCart()
      })
    }

    openMiniCart() {
      const cartDrawer = document.querySelector('cart-drawer')
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open()
        return
      }

      const miniCartTrigger = document.querySelector('#mini-cart-open')
      if (miniCartTrigger) {
        miniCartTrigger.click()
      }
    }

    disconnectedCallback() {
      this.removeEventListener('manual-discount-code:applied', this.handleManualDiscountApplied)
      this.removeEventListener('change', this.handleClearanceQuantityChange)

      if (this.dataset.pdpType === 'page') {
        window.removeEventListener('scroll', this.stickyProductInfo.bind(this))
      }
    }

    stickyProductInfo() {
      this.stickyTop = this.header.clientHeight + this.announcementBar.clientHeight
      this.style.setProperty('--pdp-info-sticky-top', `${this.stickyTop}px`)
      if (window.innerWidth >= 1200 && this.dataset.galleryLayout === 'grid') {
        this.classList.add('is-sticky')
      } else {
        this.classList.remove('is-sticky')
      }
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return

      // Update the corresponding size display
      const optionGroup = target.closest('.liquid-pdp__variant-selector-buttons')
      if (optionGroup) {
        const header = optionGroup.previousElementSibling
        if (header) {
          const sizeDisplay = header.querySelector('.js-selected-size-display')
          if (sizeDisplay) {
            const t = getPdpTranslations()
            if (target.value === 'XS' || target.value === 'XSM') {
              sizeDisplay.textContent = t.size_x_small || 'X-Small'
            } else if (target.value === 'S' || target.value === 'SM') {
              sizeDisplay.textContent = t.size_small || 'Small'
            } else if (target.value === 'M' || target.value === 'MD') {
              sizeDisplay.textContent = t.size_medium || 'Medium'
            } else if (target.value === 'L' || target.value === 'LG') {
              sizeDisplay.textContent = t.size_large || 'Large'
            } else if (target.value === 'XL' || target.value === 'XLG') {
              sizeDisplay.textContent = t.size_x_large || 'X-Large'
            } else if (target.value === '2XL' || target.value === '2XLG') {
              sizeDisplay.textContent = t.size_2x_large || '2X-Large'
            } else if (target.value === '3XL' || target.value === '3XLG') {
              sizeDisplay.textContent = t.size_3x_large || '3X-Large'
            } else if (target.value === '4XL' || target.value === '4XLG') {
              sizeDisplay.textContent = t.size_4x_large || '4X-Large'
            } else {
              sizeDisplay.textContent = target.value
            }
          }
        }
      }

      // In bundle mode the main size selector bulk-edits every slot. Skip
      // the normal per-product form/gallery flow (active product is the
      // custom-pack with a fixed variant).
      if (this.isCustomPackEligible() && this.isBundleMode) {
        const main = this.getMainVariantSelects()
        if (main) {
          const selectedInputs = Array.from(main.querySelectorAll('input:checked')).map(
            (i) => i.value,
          )
          this.dataset.selectedSize = selectedInputs.join(' / ')
        }
        const tcp = this.querySelector('t-custom-pack')
        if (tcp && typeof tcp.applyMainSizeToAllSlots === 'function') {
          tcp.applyMainSizeToAllSlots(this.dataset.selectedSize)
        }
        // Pack tiles still need to reflect OOS for the new main size.
        const refProduct =
          this.jsonData?.packs_data_json?.[0] || this.jsonData?.singles_data_json?.[0]
        if (refProduct) this.updateProductAvailableState(refProduct)
        document.dispatchEvent(new Event('sizeChanged'))
        return
      }

      // While in OOS substitute mode, resolve against the original product before anything else
      if (this._oosOriginalContext) {
        const variantSelects = this.querySelector('variant-selects')
        const optionGroups = variantSelects?.querySelectorAll(
          '.liquid-pdp__variant-selector-buttons',
        )
        const selectedInputs = Array.from(optionGroups || [])
          .map((g) => g.querySelector('input:checked'))
          .filter((i) => i !== null)
          .map((i) => i.value)
        const allSelected = selectedInputs.length === (optionGroups?.length || 0)

        if (allSelected) {
          const selectedTitle = selectedInputs.join(' / ')
          this.dataset.selectedSize = selectedTitle
          const origVariant = this._oosOriginalContext.variants.find(
            (v) => v.title === selectedTitle,
          )
          // No matching variant in original product — fall through to substitute product flow
          if (!origVariant) {
            // fall through
          } else if (origVariant.oos_substitute) {
            const sub = origVariant.oos_substitute
            // Update tile images for the new size before section fetch
            const checkedProductId = Number(
              this.querySelector('product-colors input:checked')?.dataset.productId,
            )
            const checkedProduct = this.getProductById(checkedProductId)
            if (checkedProduct) this.updateProductAvailableState(checkedProduct)
            const productUrl = `${Shopify.routes.root}products/${sub.product_handle}`
            this._isOosSubstituteSwap = true
            const updateCallback = this.handleDataUpdateBig(productUrl)
            this.renderProductInfo({
              requestUrl: this.buildRequestUrlWithParams(productUrl, [], false, sub.variant_id),
              callback: (html) => {
                updateCallback(html)
                this._isOosSubstituteSwap = false
                this.showOosSubstituteBubble(sub.text)
              },
            })
            return
          } else {
            // No substitute needed — revert to original product
            const origHandle = this._oosOriginalContext.productHandle
            const wasExpanded = this.querySelector(
              '.liquid-pdp__product-colors-buttons',
            )?.classList.contains('colors-buttons-expanded')
            this._oosOriginalContext = null
            this.hideOosSubstituteBubble()
            const productUrl = `${Shopify.routes.root}products/${origHandle}`
            const updateCallback = this.handleDataUpdateBig(productUrl)
            this.renderProductInfo({
              requestUrl: this.buildRequestUrlWithParams(productUrl, [], false, origVariant.id),
              callback: (html) => {
                updateCallback(html)
                // Preserve expanded tile state across revert — updateColors resets it
                if (wasExpanded) {
                  const buttons = this.querySelector('.liquid-pdp__product-colors-buttons')
                  if (buttons) buttons.classList.add('colors-buttons-expanded')
                }
              },
            })
            return
          }
        }
        // If not all options selected yet, fall through to normal flow on the substitute product
      }

      const checkedProduct = this.querySelector('product-colors input:checked')
      if (!checkedProduct) return
      const productId = Number(checkedProduct.dataset.productId)

      let productFound = this.jsonData.packs_data_json.find((product) => product.id === productId)
      if (!productFound) {
        productFound = this.jsonData.singles_data_json.find((product) => product.id === productId)
      }

      if (this.dataset.noPdpAppData === 'true') {
        const productUrl = this.dataset.url
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, false, null),
          callback: this.handleDataUpdateBig(productUrl),
        })
      } else {
        this.updateFormState(productFound)
        this.syncShopPayButtonState()
        this.updateProductGalleryPosition(productFound)
        if (this.dataset.isVarietyPacks === 'true') return
      }

      // Main size change overwrites every bundle slot's size.
      if (this.isCustomPackEligible()) {
        const tcp = this.querySelector('t-custom-pack')
        if (tcp && typeof tcp.applyMainSizeToAllSlots === 'function') {
          tcp.applyMainSizeToAllSlots(this.dataset.selectedSize)
        }
      }

      document.dispatchEvent(new Event('sizeChanged'))
    }

    // Size preference restore — read-only. Only the clearance-v3 size preference form
    // (sections/clearance-v3.liquid) writes to window.TcSizePreferences; changing a size
    // here via <variant-selects> preselects from that saved preference but never saves.

    getSizePreferenceKey(optionName) {
      return window.TcSizePreferences.getOptionSizeKey(optionName, this.dataset.productUsesBottomSize === 'true')
    }

    findRadioForSizeValue(optionGroup, key, value) {
      const { normalizeSize, getTopSizeAliases, getBottomSizeAliases } = window.TcSizePreferences
      const inputs = Array.from(optionGroup.querySelectorAll('input[type="radio"]'))

      if (key === 'bottomSize') {
        const aliases = getBottomSizeAliases(value)
        return inputs.find((input) => aliases.includes(normalizeSize(input.value))) || null
      }

      if (key === 'bottomWaist') {
        const normWaist = normalizeSize(value)
        return (
          inputs.find((input) => {
            const v = normalizeSize(input.value)
            return v === normWaist || v === `W${normWaist}`
          }) || null
        )
      }

      if (key === 'bottomLength') {
        const normLength = normalizeSize(value)
        const lengthNum = normLength.replace(/L$/, '')
        return (
          inputs.find((input) => {
            const v = normalizeSize(input.value)
            return v === normLength || v === lengthNum || v === `${lengthNum}L`
          }) || null
        )
      }

      const aliases = getTopSizeAliases(value)
      return inputs.find((input) => aliases.includes(normalizeSize(input.value))) || null
    }

    findMatchingInputForKey(optionGroups, key, value) {
      if (!value) return null
      const group = optionGroups.find((g) => this.getSizePreferenceKey(g.dataset.optionName) === key)
      if (!group) return null
      return this.findRadioForSizeValue(group, key, value)
    }

    // Checks every restored input synchronously, then fires a single deferred
    // change event (from the last input) once all are checked, so a multi-input
    // restore (waist + length) doesn't replay the complete selection twice.
    // The dispatch is deferred because during a full-section swap (e.g.
    // product-family-cards updatePDP -> changeFullSection) this runs inside the
    // new <product-info>'s connectedCallback, before the descendant
    // <variant-selects> has attached its change listener (custom element
    // callbacks fire in tree order), so a synchronous dispatch would be lost and
    // updateFormState would never run — leaving ATC stuck on "Select A Size"
    // with no variant URL param. customElements.whenDefined resolves as a
    // microtask after the whole subtree has connected.
    selectSizeInputs(inputs) {
      const selectedInputs = new Set(inputs)
      const optionGroups = new Set(
        inputs
          .map((input) => input.closest('.liquid-pdp__variant-selector-buttons'))
          .filter(Boolean),
      )

      // Keep the checked property and attribute aligned. The POC selector mirrors
      // the hidden source markup and observes `checked` attribute mutations, while
      // assigning only input.checked is invisible to that observer during restore.
      for (const optionGroup of optionGroups) {
        const groupInputs = optionGroup.querySelectorAll('input[type="radio"]')
        for (const input of groupInputs) {
          const isSelected = selectedInputs.has(input)
          input.checked = isSelected
          input.toggleAttribute('checked', isSelected)
        }
      }

      const lastInput = inputs[inputs.length - 1]
      customElements.whenDefined('variant-selects').then(function dispatchSizeChange() {
        lastInput.dispatchEvent(new Event('change', { bubbles: true }))
      })
    }

    // Returns false only when the rendered option state or PDP variant data confirms
    // that every variant matching the preferred-size inputs is sold out. If variant
    // data is unavailable, preserve the existing restore behavior.
    arePreferredSizeInputsAvailable(optionGroups, inputs) {
      const hasUnavailableInput = inputs.some(
        (input) =>
          input.getAttribute('aria-disabled') === 'true' || input.classList.contains('disabled'),
      )
      if (hasUnavailableInput) return false

      const jsonData = this.jsonData
      if (!jsonData) return true
      const productFound = [
        jsonData.packs_data_json,
        jsonData.singles_data_json,
        jsonData.families_data_json,
        jsonData.alternative_length_family_data_json,
      ]
        .filter(Array.isArray)
        .flat()
        .find((product) => product.id === Number(this.dataset.productId))
      if (!Array.isArray(productFound?.variants)) return true

      const preferredOptions = inputs
        .map((input) => ({
          index: optionGroups.indexOf(input.closest('.liquid-pdp__variant-selector-buttons')),
          value: window.TcSizePreferences.normalizeSize(input.value),
        }))
        .filter((option) => option.index >= 0)
      if (!preferredOptions.length) return true

      const matchingVariants = productFound.variants.filter((variant) =>
        preferredOptions.every(
          (option) =>
            window.TcSizePreferences.normalizeSize(variant.options?.[option.index]) === option.value,
        ),
      )
      if (!matchingVariants.length) return true

      return matchingVariants.some((variant) => variant.available)
    }

    // Preselects a saved size on first load only — never overrides a size the
    // shopper (or the URL) has already picked. Tries top size and/or Bottoms size
    // first (both on a Size + Bottoms set); falls back to bottom waist, then
    // waist+length, per clearance-v3's own match order.
    restoreSizePreference() {
      if (this.dataset.productIsKids === 'true') return
      if (this.dataset.selectedSize) return

      const variantSelects = this.querySelector('variant-selects')
      if (!variantSelects) return

      const optionGroups = Array.from(
        variantSelects.querySelectorAll('.liquid-pdp__variant-selector-buttons'),
      )
      if (!optionGroups.length) return
      if (optionGroups.some((group) => group.querySelector('input:checked'))) return

      const sizes = this.getSavedSizesForProduct()
      if (!sizes) return

      const letterSizeInputs = [
        this.findMatchingInputForKey(optionGroups, 'top', sizes.top),
        this.findMatchingInputForKey(optionGroups, 'bottomSize', sizes.bottomSize),
      ].filter(Boolean)
      if (letterSizeInputs.length && this.arePreferredSizeInputsAvailable(optionGroups, letterSizeInputs)) {
        this.selectSizeInputs(letterSizeInputs)
        return
      }

      const waistInput = this.findMatchingInputForKey(optionGroups, 'bottomWaist', sizes.bottomWaist)
      if (!waistInput) return

      const lengthInput = this.findMatchingInputForKey(optionGroups, 'bottomLength', sizes.bottomLength)
      const bottomInputs = lengthInput ? [waistInput, lengthInput] : [waistInput]
      if (!this.arePreferredSizeInputsAvailable(optionGroups, bottomInputs)) return
      this.selectSizeInputs(bottomInputs)
    }

    // Ad deep links (e.g. /products/the-staple-6-pack?size=m) preselect that size on
    // load, overriding the saved size preference and any ?variant= size. Runs once per
    // page on the main PDP only, so later full-section swaps (style/family changes)
    // and quick-add/homepage embeds never get forced back to the URL size. Matches
    // the first option group with a matching value (letter-size aliases, W32, 30L, etc.)
    // and skips a sold-out size, falling back to the normal preference restore.
    // Returns true when a size was applied.
    applyUrlSizeParam() {
      if (ProductInfo.hasHandledUrlSizeParam) return false
      if (this.closest('quick-modal, menu-with-pdp') || window.isMenuWithPdp === true) return false
      ProductInfo.hasHandledUrlSizeParam = true

      const size = new URLSearchParams(window.location.search).get('size')
      if (!size) return false

      const optionGroups = Array.from(
        this.querySelectorAll('variant-selects .liquid-pdp__variant-selector-buttons'),
      )
      const sizeInput = optionGroups
        .map((group) =>
          this.findRadioForSizeValue(group, this.getSizePreferenceKey(group.dataset.optionName), size),
        )
        .find(Boolean)
      if (!sizeInput) return false
      if (!this.arePreferredSizeInputsAvailable(optionGroups, [sizeInput])) return false

      this.selectSizeInputs([sizeInput])
      return true
    }

    // Women's products use their own key, with no fallback to the sitewide/account sizes.
    getSavedSizesForProduct() {
      const preferences = window.TcSizePreferences
      if (this.dataset.productIsWomens === 'true') return preferences.readStoredSizes(preferences.WOMENS_STORAGE_KEY)
      return preferences.getEffectiveSizes(this.metafieldSizes)
    }

    pushSizesToKlaviyo(sizes) {
      try {
        window.klaviyo = window.klaviyo || []
        klaviyo.identify({
          'Preferred Top Size': sizes.top || '',
          'Preferred Bottom Waist': sizes.bottomWaist || '',
          'Preferred Bottom Length': sizes.bottomLength || '',
          'Preferred Bottom Size': sizes.bottomSize || '',
        })
      } catch (error) {
        console.warn('Unable to push sizes to Klaviyo', error)
      }
    }

    handleStyleChange({ data: { event, target, label } }) {
      if (!this.contains(event.target)) return
      this._oosOriginalContext = null
      this.hideOosSubstituteBubble()

      // Family switch invalidates the bundle: slots, selections, gallery
      // hiding are all tied to the current product. Clear before the swap so
      // the new family lands in a clean state, regardless of whether the new
      // family is bundle-eligible.
      this.exitBundleMode()

      const isClearanceMode = this.dataset.showOnlyClearance === 'true'
      const clearanceProductUrl = target.dataset.clearanceProductUrl?.trim()
      const productUrl = isClearanceMode && clearanceProductUrl ? clearanceProductUrl : target.value
      const productFound = this.getProductByHandle(productUrl)
      const fallbackProductId = Number(target.dataset.productId)
      const productId = productFound?.id || fallbackProductId
      const variantId = Number.isFinite(productId) ? this.keepSize(productId) : null

      const isOptionsPocLengthChange =
        this.dataset.optionsPoc === 'true' &&
        target.name === 'product-style-alternative_length_family'

      if (isOptionsPocLengthChange) {
        this.renderOptionsPocLength(productUrl, variantId, label)
      } else {
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, [], false, variantId),
          callback: this.handleDataUpdateBig(productUrl, 'style', label, target),
        })
      }

      const styleOption = target.name.split('-')[1]
      const styleValue = label.dataset.value

      if (document.querySelector('.breadcrumbs__button__child-category')) {
        document.querySelector('.breadcrumbs__button__child-category').innerHTML = styleValue
      }
      document.dispatchEvent(
        new CustomEvent('product-style-updated', { detail: { styleOption, styleValue } }),
      )
    }

    renderOptionsPocLength(productUrl, variantId, label) {
      const productForm = this.querySelector('product-form')
      productForm?.classList.add('loading')

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, [], false, variantId),
        callback: (html) => {
          const variant = this.getSelectedVariant(html)

          this.updateOptionsPocProductState(html)
          this.updateURL(productUrl, variant?.id)
          this.updateOptionsPocSources(html)
          this.updateOptionsPocFormState(html, variant?.id)
          this.updateSizeChartModal(html)
          this.updateProductGalleryFromHtml(html)
          // New pack tiles, JSON and server-rendered ATC: hold/look up/re-flag like other re-renders
          this.reloadPackFontanaOosStates()
          this.sendDataLayerInfo()

          productForm?.classList.remove('loading')
          label?.classList.remove('loading')
          label?.querySelector('.loading__spinner')?.classList.add('hidden')

          document.dispatchEvent(
            new CustomEvent('pdp-options:length-updated', {
              detail: { productUrl, variantId: variant?.id || null },
            }),
          )
        },
      })
    }

    updateOptionsPocProductState(html) {
      const productInfo = html.querySelector('product-info')
      if (!productInfo) return

      const productStateKeys = [
        'productAvailable',
        'productPackSize',
        'membershipStatus',
        'customerId',
        'colorPack',
        'loginStatus',
        'packQuantity',
        'ecommerceCurrency',
        'productPrice',
        'productPriceFormatted',
        'productCompareAtPrice',
        'productCompareAtPriceFormatted',
        'productId',
        'url',
        'productHandle',
        'productTitle',
        'productDiscount',
        'productBrand',
        'productCategory',
        'productCategory2',
        'productCategory3',
        'productCategory5',
        'sku',
        'lowStockVariant',
        'variantId',
        'selectedSize',
        'sizeGroup',
        'isWeeklyDeal',
        'weeklyDealDiscountPercentage',
        'productClearance',
        'productFinalSale',
        'pdpDiscountExcluded',
        'manualDiscountPercentage',
        'manualDiscountAutoApplied',
        'promoRoundToNinetyNineCents',
      ]

      for (const key of productStateKeys) {
        const value = productInfo.dataset[key]
        if (value === undefined) delete this.dataset[key]
        else this.dataset[key] = value
      }

      this.dataset.bundleProductHandle = productInfo.dataset.bundleProductHandle || ''
      this.dataset.bundleProductVariantId = productInfo.dataset.bundleProductVariantId || ''
      this.dataset.bundleMaxItems = productInfo.dataset.bundleMaxItems || ''

      for (const [key, value] of Object.entries(productInfo.dataset)) {
        if (key.startsWith('style')) this.dataset[key] = value
      }
    }

    updateOptionsPocSources(html) {
      const selectors = [
        '.liquid-pdp__variant-selector-wrapper',
        '.js-your-cut-selector',
        'product-pack-selector',
        'product-colors',
        '.t-custom-pack-wrapper',
        'pdp-product-context',
      ]

      for (const selector of selectors) {
        const current = this.querySelector(selector)
        const next = html.querySelector(selector)
        if (current && next) current.replaceWith(next.cloneNode(true))
      }
    }

    updateOptionsPocFormState(html, variantId) {
      const nextForm = html.querySelector('product-form')
      const currentForm = this.querySelector('product-form')
      if (!nextForm || !currentForm) return

      const nextVariantInput = nextForm.querySelector('input[name="id"]')
      const currentVariantInput = currentForm.querySelector('input[name="id"]')
      if (currentVariantInput) {
        currentVariantInput.value = variantId || nextVariantInput?.value || ''
        currentVariantInput.disabled = Boolean(nextVariantInput?.disabled)
        currentVariantInput.dispatchEvent(new Event('change', { bubbles: true }))
      }

      const nextSubmit = nextForm.querySelector('button[type="submit"]')
      const currentSubmit = currentForm.querySelector('button[type="submit"]')
      if (nextSubmit && currentSubmit) {
        currentSubmit.disabled = nextSubmit.disabled
        const nextText = nextSubmit.querySelector('.js-button-text')
        const currentText = currentSubmit.querySelector('.js-button-text')
        if (nextText && currentText) currentText.textContent = nextText.textContent
      }

      const nextNotifyInput = nextForm.querySelector('input[name="variant_id"]')
      const currentNotifyInput = currentForm.querySelector('input[name="variant_id"]')
      if (nextNotifyInput && currentNotifyInput) currentNotifyInput.value = nextNotifyInput.value

      this.syncShopPayButtonState()
    }

    handleTypeChange({ data: { event, target } }) {
      if (!this.contains(event.target)) return
      this._oosOriginalContext = null
      this.hideOosSubstituteBubble()

      // Type switch invalidates the bundle for the same reasons as style
      // switch — see handleStyleChange.
      this.exitBundleMode()

      const productUrl = target.value
      const productId = Number(target.dataset.productId)
      const variantId = this.keepSize(productId)

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, [], false, variantId),
        callback: this.handleDataUpdateBig(productUrl),
      })
    }

    handleProductChange({ data: { event, target } }) {
      if (!this.contains(event.target)) return
      this._oosOriginalContext = null
      this.hideOosSubstituteBubble()

      // Pack-tile click tears down the bundle. Skip when the change came
      // from inside t-custom-pack (modal swatches bubble here too — exiting
      // on those would wipe the bundle mid-edit).
      if (this.isCustomPackEligible() && !event.target.closest('t-custom-pack'))
        this.exitBundleMode()

      const productId = Number(target.dataset.productId)

      let productFound = this.jsonData.packs_data_json.find((product) => product.id === productId)
      if (!productFound) {
        productFound = this.jsonData.singles_data_json.find((product) => product.id === productId)
      }
      if (!productFound) return

      if (window.hideFomoBadge) hideFomoBadge()

      this.dataset.productId = Number(target.dataset.productId)
      this.dataset.productHandle = target.value
      this.dataset.colorPack = target.dataset.colorPack

      if (target.classList.contains('js-is-clearance')) {
        this.dataset.productClearance = true
      } else {
        this.dataset.productClearance = false
      }

      if (this.dataset.isVarietyPacks == 'true') {
        this.updateProductTitleFromJsonData(productFound)
      }

      this.updateProductPriceFromProductData(productFound)

      this.deferSectionRender(() => {
        // Update color list
        this.updateColorList(target.dataset.colorList)

        // Update gallery slides
        this.updateGallery(productFound)

        const productUrl = target.value
        const variantId = this.keepSize(productId)

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, [], false, variantId),
          callback: this.handleDataUpdate(),
          // Picking a single and then leaving Singles is common: let this render finish and
          // cache the single so coming back to it is served from memory.
          keepOnSupersede: this.dataset.productPackSize === 'Single',
        })

        this.updateFormState(productFound)

        this.sendDataLayerInfo()

        this.updateProductGalleryPosition(productFound, { useFirstImageForPacks: true })

        if (this.dataset.productId && RecentlyViewed) {
          RecentlyViewed.addProduct(this.dataset.productId, this.dataset.productCategory5)
        }

        // checkShortSize()
        document.dispatchEvent(new CustomEvent('colorChanged'))
      })
    }

    getPackSizeChangeContext(target) {
      const isClearanceMode = this.dataset.showOnlyClearance === 'true'
      const clearanceProductUrl = target.dataset.clearanceProductUrl?.trim()
      let productUrl = isClearanceMode && clearanceProductUrl ? clearanceProductUrl : target.value
      // Returning to Singles restores the last selected single instead of the family's first.
      if (target.dataset.size === 'Single' && !isClearanceMode)
        productUrl = this.getRememberedSingleHandle(target.value) || productUrl
      const productFound = this.getProductByHandle(productUrl)
      const fallbackProductId = Number(target.dataset.productId)
      const productId = productFound?.id || fallbackProductId
      const variantId = Number.isFinite(productId) ? this.keepSize(productId) : null

      return {
        productUrl,
        variantId,
        label: target.closest('label'),
      }
    }

    // Singles radio value is the family's first single; it keys the remembered selection.
    getSinglesRadioHandle() {
      return this.querySelector('product-pack-selector input[data-size="Single"]')?.value
    }

    // Called before leaving Singles so coming back restores the selected single.
    rememberSelectedSingle() {
      if (this.dataset.productPackSize !== 'Single') return
      const key = this.getSinglesRadioHandle()
      if (key && this.dataset.productHandle) this.lastSingleHandles[key] = this.dataset.productHandle
    }

    getRememberedSingleHandle(firstSingleHandle) {
      const handle = this.lastSingleHandles[firstSingleHandle]
      return handle && this.getProductByHandle(handle) ? handle : null
    }

    handlePackSizeChange({ data: { event, target } }) {
      if (!this.contains(event.target)) return
      this._oosOriginalContext = null
      this.hideOosSubstituteBubble()
      this.rememberSelectedSingle()

      const { productUrl, variantId, label } = this.getPackSizeChangeContext(target)
      if (!productUrl) return
      const useFirstImageForPacks = target.dataset.size !== 'Single'

      // Multi-pack -> multi-pack while builder is open preserves slots +
      // re-applies bundle mode in place. Every other case falls through to a
      // standard re-render, which exits bundle mode.
      if (this.isCustomPackEligible()) {
        const targetSize = Number(target.dataset.size)
        const isNumericTargetSize = Number.isFinite(targetSize) && targetSize > 0
        const customPackVisible = !!this.querySelector('.t-custom-pack-wrapper--shown')
        const fromSingle = this.dataset.productPackSize === 'Single'
        if (customPackVisible && isNumericTargetSize && !fromSingle) {
          this.handlePackSizeChangeWithCustomPack(target, targetSize)
          return
        }

        // Switching packs invalidates the current bundle.
        this.exitBundleMode()

        const isBundleModeTarget = target.dataset.bundleMode === 'true'

        this.deferSectionRender(() => {
          const updateCallback = this.handleDataUpdateBig(productUrl, '', label, null, {
            useFirstImageForPacks,
          })

          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(productUrl, [], false, variantId),
            callback: (html) => {
              updateCallback(html)
              this.setBundleMode(isBundleModeTarget)
              // Safety net: once the singles view has rendered, eligibility
              // has flipped to false, so exitBundleMode/setBundleMode above
              // can no longer touch the slot UI (both early-return). If the
              // builder somehow survived the swap, force-hide it directly.
              if (target.dataset.size === 'Single') this.ensureBundleUiHidden()
            },
          })
        })
        return
      }

      this.deferSectionRender(() => {
        const updateCallback = this.handleDataUpdateBig(productUrl, '', label, null, {
          useFirstImageForPacks,
        })
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, [], false, variantId),
          callback: (html) => {
            updateCallback(html)
            // Switching from a non-eligible product (e.g. a single) lands here.
            // Unlike the eligible branch above, nothing re-runs updateFormState,
            // so an OOS pack would keep its server-rendered Notify Me state.
            // Re-run it for eligible packs so the OOS->bundle branch can fire;
            // gated on eligibility so plain packs keep their Notify Me state.
            this.reapplyFormStateForEligiblePack()
          },
        })
      })
    }

    // Re-applies updateFormState after a pack-size re-render, but only when the
    // freshly rendered product is custom-pack eligible. Lets an OOS pack drop
    // into bundle mode (matching color-swatch changes via handleProductChange)
    // instead of surfacing Notify Me. No-ops for non-eligible products.
    reapplyFormStateForEligiblePack() {
      if (!this.isCustomPackEligible()) return
      const productFound = this.getProductById(Number(this.dataset.productId))
      if (productFound) this.updateFormState(productFound)
    }

    changeFullSection(productUrl, updateUrl = true, params = [], options = {}) {
      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, [], false, null, params),
        callback: this.handleUpdateFullProductInfo(productUrl, updateUrl, options),
      })
    }

    // A render that a newer one replaces is normally aborted. keepOnSupersede leaves it running
    // instead: its response still lands in responseCache (so that product is warm when the
    // shopper comes back to it) but its callback is skipped. Used for a Singles swatch change,
    // which is often followed straight away by a pack-size switch.
    renderProductInfo({ requestUrl, callback, keepOnSupersede = false }) {
      const previousRender = this.currentRender
      if (previousRender?.keepOnSupersede && !previousRender.settled) {
        previousRender.stale = true
      } else {
        this.abortController?.abort()
      }
      this.abortController = new AbortController()
      const render = { keepOnSupersede, stale: false, settled: false }
      this.currentRender = render

      // Check if we have a cached response
      if (this.responseCache.has(requestUrl)) {
        const cachedHtml = this.responseCache.get(requestUrl)
        render.settled = true
        callback(cachedHtml)
        return
      }

      const epoch = this.responseCacheEpoch
      // In flight under this URL, so a pointer prefetch doesn't fetch the same thing again.
      if (keepOnSupersede) this.packPrefetches.add(requestUrl)

      fetch(requestUrl, keepOnSupersede ? {} : { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html')
          // Each modal namespaces ids with its own tag name, so the deal modal's copy never clashes.
          const quickModal = this.closest('quick-modal, deal-quick-modal')
          if (quickModal && typeof window.namespaceQuickModalProductHtml === 'function') {
            window.namespaceQuickModalProductHtml(html, quickModal.localName)
          }
          // Store the parsed HTML in cache — unless a superseded render finished after the
          // cache was cleared (the cart changed meanwhile), which would restore stale state.
          if (!render.stale || epoch === this.responseCacheEpoch) {
            this.responseCache.set(requestUrl, html)
          }
          if (render.stale) return
          callback(html)
        })
        .catch((error) => {
          // A superseded render has nothing left to update, and its newer one owns the UI state.
          if (render.stale) return
          if (error.name === 'AbortError') {
            console.error('Fetch aborted by user')
            this.querySelectorAll('label').forEach((label) => {
              label.classList.remove('loading')
            })
            this.querySelectorAll('.loading__spinner').forEach((spinner) => {
              spinner.classList.add('hidden')
            })
          } else {
            console.error(error)
            // A failed (non-abort) render never runs the success callback that
            // clears the in-flight loading state, which would otherwise leave
            // bundle ATC greyed out. Clear it and let isBundleReady re-decide.
            this.querySelector('product-form')?.classList.remove('loading')
            if (this.isCustomPackEligible() && this.isBundleMode) this.updateBundleModeUi()
            // Non-US: a Fontana lookup that settled mid-render skipped its resync
            this.resyncFormStateAfterPackFontanaLoad()
          }
        })
        .finally(() => {
          render.settled = true
          if (keepOnSupersede) this.packPrefetches.delete(requestUrl)
        })
    }

    // Warms responseCache for the section render a pack-size radio would trigger, so the
    // click that follows is served from memory. The key is built exactly like
    // handlePackSizeChange builds it. Errors are swallowed: the click just fetches normally.
    prefetchPackSection(radio) {
      if (!radio || radio.checked) return Promise.resolve()

      const { productUrl, variantId } = this.getPackSizeChangeContext(radio)
      if (!productUrl) return Promise.resolve()

      const requestUrl = this.buildRequestUrlWithParams(productUrl, [], false, variantId)
      if (this.responseCache.has(requestUrl) || this.packPrefetches.has(requestUrl)) {
        return Promise.resolve()
      }

      this.packPrefetches.add(requestUrl)
      const epoch = this.responseCacheEpoch
      return fetch(requestUrl, { priority: 'low' })
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html')
          // Each modal namespaces ids with its own tag name, so the deal modal's copy never clashes.
          const quickModal = this.closest('quick-modal, deal-quick-modal')
          if (quickModal && typeof window.namespaceQuickModalProductHtml === 'function') {
            window.namespaceQuickModalProductHtml(html, quickModal.localName)
          }
          if (epoch === this.responseCacheEpoch && !this.responseCache.has(requestUrl)) {
            this.responseCache.set(requestUrl, html)
          }
        })
        .catch(() => {})
        .finally(() => this.packPrefetches.delete(requestUrl))
    }

    handleDataUpdate() {
      this.querySelector('product-form').classList.add('loading')
      // this.productDescription.classList.add('loading')
      return (html) => {
        const updates = [
          () => (this.dataset.isVarietyPacks == 'false' ? this.updateProductTitle(html) : null),
          () =>
            this.dataset.isVarietyPacks == 'false' ? this.updateProductDescription(html) : null,
          () => (this.dataset.isVarietyPacks == 'true' ? this.updateProductPrice(html) : null),
          () => (this.dataset.isVarietyPacks == 'true' ? this.updateProductStarRating(html) : null),
          () =>
            this.dataset.isVarietyPacks == 'true'
              ? this.updateProductDescriptionVariety(html)
              : null,
          () => this.updateProductPrice(html),
          () => this.updateProductInfoData(html),
          () => this.updateOptionValues(html).then(),
          () => this.updateSizeChartButton(html),
          () => this.updateSizeChartModal(html),
          () => this.updateAfterpay(html),
          () => this.updatePromoCountdownCallout(html),
          () => this.updateProductPriceBlock(html),
          () => this.updateDiscountCode(html),
          () => updateGalleryPromoText(html),
          () => updateVdayBadge(html),
          () => this.updateBelowAtcModals(html),
          () => this.updatePackSelectors(html),
          () => this.updateUpgradeBar(html),
          () => this.updatePackProgress(html),
          () => this.updateUiFooter(html),
          () => this.updateUso(html),
          () => this.updateExpectedShipping(html),
          () => this.updateStyleItWith(html),
          () => (!this.closest('quick-add-modal') ? this.updateRebuyRecommendations(html) : null),
        ]

        // Execute all updates
        updates.forEach((update) => update())
        this.syncManualDiscountPriceUi()
        this.querySelector('product-form').classList.remove('loading')
        // this.productDescription.classList.remove('loading')
        // Non-US: a Fontana lookup that settled mid-render skipped its resync
        this.resyncFormStateAfterPackFontanaLoad()

        // Fade in newly replaced animated elements (no slide)
        if (window.fadeInAnimatedElements) window.fadeInAnimatedElements()

        // Update view full details link on quick add modal
        const viewFullDetails = document.querySelector('.js-quick-modal-view-full-details')
        if (viewFullDetails) {
          const baseHref = `${Shopify.routes.root}products/${this.dataset.productHandle}`
          viewFullDetails.href = this.viewFullDetailsHref(baseHref)
        }
      }
    }

    handleDataUpdateBig(productUrl, updateType = '', label = null, target = null, options = {}) {
      this.querySelector('product-form').classList.add('loading')

      // Grey out bundle ATC during the in-flight section render. Cart
      // correctness is enforced at submit time by isBundleReady() (count +
      // family-mismatch checks); this is just UX so the button can't be clicked
      // mid-transition. The .loading class (added above, removed in the callback
      // / fetch-failure path) is the single source of truth for "in flight".
      if (this.isCustomPackEligible() && this.isBundleMode) {
        const pendingSubmitButton = this.querySelector('product-form form button[type="submit"]')
        if (pendingSubmitButton) pendingSubmitButton.disabled = true
      }

      return (html) => {
        const preparedHtml = this.prepareFullProductInfoHtml(html, options)
        const variant = this.getSelectedVariant(preparedHtml)

        const updates = [
          () => this.updateProductInfoData(preparedHtml),
          () => this.updateURL(productUrl, variant?.id),
          () => this.updateProductTitle(preparedHtml),
          () => this.updateProductDescription(preparedHtml),
          () => this.updateProductPrice(preparedHtml),
          () => this.updateProductPriceBlock(preparedHtml),
          () => this.updateDiscountCode(preparedHtml),
          () => updateGalleryPromoText(preparedHtml),
          () => updateVdayBadge(preparedHtml),
          () => (updateType === 'style' ? this.updateProductStarRating(preparedHtml) : null),
          () => this.updateOptionValues(preparedHtml).then(),
          () => this.updateVariantInputs(variant?.id),
          () => this.updateYourCutSelector(preparedHtml),
          () => this.updateFamilySelector(preparedHtml),
          () =>
            updateType === 'style'
              ? this.updateStyleSelectionWithJs(target)
              : this.updateStyleSelectors(preparedHtml),
          () => this.updateColors(preparedHtml),
          () => this.updateProductForm(preparedHtml),
          () => this.updateAfterpay(preparedHtml),
          () => this.updatePromoCountdownCallout(preparedHtml),
          () => (this.isCustomPackEligible() ? this.updateTCustomPackWrapper(preparedHtml) : null),
          () => this.updatePackSelectors(preparedHtml),
          () => this.updateUpgradeBar(preparedHtml),
          () => this.updatePackProgress(preparedHtml),
          () => this.updateUiFooter(preparedHtml),
          () => this.updateBxgy(preparedHtml),
          () => this.updateBelowPdpAppContent(preparedHtml),
          () => this.updateModalWrapper(preparedHtml),
          () => this.updatePdpProductContext(preparedHtml),
          () => this.sendDataLayerInfo(),
          () => this.updateSizeChartButton(preparedHtml),
          () => this.updateSizeChartModal(preparedHtml),
          () => this.updateCompleteTheLook(preparedHtml),
          () => this.updateStyleItWith(preparedHtml),
          () => this.updateExpectedShipping(preparedHtml),
          () =>
            this.dataset.noPdpAppData === 'true'
              ? null
              : this.updateProductGalleryFromHtml(preparedHtml),
          () => this.updateBelowAtcModals(preparedHtml),
          () => this.updateAvailableInTarget(preparedHtml),
          () => (updateType === 'style' ? this.updateOkendoReviews() : null),
          () => (window.hideFomoBadge ? hideFomoBadge() : null),
          () => (window.showLowStockBadge ? showLowStockBadge({ checkFirst: true }) : null),
          () => updateInsiderClubUpsell(preparedHtml, this),
          () =>
            !this.closest('quick-add-modal')
              ? this.updateRebuyRecommendations(preparedHtml)
              : null,
        ]

        updates.forEach((update) => update())
        this.reloadPackFontanaOosStates()

        // A section render kicked off before bundle mode was (re-)entered can
        // resolve late — updateProductForm() swaps in the OOS curated-pack form
        // (disabled "Sold out" submit) and updateProductTitle/Price/Gallery
        // clobber the bundle presentation. The data-product-available guard in
        // updateProductInfoData only hides Notify Me; re-assert the full bundle
        // state here so a stale response can't leave bundle ATC unusable.
        // enterBundleMode is built to be re-applied after a DOM-wiping render.
        if (this.isCustomPackEligible() && this.isBundleMode) {
          // Re-apply bundle presentation, then re-run the bundle UI so ATC
          // re-evaluates (the .loading class has been cleared by now, so the
          // greyed-out state lifts and isBundleReady decides the rest).
          this.enterBundleMode().finally(() => this.updateBundleModeUi())
        }

        this.syncManualDiscountPriceUi()
        this.querySelector('product-form').classList.remove('loading')

        // Fade in newly replaced animated elements (no slide)
        if (window.fadeInAnimatedElements) window.fadeInAnimatedElements()

        // Update view full details link on quick add modal
        const viewFullDetails = document.querySelector('.js-quick-modal-view-full-details')
        if (viewFullDetails) {
          const baseHref =
            this.dataset.country === 'US'
              ? this.dataset.url
              : `/${this.dataset.locale}-${this.dataset.country}/products/${this.dataset.productHandle}`
          viewFullDetails.href = this.viewFullDetailsHref(baseHref)
        }
        // checkShortSize()

        if (label) {
          label.classList.remove('loading')
          label.querySelector('.loading__spinner')?.classList.add('hidden')
        }

        // logProductInfoDataAttributes()
      }
    }

    handleUpdateFullProductInfo(productUrl, updateUrl, options = {}) {
      this.querySelector('product-form').classList.add('loading')
      return (html) => {
        const preparedHtml = this.prepareFullProductInfoHtml(html, options)
        const preparedProductInfo = preparedHtml.querySelector('product-info')
        if (!updateUrl && preparedProductInfo) preparedProductInfo.dataset.updateUrl = 'false'

        const variant = this.getSelectedVariant(preparedHtml)
        const updates = [
          () => (updateUrl ? this.updateURL(productUrl, variant?.id) : null),
          () => this.updateFullProductInfo(preparedHtml),
          () => this.updateBelowPdpAppContent(preparedHtml),
          () => this.updateOkendoReviews(),
          () =>
            !this.closest('quick-add-modal')
              ? this.updateRebuyRecommendations(preparedHtml)
              : null,
        ]
        updates.forEach((update) => update())
        this.reloadPackFontanaOosStates()
        this.querySelector('product-form').classList.remove('loading')
        if (window.fadeInAnimatedElements) window.fadeInAnimatedElements()
        updateClearanceProgressBar(this)
        document.dispatchEvent(new CustomEvent('product-info-updated'))

        let imageUrl = null
        const imageCarousel = preparedHtml.querySelector('.liquid-pdp__gallery-main')
        const firstProductImage = imageCarousel.querySelector('.first-product-image')
        // console.log(firstProductImage)
        if (firstProductImage) imageUrl = firstProductImage.querySelector('img').src

        const productInfo = preparedHtml.querySelector('product-info')
        if (productInfo) {
          const contents = [
            {
              id: productInfo.dataset.productId,
              variantId: productInfo.dataset.variantId,
              sku: productInfo.dataset.sku,
              item_price: Number(productInfo.dataset.productPrice),
              title: productInfo.dataset.productTitle,
              handle: productInfo.dataset.productHandle,
              category: productInfo.dataset.productCategory,
              url: getProductUrl(productInfo.dataset.url),
              image: imageUrl,
              quantity: 1,
              type: 'product',
            },
          ]

          sendViewContent(productInfo.dataset.ecommerceCurrency, contents)
          // console.log(
          //   'handleUpdateFullProductInfo',
          //   contents[0].id,
          //   contents[0].handle,
          //   getProductUrl(productInfo.dataset.url),
          // )
        }
      }
    }

    prepareFullProductInfoHtml(html, options = {}) {
      if (!options.useFirstImageForPacks) return html

      const productInfo = html.querySelector('product-info')
      const productPackSize = productInfo?.dataset?.productPackSize || 'Single'
      if (productPackSize === 'Single') return html

      const preparedHtml = html.cloneNode(true)
      const preparedProductInfo = preparedHtml.querySelector('product-info')
      const productGallery =
        Array.from(preparedHtml.querySelectorAll('product-gallery')).find(
          (el) => !el.closest('t-custom-pack'),
        ) || null

      if (preparedProductInfo) preparedProductInfo.dataset.selectedVariantImagePosition = '1'
      if (productGallery) {
        productGallery.dataset.useFirstImageOnLoad = 'true'
        productGallery.dataset.useFirstImageForPacks = 'true'
      }

      return preparedHtml
    }

    findVariantFromOptions(selectedValues) {
      const productId = Number(this.dataset.productId)
      const productFound = this.getProductById(productId)
      if (!productFound) return null

      return productFound.variants.find((variant) => {
        return selectedValues.every((value, index) => {
          return variant[`option${index + 1}`] === value
        })
      })
    }

    updateFormState(productFound, { shouldSendDataLayer = true } = {}) {
      // Re-set below only while the selected pack's Fontana lookup is pending
      this._isAwaitingPackFontana = false
      const variantSelects = this.querySelector('variant-selects')
      if (!variantSelects) return

      // Get all required options
      const optionGroups = variantSelects.querySelectorAll(
        'variant-selects .liquid-pdp__variant-selector-buttons',
      )

      // Update dataset.selectedSize with all selected values
      const selectedInputs = Array.from(optionGroups)
        .map((group) => group.querySelector('input:checked'))
        .filter((input) => input !== null)
        .map((input) => input.value)

      this.dataset.selectedSize = selectedInputs.join(' / ')

      // Update form inputs
      const form = this.querySelector('product-form form')

      form.querySelectorAll('input[name^="properties[_"]').forEach((input) => input.remove())

      if (this.dataset.productClearance === 'true') {
        const clearanceInput = document.createElement('input')
        clearanceInput.type = 'hidden'
        clearanceInput.name = 'properties[_clearance]'
        clearanceInput.value = 'true'

        const typeInput = document.createElement('input')
        typeInput.type = 'hidden'
        typeInput.name = 'properties[_type]'
        typeInput.value =
          this.dataset.pageType === 'page'
            ? 'PDP'
            : this.dataset.pageType === 'popup'
              ? 'popup'
              : 'Bundle Builder'

        const preClearanceInput = document.createElement('input')
        preClearanceInput.type = 'hidden'
        preClearanceInput.name = 'properties[_pre_clearance_price]'
        preClearanceInput.value = this.dataset.productCompareAtPriceFormatted || ''

        form.appendChild(clearanceInput)
        form.appendChild(typeInput)
        form.appendChild(preClearanceInput)
      } else if (this.dataset.productPackSize === 'Single') {
        /*
        const valueBuilderInput = document.createElement('input')
        valueBuilderInput.type = 'hidden'
        valueBuilderInput.name = 'properties[_value_builder]'
        valueBuilderInput.value = 'true'

        const typeInput = document.createElement('input')
        typeInput.type = 'hidden'
        typeInput.name = 'properties[_type]'
        typeInput.value =
          this.dataset.pageType === 'page'
            ? 'PDP'
            : this.dataset.pageType === 'popup'
              ? 'popup'
              : 'Bundle Builder'

        form.appendChild(valueBuilderInput)
        form.appendChild(typeInput)
        */
      }

      this.allOptionsSelected = selectedInputs.length === optionGroups.length

      // Update form button
      const submitButton = this.querySelector('product-form form button[type="submit"]')
      const submitButtonText = submitButton.querySelector('.js-button-text')

      if (!this.allOptionsSelected) {
        submitButton.disabled = true
        // In bundle mode ATC text comes from bundle state (e.g. "Select Your
        // Shirts") instead of "Select a size".
        submitButtonText.innerHTML =
          this.isCustomPackEligible() && this.isBundleMode
            ? this.getSubmitButtonText()
            : submitButton.dataset.selectSizeText
        this.updateURL(this.dataset.productHandle, '')
        this.updateProductInfoDataFromJsonData(productFound)
        return
      }

      // Bundle mode: skip every variant/availability check against productFound.
      // The cart adds the bundle product (no per-size inventory), so the
      // curated pack's variant availability is irrelevant here. Button state
      // derives entirely from isBundleReady (every slot has an in-stock single).
      // Forcing productAvailable='true' also recovers from a stale 'false' set
      // by a prior pre-bundle pass through updateFormState.
      if (this.isCustomPackEligible() && this.isBundleMode) {
        const ready = this.isBundleReady()
        submitButton.disabled = !ready
        submitButtonText.innerHTML = this.getSubmitButtonText()
        this.dataset.productAvailable = 'true'
        return
      }

      // If all options are selected, check if the variant exists and is available
      const selectedVariant = this.findVariantFromOptions(selectedInputs)

      if (!selectedVariant) {
        // The active product doesn't offer the selected size combo (e.g.
        // switching to a pack that lacks 4XL). There's intentionally no
        // "unavailable" UI state — deselect the size and fall back to the
        // normal "choose a size" state.
        const t = getPdpTranslations()
        variantSelects.querySelectorAll('input:checked').forEach((input) => {
          input.checked = false
        })
        optionGroups.forEach((group) => {
          const sizeDisplay =
            group.previousElementSibling?.querySelector('.js-selected-size-display')
          if (sizeDisplay) sizeDisplay.textContent = t.choose_size || 'Choose a Size'
        })
        this.dataset.selectedSize = ''
        this.allOptionsSelected = false

        // No size selected → clear any size-driven OOS state from the pack
        // tiles so they return to the neutral (all-available) state.
        this.querySelectorAll('product-colors input[name*="curated-pack-selector"]').forEach(
          (input) => {
            input.classList.remove('disabled')
            input.dataset.disabled = 'false'
            // No-op for US (attribute never set there)
            delete input.dataset.fontanaOos
          },
        )

        // Restore any tile images swapped to an OOS-substitute thumbnail by a
        // prior selected size (updateProductAvailableState does this swap). With
        // no size selected the tiles must show their original images again.
        this.querySelectorAll('product-colors input').forEach((input) => {
          const img = input.closest('label')?.querySelector('img')
          if (img && img.dataset.originalSrc) img.src = img.dataset.originalSrc
        })

        submitButton.disabled = true
        submitButtonText.innerHTML = submitButton.dataset.selectSizeText
        this.dataset.productAvailable = false
        this.updateURL(this.dataset.productHandle, '')
        this.updateProductInfoDataFromJsonData(productFound)
        return
      }

      // OOS substitute: swap to the substitute product/variant via section fetch
      if (selectedVariant.oos_substitute) {
        const sub = selectedVariant.oos_substitute
        // Guard: don't overwrite if already in substitute mode — preserves path back to true original
        if (!this._oosOriginalContext) {
          this._oosOriginalContext = {
            productHandle: this.dataset.productHandle,
            variants: productFound.variants,
          }
        }
        // Update tile images immediately (updateColors is skipped during substitute swaps)
        this.updateProductAvailableState(productFound)
        const productUrl = `${Shopify.routes.root}products/${sub.product_handle}`
        this._isOosSubstituteSwap = true
        const updateCallback = this.handleDataUpdateBig(productUrl)
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, [], false, sub.variant_id),
          callback: (html) => {
            updateCallback(html)
            this._isOosSubstituteSwap = false
            this.showOosSubstituteBubble(sub.text)
          },
        })
        return
      }

      this.hideOosSubstituteBubble()

      this.dataset.variantId = selectedVariant.id
      this.dataset.sku = selectedVariant.sku
      this.dataset.lowStockVariant = selectedVariant.low_stock
      const formInput = this.querySelector('product-form form input[name="id"]')
      formInput.value = selectedVariant.id
      // Non-US: Fontana-OOS is sold out like any OOS variant
      const isSoldOut =
        !selectedVariant.available ||
        this.isFontanaSoldOutVariant(selectedVariant, this.isPackProduct(productFound))
      formInput.disabled = isSoldOut
      this.updateURL(this.dataset.productHandle, selectedVariant.id)

      this.updateProductInfoDataFromJsonData(productFound, selectedVariant)

      if (window.hideFomoBadge) hideFomoBadge()
      if (selectedVariant.low_stock && window.showLowStockBadge)
        showLowStockBadge({ checkFirst: false })

      if (isSoldOut) {
        // Sold-out curated pack that has a custom-pack equivalent: drop the
        // user straight into bundle mode pre-filled from the active pack's
        // colors — same outcome as clicking that pack's Customize button —
        // instead of surfacing the Notify Me form. Substitute-configured
        // variants never reach here (handled above).
        //
        // setBundleMode(true) MUST run before revealTCustomPack: its
        // preFillBundleFromPack() calls updateBundleModeUi() → updateFormState()
        // synchronously, and without bundle mode already on, that nested call
        // re-enters this same OOS branch and recurses until the tab freezes.
        // Flipping the flag first makes those nested calls hit the bundle-mode
        // early-return above.
        //
        // Skip clearance packs — they're end-of-life inventory with no
        // bundle-eligible variants, so Notify Me is the correct fallback.
        if (
          this.isCustomPackEligible() &&
          !this.isBundleMode &&
          this.dataset.productClearance !== 'true'
        ) {
          // Tiles must reflect the new size before the early return
          if (this.dataset.isVarietyPacks == 'false') this.updateProductAvailableState(productFound)
          this.setBundleMode(true)
          this.revealTCustomPack()
          return
        }

        submitButton.disabled = true
        submitButtonText.innerHTML = submitButton.dataset.soldOutText
        this.dataset.productAvailable = false

        // Update Klaviyo form variant ID
        const klaviyoForm = this.querySelector('klaviyo-notify-me')
        if (klaviyoForm) {
          const variantIdInput = klaviyoForm.querySelector('input[name="variant_id"]')
          if (variantIdInput) {
            variantIdInput.value = selectedVariant.id
          }
          const formButton = klaviyoForm.querySelector('button[type="submit"]')
          if (formButton) {
            formButton.dataset.productId = this.dataset.productId
            formButton.dataset.variantId = selectedVariant.id
            formButton.dataset.productTitle = this.dataset.productTitle
            formButton.dataset.productUrl = this.dataset.url
          }
        }
        if (this.dataset.isVarietyPacks == 'false') {
          this.updateProductAvailableState(productFound)
        }
        return
      }

      // In bundle mode ATC stays disabled until every slot has a real,
      // in-stock variant id (isBundleReady) and no section render is in flight
      // (.loading) — the latter just greys it out mid-switch.
      const bundleNotReady =
        this.isCustomPackEligible() &&
        this.isBundleMode &&
        (!this.isBundleReady() || this.isBundleRenderInFlight())
      // Non-US: hold the ATC until the pack's Fontana lookup settles (resync releases it)
      const isFontanaPending = this.isPackFontanaPending(productFound)
      this._isAwaitingPackFontana = isFontanaPending
      if (isFontanaPending) this.loadPackFontanaOosStates()
      submitButton.disabled = bundleNotReady || isFontanaPending
      submitButtonText.innerHTML =
        this.isCustomPackEligible() && this.isBundleMode
          ? this.getSubmitButtonText()
          : submitButton.dataset.addToCartText
      this.dataset.productAvailable = true

      if (this.dataset.isVarietyPacks == 'false') {
        this.updateProductAvailableState(productFound)
      }

      if (shouldSendDataLayer) this.sendDataLayerInfo()
    }

    // Singles: own is_fontana_oos. Pack variants: components for native bundles,
    // own flag for plain packs. Pack variants stay neutral until the lookup
    // resolves (a bundle's own metafield must never be used). Non-US only.
    isVariantFontanaOos(variant, isPackVariant) {
      if (!variant || !this.isFontanaSoldOutRegion()) return false
      if (!isPackVariant) return variant.is_fontana_oos === true
      if (packVariantFontanaOos.has(variant.id)) return packVariantFontanaOos.get(variant.id)
      return plainPackVariantIds.has(variant.id) && variant.is_fontana_oos === true
    }

    // All Fontana logic is non-US only; the US PDP is untouched.
    isFontanaSoldOutRegion() {
      return this.dataset.country !== 'US'
    }

    isFontanaSoldOutVariant(variant, isPackVariant) {
      return this.isFontanaSoldOutRegion() && this.isVariantFontanaOos(variant, isPackVariant)
    }

    // Single variant that can be bought (in stock and not sold out via Fontana).
    isSingleVariantPurchasable(variant) {
      return variant?.available === true && !this.isFontanaSoldOutVariant(variant, false)
    }

    // Flags a swatch/tile input for the red strikethrough (non-US only).
    // Fully sold-out variants keep the white strikethrough.
    setSwatchFontanaOosState(input, variant, isPackVariant = false) {
      if (!SHOW_FONTANA_OOS_STYLE || !this.isFontanaSoldOutRegion()) return
      const isFontanaOnlyOos =
        variant?.available === true && this.isVariantFontanaOos(variant, isPackVariant)
      input.dataset.fontanaOos = isFontanaOnlyOos ? 'true' : 'false'
    }

    getCuratedPackTileInputs() {
      return this.querySelectorAll('product-colors input[name*="curated-pack-selector"]')
    }

    // Re-flags curated pack tiles for the selected size from the cached lookup.
    refreshPackFontanaOosStates() {
      const selectedSize = this.dataset.selectedSize
      if (!selectedSize) return
      const packs = this.jsonData?.packs_data_json || []
      this.getCuratedPackTileInputs().forEach((input) => {
        const productId = Number(input.dataset.productId)
        const pack = packs.find((p) => p.id === productId)
        const variant = pack?.variants.find((v) => v.title === selectedSize)
        this.setSwatchFontanaOosState(input, variant, true)
        // Substitute sizes stay selectable, matching updateProductAvailableState
        if (this.isFontanaSoldOutVariant(variant, true) && !this.hasOosSubstituteSize(input, variant)) {
          input.classList.add('disabled')
          input.dataset.disabled = 'true'
        }
      })
    }

    // Tile size kept selectable because it swaps to an OOS substitute
    hasOosSubstituteSize(input, variant) {
      const oosSubSizes = input.dataset.oosSubSizes ? input.dataset.oosSubSizes.split(', ') : []
      return oosSubSizes.includes(variant?.option1)
    }

    getPackFontanaLookupIds() {
      return (this.jsonData?.packs_data_json || []).map((pack) => pack.id)
    }

    // Lookup runs when there are pack tiles to flag or the active product is a pack
    shouldLoadPackFontanaOos(packIds) {
      if (!this.isFontanaSoldOutRegion() || !packIds.length) return false
      if (this.getCuratedPackTileInputs().length) return true
      return packIds.includes(Number(this.dataset.productId))
    }

    // Non-US pack whose Fontana lookup will run but hasn't settled yet
    isPackFontanaPending(productFound) {
      if (!this.isFontanaSoldOutRegion() || !this.isPackProduct(productFound)) return false
      if (settledPackFontanaIds.has(productFound.id)) return false
      const packIds = this.getPackFontanaLookupIds()
      return packIds.includes(productFound.id) && this.shouldLoadPackFontanaOos(packIds)
    }

    // Disables an enabled ATC (server-rendered or re-rendered) until the selected
    // pack's lookup settles. resyncFormStateAfterPackFontanaLoad releases it.
    holdAtcForPendingPackFontana() {
      if (this.isCustomPackEligible() && this.isBundleMode) return
      // Substitute mode never resyncs, so a hold would never be released
      if (this._oosOriginalContext) return
      if (!this.jsonData) return
      const productFound = this.getProductById(Number(this.dataset.productId))
      if (!this.isPackFontanaPending(productFound)) return
      const submitButton = this.querySelector('product-form form button[type="submit"]')
      if (!submitButton || submitButton.disabled) return
      submitButton.disabled = true
      this._isAwaitingPackFontana = true
      this.syncShopPayButtonState()
    }

    // Loads pack component data (once) then flags the tiles
    loadPackFontanaOosStates() {
      const packIds = this.getPackFontanaLookupIds()
      if (!this.shouldLoadPackFontanaOos(packIds)) return
      const packsData = this.jsonData?.packs_data_json || []
      ensurePackFontanaOosLoaded(packIds, packsData, this.jsonData?.singles_data_json || []).then(() => {
        if (!this.isConnected) return
        this.refreshPackFontanaOosStates()
        this.resyncFormStateAfterPackFontanaLoad()
      })
    }

    // Section re-renders (cut/family/style switches) swap in fresh pack tiles
    // and a new pack list: look up any new packs, re-flag tiles, re-sync the form.
    reloadPackFontanaOosStates() {
      this.holdAtcForPendingPackFontana()
      this.loadPackFontanaOosStates()
    }

    // Quick-add modal: <product-info> is a singleton connected once, empty, so
    // connectedCallback never sees the product injected on each open. Called by
    // quick-add-modal.js after the content is in place. Non-US only.
    handleQuickModalContentUpdate() {
      if (!this.isFontanaSoldOutRegion()) return
      // Fresh content: drop state left over from the previously viewed product
      this._isAwaitingPackFontana = false
      this._oosOriginalContext = null
      this.reloadPackFontanaOosStates()
    }

    // Selected variant is Fontana-OOS but the ATC still sells it (e.g. a
    // server-rendered form for a bundle Liquid can't check)
    isFontanaSoldOutStateStale(productFound) {
      const submitButton = this.querySelector('product-form form button[type="submit"]')
      if (!submitButton || submitButton.disabled) return false
      const selectedSize = this.getMainSelectedSize()
      const variant = productFound.variants?.find((v) => v.title === selectedSize)
      return this.isFontanaSoldOutVariant(variant, this.isPackProduct(productFound))
    }

    // Main size selector only (the selectedSize getter also reads the custom-pack modal)
    getMainSelectedSize() {
      const variantSelects = this.getMainVariantSelects()
      if (!variantSelects) return ''
      return Array.from(variantSelects.querySelectorAll('.liquid-pdp__variant-selector-buttons'))
        .map((group) => group.querySelector('input:checked')?.value)
        .filter(Boolean)
        .join(' / ')
    }

    // Non-US: re-runs the form state only when a lookup changes it (held ATC
    // to release, or a now-sold-out variant). Once applied, neither condition
    // holds, which stops updateFormState -> lookup -> resync from re-looping.
    resyncFormStateAfterPackFontanaLoad() {
      if (!this.isFontanaSoldOutRegion()) return
      // Substitute mode: dataset.productId is still the original product, so
      // updateFormState would re-trigger the substitute swap in an endless loop
      if (this._oosOriginalContext) return
      if (this.isCustomPackEligible() && this.isBundleMode) return
      // A section render is in flight: its callback re-runs the lookup + resync
      // (reloadPackFontanaOosStates). Resyncing now would act on the outgoing product.
      if (this.isBundleRenderInFlight()) return
      const productFound = this.getProductById(Number(this.dataset.productId))
      if (!productFound) return
      if (!this._isAwaitingPackFontana && !this.isFontanaSoldOutStateStale(productFound)) return
      // The triggering render/selection already pushed view_item
      this.updateFormState(productFound, { shouldSendDataLayer: false })
      this.syncShopPayButtonState()
    }

    updateProductAvailableState(productFound) {
      const productFoundOptionsSize = productFound.options?.length
      const variantSelects = this.querySelector('variant-selects')
      if (!variantSelects) return

      // Get all checked inputs with their names and values
      const selectedOptions = {}
      variantSelects.querySelectorAll('input:checked').forEach((input) => {
        selectedOptions[input.name] = input.value
      })

      // For each color input, check if it has the exact option combination
      this.querySelectorAll('product-colors input').forEach((input) => {
        const colorProductId = Number(input.dataset.productId)
        let productFound = this.jsonData.packs_data_json.find(
          (product) => product.id === colorProductId,
        )
        const isPackProduct = Boolean(productFound)
        if (!productFound) {
          productFound = this.jsonData.singles_data_json.find(
            (product) => product.id === colorProductId,
          )
        }
        if (!productFound) return

        const variantFound = productFound.variants.find(
          (variant) => variant.title === this.dataset.selectedSize,
        )
        // Fontana check is independent of (and wins over) general availability
        this.setSwatchFontanaOosState(input, variantFound, isPackProduct)
        if (!variantFound) {
          if (productFoundOptionsSize === productFound.options?.length) {
            input.classList.add('disabled')
            input.dataset.disabled = 'true'
          }
          // Restore original tile image if it was swapped by a previous substitute size
          const img = input.closest('label')?.querySelector('img')
          if (img && img.dataset.originalSrc) img.src = img.dataset.originalSrc
          return
        }

        let variantAvailable = variantFound.available
        // Non-US Fontana-OOS counts as unavailable (substitute/preorder overrides below still win)
        if (this.isFontanaSoldOutVariant(variantFound, isPackProduct)) variantAvailable = false
        if (this.hasOosSubstituteSize(input, variantFound)) variantAvailable = true
        // Preorder products should not show as OOS
        if (productFound.is_preorder) variantAvailable = true

        if (variantAvailable) {
          input.classList.remove('disabled')
          input.dataset.disabled = 'false'
        } else {
          input.classList.add('disabled')
          input.dataset.disabled = 'true'
        }

        // Swap tile image to substitute product image when applicable
        const img = input.closest('label')?.querySelector('img')
        if (img && img.dataset.originalSrc) {
          if (variantFound.oos_substitute?.product_image)
            img.src = variantFound.oos_substitute.product_image
          else img.src = img.dataset.originalSrc
        }
      })

      // a11y (WCAG 1.3.2): don't re-sort swatch DOM post-load — moving nodes
      // breaks the reading/focus sequence for screen reader users. Keep DOM
      // order and pick the visible window by availability instead.
      const productColors = this.querySelector('product-colors')
      if (productColors) {
        const swatchesWrappers = productColors.querySelectorAll(
          '.liquid-pdp__product-color-swatches-wrapper',
        )
        swatchesWrappers.forEach((wrapper) => {
          const labels = Array.from(wrapper.querySelectorAll('label'))
          const swatchLimit = Number(wrapper.dataset.swatchLimit) || this.firstLoadLimit
          const isLabelDisabled = (label) =>
            label.querySelector('input').classList.contains('disabled')
          // Prioritize available swatches for the visible slots (same set the
          // old sort produced), without moving elements.
          const prioritized = [
            ...labels.filter((label) => !isLabelDisabled(label)),
            ...labels.filter(isLabelDisabled),
          ]
          const visibleLabels = new Set(prioritized.slice(0, swatchLimit))
          labels.forEach((label) => {
            label.classList.toggle(
              'liquid-pdp__product-color-swatch--hidden',
              !visibleLabels.has(label),
            )
          })
          // Send button to the end of the wrapper
          const button = wrapper.querySelector('.liquid-pdp__singles-swatches__toggle')
          if (button) wrapper.appendChild(button)

          this.checkNumberOfVisibleSwatches(labels, wrapper)
        })
      }

      handleNotReturningSwatches(this)
      this.loadPackFontanaOosStates()
    }

    updateProductGalleryPosition(productFound, options = {}) {
      if (options.useFirstImageForPacks && this.isPackProduct(productFound)) {
        const gallery = this.productGallery
        if (gallery?.swiper) {
          const firstSlide = gallery.swiperElement?.querySelector(
            '.swiper-wrapper > .swiper-slide:first-child',
          )
          const hasVideoAtPosition0 = firstSlide?.classList.contains('js-carousel-video')
          requestAnimationFrame(() => {
            gallery.swiper.slideTo(hasVideoAtPosition0 ? 1 : 0, 0)
          })
        }
        return
      }
      const variantFound = productFound.variants.find(
        (variant) => variant.title === this.dataset.selectedSize,
      )
      if (!variantFound) return
      const selectedVariantImagePosition = variantFound.featured_image_position
      if (!selectedVariantImagePosition) return
      this.productGallery.goToPosition(selectedVariantImagePosition)
    }

    isPackProduct(productFound) {
      return !!productFound && productFound.pack_size != null && productFound.pack_size !== 'Single'
    }

    checkNumberOfVisibleSwatches(labels, wrapper) {
      if (!wrapper) return

      const toggle = wrapper.querySelector('.liquid-pdp__singles-swatches__toggle')
      if (!toggle) return

      let visibleLabelsCount = 0
      let invisibleLabelsCount = 0
      let hiddenSelectableLabelsCount = 0
      const swatchLimit = Number(wrapper.dataset.swatchLimit) || this.firstLoadLimit
      labels.forEach((label) => {
        const input = label.querySelector('input')
        const isCollapsedHidden = label.classList.contains(
          'liquid-pdp__product-color-swatch--hidden',
        )
        if (isCollapsedHidden && !input?.classList.contains('disabled')) {
          hiddenSelectableLabelsCount++
        }
        if (window.getComputedStyle(label).display === 'none') {
          invisibleLabelsCount++
        } else {
          visibleLabelsCount++
        }
      })
      if (hiddenSelectableLabelsCount === 0) {
        toggle.classList.add('hide')
      } else if (visibleLabelsCount < swatchLimit && invisibleLabelsCount > 0) {
        toggle.classList.add('hide')
        if (!wrapper.classList.contains('showing-all-swatches')) {
          wrapper.classList.add('showing-all-swatches')
        }
      } else {
        toggle.classList.remove('hide')
      }
    }

    updateColorList(colorList) {
      const colorDisplay = this.querySelector('[data-color-display]')
      if (colorDisplay) {
        colorDisplay.dataset.colorList = colorList
        const productColors = this.querySelector('product-colors')
        if (productColors) {
          productColors.setupToggleColors()
        }
      }
    }

    updateGallery(productFound) {
      // console.log('updateGallery', productFound.carousel_video, this.jsonData.video_data)
      const gallery = this.querySelector('product-gallery')
      if (gallery && productFound.media && productFound.media.length > 0) {
        gallery.updateGalleryMedia(
          productFound.media,
          productFound.carousel_video,
          this.jsonData.video_data,
          productFound.media_test,
        )
      }
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode.querySelector(
        'variant-selects [data-selected-variant]',
      )?.innerHTML
      return !!selectedVariant ? JSON.parse(selectedVariant) : null
    }

    updateOptionValues(html) {
      return new Promise((resolve) => {
        const variantSelects = html.querySelector('variant-selects')

        if (variantSelects && this.variantSelectors) {
          // Save the size options wrapper before update
          const existingSizeOptions = this.variantSelectors.querySelector('.size-options-wrapper')

          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, [])

          // If there was a size options wrapper, restore it
          if (existingSizeOptions) {
            const newSizeOptionsWrapper =
              this.variantSelectors.querySelector('.size-options-wrapper')
            if (newSizeOptionsWrapper) {
              newSizeOptionsWrapper.replaceWith(existingSizeOptions)
            }
          }
        }
        resolve()
      })
    }

    updateColors(html) {
      if (this.dataset.isVarietyPacks == 'true') return
      // Substitute swaps should not replace color tiles — preserves selected tile, expanded state, and position
      if (this._isOosSubstituteSwap) return
      const productColors = html.querySelector('product-colors')
      if (productColors && this.productColors) {
        HTMLUpdateUtility.viewTransition(this.productColors, productColors, [])
      }
    }

    updateColorsWithJsonData(html) {
      const colorsWays = html.querySelector('product-colors [data-color-display]')
      const oldColorsWays = this.productColors.querySelector('[data-color-display]')
      if (colorsWays && oldColorsWays) {
        HTMLUpdateUtility.viewTransition(oldColorsWays, colorsWays, [])
      }
      const selectedSize = this.selectedSize
      if (!selectedSize) return
      const singlesDataJson = this.jsonData.singles_data_json
      if (!singlesDataJson) return
      singlesDataJson.forEach((product) => {
        const productHandle = product.handle
        const foundColorInput = this.querySelector(`product-colors input[value="${productHandle}"]`)
        if (!foundColorInput) return
        const foundVariant = product.variants.find((variant) => variant.title === selectedSize)
        if (!foundVariant) return
        // Preorder products should not show as OOS
        if (foundVariant.available || product.is_preorder) {
          foundColorInput.classList.remove('disabled')
        } else {
          foundColorInput.classList.add('disabled')
        }
      })
    }

    updateFamilySelector(html) {
      const productStyle = html.querySelector('product-style.family-selector')
      if (productStyle) {
        HTMLUpdateUtility.viewTransition(this.productFamily, productStyle, [])
      }
    }

    updateStyleSelectors(html) {
      const productStyles = html.querySelectorAll('product-style[data-style]')
      if (productStyles.length > 0) {
        productStyles.forEach((productStyle) => {
          const productStyleHandle = productStyle.dataset.style
          const thisProductStyle = this.querySelector(
            `product-style[data-style="${productStyleHandle}"]`,
          )
          if (productStyleHandle && thisProductStyle) {
            HTMLUpdateUtility.viewTransition(thisProductStyle, productStyle, [])
          }
        })
      }
    }

    updatePackSelectors(html) {
      const packSelectors = html.querySelector('product-pack-selector')
      if (packSelectors && this.packSelectors) {
        HTMLUpdateUtility.viewTransition(this.packSelectors, packSelectors, [])
      }
    }

    // Section re-render swaps the bar wholesale so the suggested pack-size,
    // base-price-cents and PPI track the new pack context. Snippet emits no
    // markup on the family's largest pack — handle the add/remove/replace
    // cases explicitly since `this` may or may not currently host a bar.
    // Uses replaceWith (not HTMLUpdateUtility.viewTransition) so there's no
    // 500ms double-bar overlap; the utility hides the old node via inline
    // `display: none`, which IG's `display: flex !important` overrides.
    updateUpgradeBar(html) {
      const next = html.querySelector('[data-upgrade-bar]')
      const current = this.querySelector('[data-upgrade-bar]')
      if (next && current) {
        current.replaceWith(next.cloneNode(true))
      } else if (next && !current) {
        // Bar lives at the end of the price row (after the price element). Fall
        // back to after the pack selector only if the row isn't present.
        const priceRow = this.querySelector('.liquid-pdp__price-row')
        if (priceRow) {
          priceRow.appendChild(next.cloneNode(true))
        } else {
          const anchor = this.querySelector('product-pack-selector')
          anchor?.insertAdjacentElement('afterend', next.cloneNode(true))
        }
      } else if (!next && current) {
        current.remove()
      }
    }

    updatePackProgress(html) {
      const next = html.querySelector('.pack-progress')
      const current = this.packProgress
      if (!next || !current) return

      // Show/hide for Singles vs Packs
      current.classList.toggle(
        'pack-progress--hidden',
        next.classList.contains('pack-progress--hidden'),
      )

      current.style.setProperty('--pack-progress', next.style.getPropertyValue('--pack-progress'))
      current.style.setProperty(
        '--discount-font-size',
        next.style.getPropertyValue('--discount-font-size'),
      )
      current.style.setProperty('--tier-center', next.style.getPropertyValue('--tier-center'))
      current.classList.toggle(
        'pack-progress--full',
        next.classList.contains('pack-progress--full'),
      )
      current.classList.toggle(
        'pack-progress--base-tier',
        next.classList.contains('pack-progress--base-tier'),
      )
      current.classList.toggle(
        'pack-progress--single',
        next.classList.contains('pack-progress--single'),
      )

      // Sync current pack size attribute used by boost logic
      current.dataset.currentPackSize = next.dataset.currentPackSize || ''

      // Sync "Up to" messages (innerHTML so the emph markup is preserved)
      current.querySelectorAll('.pack-progress__upto').forEach((curUpto) => {
        const variant = curUpto.classList.contains('pack-progress__upto--single')
          ? 'pack-progress__upto--single'
          : 'pack-progress__upto--packs'
        const nxtUpto = next.querySelector('.' + variant)
        if (nxtUpto && curUpto.innerHTML !== nxtUpto.innerHTML) {
          curUpto.innerHTML = nxtUpto.innerHTML
          curUpto.dataset.maxDiscount = nxtUpto.dataset.maxDiscount
        }
      })

      const nextData = next.dataset.packPriceData || ''
      const curData = current.dataset.packPriceData || ''
      const structureChanged = curData !== nextData

      if (structureChanged) {
        // Pack-size set changed — replace labels and dividers entirely.
        // IMPORTANT: always clone nodes from `next` (the parsed HTML may come from
        // renderProductInfo's response cache; moving nodes would mutate the cache
        // and break subsequent navigations back to the same family).
        current.dataset.packPriceData = nextData
        const currentLabels = current.querySelector('.pack-progress__labels')
        const nextLabels = next.querySelector('.pack-progress__labels')
        if (currentLabels && nextLabels) currentLabels.replaceWith(nextLabels.cloneNode(true))

        // Replace dividers (siblings of labels)
        current.querySelectorAll('.pack-progress__divider').forEach((d) => d.remove())
        const nextDividers = next.querySelectorAll('.pack-progress__divider')
        const newLabels = current.querySelector('.pack-progress__labels')
        nextDividers.forEach((d) => current.insertBefore(d.cloneNode(true), newLabels))
      } else {
        // Same structure — sync --reached class per label
        const nextLabels = next.querySelectorAll('.pack-progress__label')
        current.querySelectorAll('.pack-progress__label').forEach((label, i) => {
          const nxtLabel = nextLabels[i]
          if (!nxtLabel) return
          label.classList.toggle(
            'pack-progress__label--reached',
            nxtLabel.classList.contains('pack-progress__label--reached'),
          )
        })
      }

      // Apply boost overlay (clearance / weekly-deal) on top of the normal state.
      // Also handles the discount-number flip animation to the correct target.
      this.applyPackProgressBoost()
    }

    // Overlay clearance / weekly-deal elevated pct + ppi on top of the normal
    // progress-bar state. Also animates the discount-num to the appropriate
    // target (boosted value when active, otherwise the pack's normal family value).
    applyPackProgressBoost() {
      const bar = this.packProgress
      if (!bar) return

      const packSize = parseInt(bar.dataset.currentPackSize, 10)
      const isWeeklyDeal = this.dataset.isWeeklyDeal === 'true'
      const isClearance = this.dataset.productClearance === 'true'
      const hasBoost = isWeeklyDeal || isClearance

      const normalDisc = this.getPackProgressNormalDisc(bar, packSize)

      // Reset every label's price to its normal ppi + drop boosted class.
      bar.querySelectorAll('.pack-progress__label').forEach((label) => {
        label.classList.remove('pack-progress__label--boosted')
        const np = parseInt(label.dataset.normalPpi, 10)
        const priceEl = label.querySelector('.pack-progress__label-price')
        if (priceEl && Number.isFinite(np)) priceEl.textContent = this.formatPackProgressMoney(np)
      })

      if (!hasBoost) {
        bar.classList.remove('pack-progress--has-boost')
        this.setPackProgressDiscountTarget(normalDisc)
        return
      }

      const boosted = this.computeBoostedDiscountAndPpi(packSize, isWeeklyDeal, isClearance)

      bar.classList.add('pack-progress--has-boost')

      const currentLabel = bar.querySelector(`.pack-progress__label[data-size="${packSize}"]`)
      if (currentLabel) {
        currentLabel.classList.add('pack-progress__label--boosted')
        const priceEl = currentLabel.querySelector('.pack-progress__label-price')
        if (priceEl) priceEl.textContent = this.formatPackProgressMoney(boosted.ppi)
      }

      this.setPackProgressDiscountTarget(boosted.disc)
    }

    // Returns the normal (non-boosted) discount % for the given pack size
    // by reading data-pack-price-data on the bar.
    getPackProgressNormalDisc(bar, packSize) {
      const data = bar.dataset.packPriceData || ''
      for (const entry of data.split(',')) {
        const parts = entry.split(':')
        if (parseInt(parts[1], 10) === packSize) return parseInt(parts[3], 10) || 0
      }
      return 0
    }

    // Computes the boosted discount % and per-item price (cents) for the
    // currently-selected product using product-info dataset values.
    computeBoostedDiscountAndPpi(packSize, isWeeklyDeal, isClearance) {
      const priceDollars = parseFloat(this.dataset.productPrice) || 0
      const capDollars = parseFloat(this.dataset.productCompareAtPrice) || priceDollars
      const weeklyDealPct = parseFloat(this.dataset.weeklyDealDiscountPercentage) || 0

      let priceCents = Math.round(priceDollars * 100)
      if (isWeeklyDeal && !isClearance && weeklyDealPct > 0) {
        const da = Math.floor((priceCents * weeklyDealPct) / 100)
        priceCents = priceCents - da
      }

      const capCents = Math.round(capDollars * 100)
      const disc = capCents > 0 ? Math.round(((capCents - priceCents) / capCents) * 100) : 0
      const ppi = packSize > 0 ? Math.floor(priceCents / packSize) : 0
      return { disc, ppi }
    }

    // Animates (or snaps) the progress-bar discount-num to the given target %.
    // Preserves the previous value via dataset.flipValue so animateNumberFlip
    // can animate from the currently-displayed number.
    setPackProgressDiscountTarget(targetDisc) {
      const num = this.packProgress?.querySelector('.pack-progress__discount-num')
      if (!num) return
      const newVal = parseInt(targetDisc, 10) || 0
      const prev = parseInt(num.dataset.discount, 10)
      if (Number.isFinite(prev) && !num.dataset.flipValue) num.dataset.flipValue = String(prev)

      if (newVal > 0 && window.animateNumberFlip) {
        window.animateNumberFlip(num, newVal)
      } else {
        num.textContent = newVal > 0 ? String(newVal) : ''
        num.dataset.flipValue = String(newVal)
      }
      num.dataset.discount = newVal
    }

    // Formats a cents integer to match the Liquid `{{ money_prefix }}{{ value | money }}`
    // output used when the bar was first rendered.
    formatPackProgressMoney(cents) {
      const amt = Number(cents)
      if (!Number.isFinite(amt)) return ''
      const probeFormatted = this.formatMoneyFromProbe(amt)
      if (probeFormatted !== null) return probeFormatted
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(amt)
      return `$${(amt / 100).toFixed(2)}`
    }

    updateProductTitle(html) {
      const productTitle = html.querySelector('.js-product-title')
      const productInfo = html.querySelector('product-info')
      if (this.shouldUseProductTitle() && productTitle && productInfo?.dataset.productTitle)
        productTitle.textContent = productInfo.dataset.productTitle

      if (productTitle && this.productTitle) {
        HTMLUpdateUtility.viewTransition(this.productTitle, productTitle, [])
      }
    }

    shouldUseProductTitle() {
      return this.closest('quick-modal, deal-quick-modal')?.dataset.useProductTitle === 'true'
    }

    updateProductDescriptionVariety(html) {
      const productDescriptionVariety = html.querySelector('.js-product-description-variety')
      if (productDescriptionVariety && this.productDescriptionVariety) {
        HTMLUpdateUtility.viewTransition(
          this.productDescriptionVariety,
          productDescriptionVariety,
          [],
        )
      }
    }

    updateProductTitleFromJsonData(productFound) {
      const productTitle = this.querySelector('.js-product-title')
      if (productTitle) {
        productTitle.innerHTML = productFound.title
      }
    }

    updateProductInfoDataFromJsonData(productFound = null, selectedVariant = null) {
      if (productFound) {
        this.dataset.productId = productFound.id
        this.dataset.url = `/products/${productFound.handle}`
        this.dataset.productHandle = productFound.handle
        this.dataset.productTitle = productFound.title
        this.applyPriceUiData(productFound.price_ui, productFound.pack_size)
      }
      if (selectedVariant) {
        this.dataset.variantId = selectedVariant.id
        this.dataset.sku = selectedVariant.sku
        this.dataset.lowStockVariant = selectedVariant.low_stock
      }
    }

    updateProductInfoData(html) {
      // OOS substitute swap should not update analytics or dataset identity fields
      if (this._isOosSubstituteSwap) return

      const productInfo = html.querySelector('product-info')
      if (productInfo) {
        // In bundle mode the cart adds the bundle product (no per-size
        // inventory), so the curated pack's server-rendered availability is
        // irrelevant. Copying an OOS 'false' here would clobber the 'true'
        // set on bundle entry and surface the Notify Me form — this async
        // section render can resolve AFTER bundle mode was re-entered when
        // returning to an OOS pack (the intermittent ATC/Notify-Me bug).
        this.dataset.productAvailable =
          this.isCustomPackEligible() && this.isBundleMode
            ? 'true'
            : productInfo.dataset.productAvailable
        this.dataset.productPackSize = productInfo.dataset.productPackSize
        this.dataset.membershipStatus = productInfo.dataset.membershipStatus
        this.dataset.customerId = productInfo.dataset.customerId
        this.dataset.colorPack = productInfo.dataset.colorPack
        this.dataset.loginStatus = productInfo.dataset.loginStatus
        this.dataset.packQuantity = productInfo.dataset.packQuantity
        this.dataset.ecommerceCurrency = productInfo.dataset.ecommerceCurrency
        this.dataset.productPrice = productInfo.dataset.productPrice
        this.dataset.productPriceFormatted = productInfo.dataset.productPriceFormatted
        this.dataset.productCompareAtPrice = productInfo.dataset.productCompareAtPrice
        this.dataset.productCompareAtPriceFormatted =
          productInfo.dataset.productCompareAtPriceFormatted
        this.dataset.productId = productInfo.dataset.productId
        this.dataset.url = productInfo.dataset.url
        this.dataset.productHandle = productInfo.dataset.productHandle
        this.dataset.productTitle = productInfo.dataset.productTitle
        this.dataset.productDiscount = productInfo.dataset.productDiscount
        this.dataset.productBrand = productInfo.dataset.productBrand
        this.dataset.productCategory = productInfo.dataset.productCategory
        this.dataset.productCategory2 = productInfo.dataset.productCategory2
        this.dataset.productCategory3 = productInfo.dataset.productCategory3
        this.dataset.productCategory5 = productInfo.dataset.productCategory5
        this.dataset.sku = productInfo.dataset.sku
        this.dataset.lowStockVariant = productInfo.dataset.lowStockVariant
        this.dataset.variantId = productInfo.dataset.variantId
        this.dataset.isWeeklyDeal = productInfo.dataset.isWeeklyDeal
        this.dataset.weeklyDealDiscountPercentage = productInfo.dataset.weeklyDealDiscountPercentage
        this.dataset.productClearance = productInfo.dataset.productClearance
        this.dataset.clearanceSelectTagged = productInfo.dataset.clearanceSelectTagged
        this.dataset.clearanceBaseTagged = productInfo.dataset.clearanceBaseTagged
        this.dataset.productFinalSale = productInfo.dataset.productFinalSale
        this.dataset.pdpDiscountExcluded = productInfo.dataset.pdpDiscountExcluded
        this.dataset.manualDiscountPercentage = productInfo.dataset.manualDiscountPercentage
        this.dataset.manualDiscountAutoApplied = productInfo.dataset.manualDiscountAutoApplied
        this.dataset.promoRoundToNinetyNineCents = productInfo.dataset.promoRoundToNinetyNineCents
        this.syncPdpQuantitySelector()

        // Bundle builder identity — always copy from new HTML so the dataset
        // tracks (family, pack_size) truthfully. Don't gate on the CURRENT
        // eligibility: when switching A (eligible) → B (not) → A, the B step
        // makes us non-eligible, and gating would then prevent us from re-arming
        // when returning to A.
        this.dataset.bundleProductHandle = productInfo.dataset.bundleProductHandle || ''
        this.dataset.bundleProductVariantId = productInfo.dataset.bundleProductVariantId || ''
        this.dataset.bundleMaxItems = productInfo.dataset.bundleMaxItems || ''

        // Toggle boolean attribute based on whether the new product-info has it
        if (productInfo.hasAttribute('data-can-show-discount-progress-bar'))
          this.setAttribute('data-can-show-discount-progress-bar', '')
        else this.removeAttribute('data-can-show-discount-progress-bar')

        // update dataset entries that start with 'style'
        const entries = Object.entries(productInfo.dataset).filter(([k]) => k.startsWith('style'))
        entries.forEach(([k, v]) => (this.dataset[k] = v))

        let imageUrl = null
        const imageCarousel = html.querySelector('.liquid-pdp__gallery-main')
        const firstProductImage = imageCarousel.querySelector('.first-product-image')
        // console.log(firstProductImage)
        if (firstProductImage) imageUrl = firstProductImage.querySelector('img').src

        const contents = [
          {
            id: productInfo.dataset.productId,
            variantId: productInfo.dataset.variantId,
            sku: productInfo.dataset.sku,
            item_price: Number(productInfo.dataset.productPrice),
            title: productInfo.dataset.productTitle,
            handle: productInfo.dataset.productHandle,
            category: productInfo.dataset.productCategory,
            url: getProductUrl(productInfo.dataset.url),
            image: imageUrl,
            quantity: 1,
            type: 'product',
          },
        ]
        sendViewContent(productInfo.dataset.ecommerceCurrency, contents)
        // console.log(
        //   'updateProductInfoData',
        //   contents[0].id,
        //   contents[0].handle,
        //   getProductUrl(productInfo.dataset.url),
        // )

        updateInsiderClubUpsell(html, this)
        updateCashbackMessage(html)
        updateStoreCreditInfo(html, this)
        updatePdpModal(html)

        if (this.bxgyPrice) {
          const space = currencySymbol === 'MX$' ? ' ' : ''
          const bxgyPriceCents = Math.round(Number(productInfo.dataset.productPrice) * 100)
          this.bxgyPrice.textContent =
            this.formatMoneyFromProbe(bxgyPriceCents) ??
            `${currencySymbol}${space}${Number(productInfo.dataset.productPrice).toFixed(2)}`
        }

        if (document.querySelector('input[name="insider-club-upsell"]')?.checked) {
          document
            .querySelector('product-form shopify-accelerated-checkout')
            ?.classList.add('hidden')
        } else {
          document
            .querySelector('product-form shopify-accelerated-checkout')
            ?.classList.remove('hidden')
        }

        if (
          window.isMultiFamilyLp === true ||
          window.isMenuWithPdp === true ||
          this.dataset.updateUrl === 'false'
        ) {
          this.dataset.updateUrl = false
        } else {
          this.dataset.updateUrl = true
        }
      }
    }

    updateProductStarRating(html) {
      const productStarRating = html.querySelector('.js-product-star-rating')
      if (productStarRating && this.productStarRating) {
        HTMLUpdateUtility.viewTransition(this.productStarRating, productStarRating, [])
      }
    }

    updateProductPrice(html) {
      const productPrice = html.querySelector('.js-product-price')
      if (productPrice && this.productPrice) {
        HTMLUpdateUtility.viewTransition(this.productPrice, productPrice, [])
      }
    }

    updateProductPriceBlock(html) {
      const productPriceBlock = html.querySelector('.pdp-product-price-wrapper')

      if (productPriceBlock && this.productPriceBlock) {
        // Strip state classes so the hidden old node (kept for 500ms by viewTransition)
        // can't keep matching :has() deal-row/discount layout rules
        this.productPriceBlock.classList.remove(
          'pdp-product-price-wrapper--deal-row-active',
          'pdp-product-price-wrapper--manual-discount-active',
        )
        HTMLUpdateUtility.viewTransition(
          this.productPriceBlock,
          productPriceBlock,
          [(newPriceBlock) => this.updateSelectedClearanceFinalPrice(newPriceBlock)],
          [],
        )
        this.updatePackPerItemPriceBlock(html)
      }
    }

    updatePackPerItemPriceBlock(html) {
      const nextPerItemPrices = Array.from(
        html.querySelectorAll('.pdp-price-and-discount__per-item-price'),
      )
      const currentPerItemPrices = this.querySelectorAll(
        '.pdp-price-and-discount__per-item-price',
      )

      currentPerItemPrices.forEach((currentPerItemPrice, index) => {
        const nextPerItemPrice = nextPerItemPrices[index]
        const nextText = nextPerItemPrice?.textContent?.trim() || ''
        const nextPerItemLabel = nextPerItemPrice?.dataset.perItemLabel

        if (nextPerItemLabel) currentPerItemPrice.dataset.perItemLabel = nextPerItemLabel
        this.copyPriceBreakdownData(currentPerItemPrice, nextPerItemPrice)

        if (!nextPerItemPrice || nextPerItemPrice.hidden || !nextText) {
          currentPerItemPrice.innerHTML = nextPerItemPrice?.innerHTML || ''
          currentPerItemPrice.hidden = true
          return
        }

        currentPerItemPrice.innerHTML = nextPerItemPrice.innerHTML
        currentPerItemPrice.hidden = false
      })

      // Re-sync an open price breakdown tooltip with the new numbers
      window.refreshPriceBreakdownTooltips?.()
    }

    // Data the price breakdown tooltip (assets/pdp-price-breakdown.js) reads off the per-item price
    copyPriceBreakdownData(currentPerItemPrice, nextPerItemPrice) {
      const { finalPriceCents, packSize, discountLabel } = nextPerItemPrice?.dataset || {}
      if (finalPriceCents !== undefined) currentPerItemPrice.dataset.finalPriceCents = finalPriceCents
      if (packSize !== undefined) currentPerItemPrice.dataset.packSize = packSize
      currentPerItemPrice.dataset.discountLabel = discountLabel || ''
    }

    get discountCodePlaceholder() {
      return this.querySelector('.pdp-price-and-discount__discount-placeholder')
    }

    // Blank stand-in for the discount row while the section render is in
    // flight — keeps the layout height stable so content below doesn't jump.
    insertDiscountCodePlaceholder() {
      if (this.discountCodePlaceholder) return
      const topRow = this.productPriceBlock?.closest('.pdp-price-and-discount__top')
      if (!topRow) return

      const placeholder = document.createElement('div')
      placeholder.className = 'pdp-price-and-discount__discount-placeholder'
      placeholder.setAttribute('aria-hidden', 'true')
      topRow.insertAdjacentElement('afterend', placeholder)
    }

    removeDiscountCodePlaceholder() {
      this.discountCodePlaceholder?.remove()
    }

    // Clones the inert section-rendered template into the discount row slot.
    // Real markup + real behavior (the custom element initializes on
    // insertion); prices get filled by updatePendingManualDiscountCodePrice /
    // syncManualDiscountPriceUi right after. Returns whether it inserted.
    insertDiscountCodeFromTemplate() {
      const template = this.querySelector('.js-pdp-discount-code-template')
      const templateBox = template?.content?.querySelector('manual-discount-code')
      if (!templateBox) return false

      const topRow = this.productPriceBlock?.closest('.pdp-price-and-discount__top')
      if (!topRow) return false

      const box = templateBox.cloneNode(true)
      // No fadeInAnimatedElements pass follows client-side inserts — strip the
      // scroll-animation hooks or the box would stay invisible
      box.classList.remove('anim-slide-up')
      box.removeAttribute('data-animate')

      this.removeDiscountCodePlaceholder()
      topRow.insertAdjacentElement('afterend', box)
      return true
    }

    updateDiscountCode(html) {
      // Section result is final — the optimistic placeholder's job is done
      this.removeDiscountCodePlaceholder()
      const newDiscountCode = html.querySelector('manual-discount-code')
      const currentDiscountCode = this.discountCode

      if (newDiscountCode && currentDiscountCode) {
        HTMLUpdateUtility.viewTransition(currentDiscountCode, newDiscountCode, [], [])
        // Drop the hidden old node now instead of after viewTransition's 500ms
        // delay — as a direct child it keeps :has(> manual-discount-code)
        // matched, which can stack a duplicate divider when switching to a
        // flash deal mid-window. No exit transition to preserve here.
        currentDiscountCode.remove()
        return
      }

      if (newDiscountCode && !currentDiscountCode) {
        const priceBlock = this.productPriceBlock
        if (priceBlock) {
          const newNodeWrapper = document.createElement('div')
          HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newDiscountCode.outerHTML)
          const newNode = newNodeWrapper.firstChild
          const priceContainer = priceBlock.closest('.pdp-price-and-discount')
          const pricePerItem = priceContainer?.querySelector(
            '.pdp-price-and-discount__per-item-price',
          )
          // Insert after the top row so the element stays a direct child of
          // .pdp-price-and-discount (the row divider relies on :has(> ...))
          const topRow = priceBlock.closest('.pdp-price-and-discount__top')
          const discountCodeAnchor = topRow || pricePerItem || priceBlock
          discountCodeAnchor.insertAdjacentElement('afterend', newNode)
        }
        return
      }

      if (!newDiscountCode && currentDiscountCode) {
        currentDiscountCode.remove()
      }
    }

    updateCollapsibleWrapper(html) {
      const collapsibleWrapper = html.querySelector('.liquid-pdp__collapsible-wrapper')
      if (collapsibleWrapper && this.collapsibleWrapper) {
        HTMLUpdateUtility.viewTransition(this.collapsibleWrapper, collapsibleWrapper, [])
      }
    }

    updateProductForm(html) {
      const productForm = html.querySelector('product-form')
      if (productForm && this.productForm) {
        // Preserve the custom Shop Pay express button across form swaps.
        // Section fetches render under the default product template (not the
        // quick-add view), so the incoming form omits the button — carry the
        // existing one over so it survives size/pack/style changes.
        const existingShopPayButton = this.productForm.querySelector('.js-shop-pay-button')

        HTMLUpdateUtility.viewTransition(
          this.productForm,
          productForm,
          [],
          [
            (newNode) => {
              // Re-insert the Shop Pay button (server omits it in section
              // fetches) before the ATC submit, then mirror the ATC's disabled
              // state so it stays in sync after the swap.
              const submitButton = newNode.querySelector(
                'form.js-product-form button[type="submit"]',
              )
              if (existingShopPayButton && !newNode.querySelector('.js-shop-pay-button')) {
                if (submitButton)
                  submitButton.parentNode.insertBefore(
                    existingShopPayButton.cloneNode(true),
                    submitButton,
                  )
              }
              const shopPayButton = newNode.querySelector('.js-shop-pay-button')
              if (shopPayButton && submitButton) shopPayButton.disabled = submitButton.disabled
              if (!newNode.classList.contains('anim-slide-up')) return
              newNode.setAttribute('data-animate-init', '')
              newNode.classList.add('is-visible')
            },
          ],
        )
      }
    }

    // Mirror the ATC submit button's disabled state onto the custom Shop Pay
    // button so it enables/disables in lockstep with size selection.
    syncShopPayButtonState() {
      const shopPayButton = this.querySelector('.js-shop-pay-button')
      if (!shopPayButton) return
      const submitButton = this.querySelector('form.js-product-form button[type="submit"]')
      if (!submitButton) return
      shopPayButton.disabled = submitButton.disabled
    }

    updateAfterpay(html) {
      const afterpay = html.querySelector('.afterpay-widget')
      if (afterpay && this.afterpay) {
        HTMLUpdateUtility.viewTransition(this.afterpay, afterpay, [])
      }
    }

    updatePromoCountdownCallout(html) {
      const nextCallout = html.querySelector('.promo-countdown-callout--pdp')
      const currentCallout = this.promoCountdownCallout

      if (nextCallout && currentCallout) {
        HTMLUpdateUtility.viewTransition(currentCallout, nextCallout, [])
        return
      }

      if (nextCallout && !currentCallout) {
        const builderButtonWrapper = this.querySelector('.liquid-pdp__builder-button-wrapper')
        builderButtonWrapper?.insertAdjacentElement('afterend', nextCallout.cloneNode(true))
        return
      }

      if (!nextCallout && currentCallout) {
        currentCallout.remove()
      }
    }

    updatePdpProductContext(html) {
      const pdpProductContext = html.querySelector('pdp-product-context')
      if (pdpProductContext && this.pdpProductContext) {
        HTMLUpdateUtility.viewTransition(this.pdpProductContext, pdpProductContext, [])
      }
    }

    updateUso(html) {
      const productUso = html.querySelector('product-uso')
      if (productUso && this.productUso) {
        HTMLUpdateUtility.viewTransition(this.productUso, productUso, [])
      }
    }

    updateProductGalleryFromHtml(html) {
      updateGalleryPromoText(html)
      // Skip the modal gallery inside t-custom-pack on the fetched HTML —
      // only the main gallery should swap.
      const productGallery =
        Array.from(html.querySelectorAll('product-gallery')).find(
          (el) => !el.closest('t-custom-pack'),
        ) || null
      if (productGallery && this.productGallery) {
        this.productGallery.updateGallery(productGallery)
      }
      updateVdayBadge(html) // must run after gallery update
    }

    updateYourCutSelector(html) {
      // Swap the length + hem toggles wholesale so the checked options and the
      // descriptive copy track the newly selected cut.
      const newYourCutSelector = html.querySelector('.js-your-cut-selector')
      const currentYourCutSelector = this.yourCutSelector
      if (newYourCutSelector && currentYourCutSelector) {
        HTMLUpdateUtility.viewTransition(currentYourCutSelector, newYourCutSelector, [])
      }
    }

    // below-pdp-app-content is its own section (sections/below-pdp-app-content.liquid), so it is
    // not part of the liquid-pdp response: fetch that section for the new product and swap it in.
    updateBelowPdpAppContent(html) {
      const productHandle = html.querySelector('product-info')?.dataset.productHandle
      if (!productHandle || !this.belowPdpAppContent || this.closest('quick-add-modal')) return

      this.swapBelowPdpAppContent(productHandle)
    }

    // Fills the empty below-pdp-app-content rendered where Liquid has no `product` (e.g. the
    // multi_family_lp metaobject template) with the PDP's initial product.
    hydrateBelowPdpAppContent() {
      const hydrate = () => {
        if (this.belowPdpAppContent?.dataset.hydrate !== 'true') return
        this.swapBelowPdpAppContent(this.dataset.productHandle)
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', hydrate, { once: true })
        return
      }

      hydrate()
    }

    swapBelowPdpAppContent(productHandle) {
      // Only the latest request may swap, so fast product changes never apply stale content
      const requestId = (this.belowPdpRequestId || 0) + 1
      this.belowPdpRequestId = requestId

      this.fetchBelowPdpAppContent(productHandle).then((newBelowPdpAppContent) => {
        const oldBelowPdpAppContent = this.belowPdpAppContent
        if (requestId !== this.belowPdpRequestId) return
        if (!newBelowPdpAppContent || !oldBelowPdpAppContent) return

        HTMLUpdateUtility.viewTransition(oldBelowPdpAppContent, newBelowPdpAppContent, [])
        // The swap lands after the full-section fadeInAnimatedElements pass, so run it again or
        // the new element keeps the anim-slide-up hidden state
        if (window.fadeInAnimatedElements) window.fadeInAnimatedElements()
      })
    }

    fetchBelowPdpAppContent(productHandle) {
      const sectionId = this.belowPdpAppContent
        ?.closest('.shopify-section')
        ?.id.replace('shopify-section-', '')
      if (!sectionId) return Promise.resolve(null)

      const params = [`section_id=${sectionId}`]
      if (this.dataset.optionsPoc === 'true') params.push('view=pdp-options-poc')
      const requestUrl = `${this.constructProductUrl(productHandle)}?${params.join('&')}`

      if (this.responseCache.has(requestUrl)) {
        return Promise.resolve(this.responseCache.get(requestUrl))
      }

      return fetch(requestUrl)
        .then((response) => response.text())
        .then((responseText) => {
          const belowPdpAppContent = new DOMParser()
            .parseFromString(responseText, 'text/html')
            .querySelector('below-pdp-app-content')
          if (belowPdpAppContent) this.responseCache.set(requestUrl, belowPdpAppContent)
          return belowPdpAppContent
        })
        .catch((error) => {
          console.error(error)
          return null
        })
    }

    updateModalWrapper(html) {
      const modalWrapper = html.querySelector('.pdp-modal-wrapper')
      if (modalWrapper && this.modalWrapper) {
        HTMLUpdateUtility.viewTransition(this.modalWrapper, modalWrapper, [])
      }
    }

    updateVariantInputs(variantId) {
      this.querySelectorAll('form.js-product-form').forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]')
        input.value = variantId ?? ''
        input.dispatchEvent(new Event('change', { bubbles: true }))
      })
    }

    updateURL(url, variantId) {
      if (this.dataset.updateUrl === 'false') return
      // Homepage menu-with-pdp embeds must not rewrite the browser URL to a PDP.
      if (window.isMenuWithPdp === true || this.closest('menu-with-pdp')) return
      // OOS substitute swap should not change the visible URL
      if (this._isOosSubstituteSwap) return

      const finalUrl = this.constructProductUrl(url)
      this.dataset.url = finalUrl

      const params = new URLSearchParams()
      if (variantId) params.set('variant', variantId)
      if (this.dataset.optionsPoc === 'true') params.set('view', 'pdp-options-poc')
      const liveStream = this.dataset.liveStream || new URLSearchParams(window.location.search).get('live_stream')
      if (liveStream) params.set('live_stream', liveStream)

      const search = params.toString()
      window.history.replaceState({}, '', `${finalUrl}${search ? `?${search}` : ''}`)
    }

    // Re-append the live_stream attribution param (if any) to a base href —
    // section re-renders (pack/style/color/variant changes) rebuild the
    // "view full details" href from scratch, so it must be re-applied every time.
    viewFullDetailsHref(baseHref) {
      if (!this.dataset.liveStream) return baseHref
      const url = new URL(baseHref, window.location.origin)
      url.searchParams.set('live_stream', this.dataset.liveStream)
      return `${url.pathname}${url.search}`
    }

    prependLocalizedRoot(path) {
      const localizedRoot = window.Shopify?.routes?.root || '/'
      const normalizedRoot = localizedRoot === '/' ? '' : localizedRoot.replace(/\/+$/, '')
      const normalizedPath = `/${String(path).replace(/^\/+/, '')}`

      if (
        !normalizedRoot ||
        normalizedPath === normalizedRoot ||
        normalizedPath.startsWith(`${normalizedRoot}/`)
      ) {
        return normalizedPath
      }

      return `${normalizedRoot}${normalizedPath}`
    }

    constructProductUrl(url) {
      // Normalize the current pathname by removing trailing slashes
      const normalizedPathname = window.location.pathname.replace(/\/+$/, '')
      const normalizedUrl = String(url).trim()

      // Check if the URL already contains a full path
      if (normalizedUrl.includes('/products/')) {
        return this.prependLocalizedRoot(normalizedUrl)
      }

      // If we're on a product page, replace the current product with the new one
      if (normalizedPathname.includes('/products/')) {
        // Extract the base path (everything before the last product handle)
        const pathParts = normalizedPathname.split('/')
        const productsIndex = pathParts.indexOf('products')
        if (productsIndex !== -1 && productsIndex < pathParts.length - 1) {
          // Remove the current product handle and add the new one
          const basePath = pathParts.slice(0, productsIndex + 1).join('/')
          return `${basePath}/${normalizedUrl}`
        }
        // Fallback: construct from root
        return `${normalizedPathname.replace(/\/products\/[^\/]+$/, '')}/products/${normalizedUrl}`
      }

      // Not on a product page, construct the product URL from root
      return this.prependLocalizedRoot(`/products/${normalizedUrl}`)
    }

    updateUiFooter(html) {
      const uiFooter = html.querySelector('.js-liquid-pdp__ui-footer')
      if (uiFooter && this.uiFooter) {
        HTMLUpdateUtility.viewTransition(this.uiFooter, uiFooter, [])
      }
    }

    updateBxgy(html) {
      const bxgyWrapper = html.querySelector('.js-bxgy-wrapper')
      if (bxgyWrapper && this.bxgyWrapper) {
        HTMLUpdateUtility.viewTransition(this.bxgyWrapper, bxgyWrapper, [])
      }
    }

    updateSizeChartButton(html) {
      const sizeChartButton = html.querySelector('.js-size-chart-button-wrapper')
      if (sizeChartButton && this.sizeChartButton) {
        HTMLUpdateUtility.viewTransition(this.sizeChartButton, sizeChartButton, [])
      }
    }

    updateSizeChartModal(html) {
      const sizeChartModal = html.querySelector('size-chart-modal, size-chart-modal-tiles')
      if (sizeChartModal && this.sizeChartModal) {
        HTMLUpdateUtility.viewTransition(this.sizeChartModal, sizeChartModal, [])
      }
    }

    updateAvailableInTarget(html) {
      const availableInTarget = html.querySelector('available-in-target')
      if (availableInTarget && this.availableInTarget) {
        HTMLUpdateUtility.viewTransition(this.availableInTarget, availableInTarget, [])
      }
    }

    async updateOkendoReviews() {
      const okendoReviews = document.querySelector('okendo-reviews')
      if (okendoReviews) {
        await okendoReviews.updateReviews()
        updateOkendoReviewsRecommendations()
      }
    }

    updateRebuyRecommendations(html) {
      const rebuyRecommendations = document.querySelector('rebuy-recommendations')
      const productInfo = html?.querySelector('product-info') || this
      if (!rebuyRecommendations) return

      rebuyRecommendations.dataset.productId = productInfo.dataset.productId
      rebuyRecommendations.dataset.hasProductFamily =
        productInfo.dataset.hasRecommendationProductFamily
      rebuyRecommendations.dataset.productFamilyProductIds =
        productInfo.dataset.recommendationProductFamilyIds || '[]'
      rebuyRecommendations.fetchRecommendations()
    }

    updateProductDescription(html) {
      const productDescription = html.querySelector('.js-product-description')
      if (productDescription && this.productDescription) {
        HTMLUpdateUtility.viewTransition(this.productDescription, productDescription, [])
      }
    }

    updateCompleteTheLook(html) {
      const completeTheLook = html.querySelector('.js-complete-the-look')
      if (completeTheLook && this.completeTheLook) {
        HTMLUpdateUtility.viewTransition(this.completeTheLook, completeTheLook, [])
      }
    }

    // The style-it-with collection comes from the product's pdp_app metaobject, so it
    // changes with the family/variant — swap the whole carousel from the section render.
    // Size/pack renders usually return the same cards; skipping an identical swap keeps
    // the carousel's scroll position (and its lazy-loaded images) intact.
    updateStyleItWith(html) {
      const styleItWith = html.querySelector('.js-pdp-style-it-with')
      if (!styleItWith || !this.styleItWith) return
      if (styleItWith.innerHTML === this.styleItWith.innerHTML) return
      HTMLUpdateUtility.viewTransition(this.styleItWith, styleItWith, [])
    }

    updateExpectedShipping(html) {
      const source = html.querySelector('.js-expected-shipping')
      if (!source || !this.expectedShipping) return
      // `html` may be a cached section response that later updates read again, so never
      // mutate it — carrying the badge over below would otherwise poison the cache.
      const expectedShipping = source.cloneNode(true)
      // The section render already carries the new free shipping state, but swapping it
      // in wholesale would make the badge jump. Swap the row with the badge as it looks
      // now, then animate toward the incoming state.
      const nextBadge = getShipsFreeBadge(expectedShipping)
      carryOverShipsFreeBadge(this.expectedShipping, expectedShipping)
      HTMLUpdateUtility.viewTransition(
        this.expectedShipping,
        expectedShipping,
        [],
        [revealAnimatedNode],
      )
      animateShipsFreeBadge(this, nextBadge)
    }

    updateFullProductInfo(html) {
      const fullProductInfo = html.querySelector('product-info')
      if (fullProductInfo) {
        HTMLUpdateUtility.viewTransition(this, fullProductInfo, [])
      }
    }

    buildRequestUrlWithParams(
      url,
      optionValues,
      shouldFetchFullPage = false,
      variantId,
      params = [],
    ) {
      const finalUrl = this.constructProductUrl(url)

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)
      if (this.dataset.optionsPoc === 'true') params.push('view=pdp-options-poc')

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`)
      }

      if (variantId) {
        params.push(`variant=${variantId}`)
      }

      if (this.dataset.isVdayLp === 'true') {
        params.push('is_vday_lp=true')
      }

      return `${finalUrl}?${params.join('&')}`
    }

    keepSize(productId) {
      let size = this.dataset.selectedSize
      if (!size) return

      let productFound = this.jsonData.packs_data_json.find((product) => product.id === productId)
      if (!productFound) {
        productFound = this.jsonData.singles_data_json.find((product) => product.id === productId)
      }
      if (!productFound) {
        productFound = this.jsonData.families_data_json.find((product) => product.id === productId)
      }
      if (!productFound) {
        productFound = this.jsonData.alternative_length_family_data_json.find(
          (product) => product.id === productId,
        )
      }
      if (!productFound) return

      const variantFound = productFound.variants.find((variant) => variant.title === size)
      if (!variantFound) return

      return variantFound.id
    }

    updateProductPriceFromInputData(target) {
      const priceWrapper = this.querySelector('.js-product-price')
      if (!priceWrapper) return

      // const isClearance = target.dataset.isClearance === 'true'
      // const isPromo = target.dataset.isPromo === 'true'
      // const isWeeklyDeal = target.dataset.isWeeklyDeal === 'true'
      // console.log(
      //   'isPromo',
      //   target.dataset.isPromo,
      //   'isWeeklyDeal',
      //   target.dataset.isWeeklyDeal,
      //   'isClearance',
      //   target.dataset.isClearance,
      // )
      const price = target.dataset.productPrice
      const compareAtPrice = target.dataset.compareAtPrice
      this.dataset.productDiscount = this.calculateDiscount(price, compareAtPrice)

      // Handled by section rendering so discounted_price can be used
      /*
      let priceHtml = ''
      // Only show compare price and discount if there's an actual price difference
      if (
        compareAtPrice &&
        this.extractPrice(compareAtPrice) > this.extractPrice(price) &&
        !isClearance
      ) {
        priceHtml = `
          <div class="liquid-pdp__price liquid-pdp__price--full">
            <span class="visually-hidden">Regular price!</span>
            <s>${this.addSpaceToPrice(compareAtPrice)}</s>
          </div>

          <div class="liquid-pdp__price">
            <span class="visually-hidden">Discounted price</span>
            <span>${this.addSpaceToPrice(price)}</span>
          </div>

          ${this.showFreeShippingBadge(this.extractPrice(price)) ? '<div class="liquid-pdp__price-badges"><div class="badge free-shipping"><span>Free Shipping</span></div></div>' : ''}

          ${!isClearance ? '<span class="single-quote"><strong>Get up to 3 FREE items below.</strong></span>' : ''}
        `
      } else {
        priceHtml = `
          <div class="liquid-pdp__price">
            <span class="visually-hidden">Price</span>
            <span>${this.addSpaceToPrice(isClearance ? compareAtPrice : price)}</span>
          </div>
          ${this.showFreeShippingBadge(this.extractPrice(price)) ? '<div class="liquid-pdp__price-badges"><div class="badge free-shipping"><span>Free Shipping</span></div></div>' : ''}
          ${!isClearance ? '<span class="single-quote"><strong>Get up to 3 FREE items below.</strong></span>' : ''}
        `
      }

      priceWrapper.innerHTML = priceHtml
      */

      const selectedPackSize = this.querySelector('product-pack-selector input:checked')
      if (selectedPackSize) {
        const packSize = Number(selectedPackSize.dataset.size)
        const newPrice = this.toFixedTruncate(this.extractPrice(price) / packSize)
        const decimalSeparator = this.detectDecimalSeparator(price)
        let formattedPrice = newPrice
        if (decimalSeparator === ',') {
          formattedPrice = newPrice.replace('.', ',') // For EU: "480.16" → "480,16"
        }

        // selectedPackSize.parentNode.querySelector('small').innerHTML =
        //   `${this.symbol}${this.symbol === 'MX$' ? ' ' : ''}${formattedPrice}/item`
      }
    }

    updateProductPriceFromProductData(productFound) {
      if (!productFound?.price_ui) return

      this.applyPriceUiData(productFound.price_ui, productFound.pack_size)
      // Flash deals, clearance, and excluded products all suppress the
      // manual-discount-code UI (mirrors the Liquid suppression) — drop it
      // optimistically so it doesn't linger next to the deal price + show a
      // duplicate divider while the section render is in flight.
      // updateDiscountCode re-inserts it when an eligible product lands.
      // For manual products without a box yet, a blank placeholder reserves
      // the row's height so the content below doesn't jump.
      // exclude_all_sales gates the box only (not the manual price placement),
      // mirroring the render condition in pdp-product-price.liquid.
      const allowsDiscountBox =
        this.isManualDiscountPriceUi(productFound.price_ui) &&
        productFound.price_ui.exclude_all_sales !== true
      if (!allowsDiscountBox) {
        this.discountCode?.remove()
        this.removeDiscountCodePlaceholder()
      } else if (!this.discountCode) {
        // Prefer the real box (needed when no section render follows, e.g.
        // bundle mode); fall back to the blank height-reserving placeholder.
        if (!this.insertDiscountCodeFromTemplate()) this.insertDiscountCodePlaceholder()
      }
      if (this.productPriceBlock) {
        this.productPriceBlock.classList.toggle(
          'pdp-product-price-wrapper--manual-discount-active',
          this.isManualDiscountPriceUi(productFound.price_ui),
        )
        this.productPriceBlock.classList.toggle(
          'pdp-product-price-wrapper--deal-row-active',
          this.isDealRowPriceUi(productFound.price_ui),
        )
        this.productPriceBlock.innerHTML = this.buildProductPriceHtml(productFound.price_ui)
        this.updateSelectedClearanceFinalPrice(this.productPriceBlock)
      }
      this.updatePackPerItemPriceFromPriceUi(productFound.price_ui, productFound.pack_size)
      this.updateInsiderPriceFromPriceUi(productFound.price_ui)
      this.updatePendingManualDiscountCodePrice(productFound.price_ui)
      this.syncAutoAppliedDiscountPercentage(productFound.price_ui)
      this.syncManualDiscountPriceUi()
    }

    // Unapplied manual-code mode: recompute the discount box price for the
    // new product so it doesn't show the previous product's price while the
    // section render is in flight (syncManualDiscountPriceUi only covers the
    // applied state). Mirrors the floor math in pdp-product-price.liquid.
    updatePendingManualDiscountCodePrice(priceUi) {
      const manualDiscountCodeEl = this.discountCode
      if (!manualDiscountCodeEl || manualDiscountCodeEl.dataset.applied === 'true') return

      const discountPercentage = Number(manualDiscountCodeEl.dataset.discountPercentage || 0)
      const basePriceCents = Math.round(Number(priceUi?.product_price || 0) * 100)
      if (discountPercentage <= 0 || basePriceCents <= 0) return

      const discountedCents = this.getDiscountedPriceCents(basePriceCents, discountPercentage)
      this.updateManualDiscountCodePrices(this.formatProductMoneyWithoutTrailingZeros(discountedCents))
    }

    deferSectionRender(callback) {
      window.setTimeout(callback, 0)
    }

    applyPriceUiData(priceUi, packSizeOverride = null) {
      if (!priceUi) return

      this.dataset.productPrice = priceUi.product_price
      this.dataset.productCompareAtPrice = priceUi.compare_at_price
      this.dataset.productPriceFormatted = priceUi.product_price_formatted
      this.dataset.productCompareAtPriceFormatted = priceUi.compare_at_price_formatted
      this.dataset.productDiscount = priceUi.discount_percentage
      this.dataset.productClearance = priceUi.is_clearance
      // Tag-mode pricing keys off these, and a colour change can land on a
      // differently tagged product, so they have to move with the price data.
      if (priceUi.select_tagged !== undefined)
        this.dataset.clearanceSelectTagged = String(priceUi.select_tagged)
      if (priceUi.base_tagged !== undefined)
        this.dataset.clearanceBaseTagged = String(priceUi.base_tagged)
      this.dataset.productFinalSale = priceUi.is_final_sale
      this.dataset.isWeeklyDeal = priceUi.is_weekly_deal
      this.dataset.pdpDiscountExcluded = priceUi.pdp_discount_excluded ? 'true' : 'false'
      this.syncPdpQuantitySelector(priceUi, packSizeOverride)
    }

    syncPdpQuantitySelector(priceUi = null, packSizeOverride = null) {
      const selector = this.pdpQuantitySelector
      if (!selector) return

      const isClearance = this.dataset.productClearance === 'true'
      const packSize = packSizeOverride || this.dataset.productPackSize
      const isSingle = !packSize || packSize === 'Single'
      // Tag mode does not render the add-more-save-more control at all
      // (snippets/pdp-quantity-selector.liquid), so never re-activate it here.
      const isClearanceSingle = isClearance && isSingle && !isClearanceTagModeSafe()
      selector.classList.toggle('pdp-quantity-selector--clearance', isClearanceSingle)
      selector.dataset.clearanceActive = isClearanceSingle ? 'true' : 'false'
      if (!isSingle) {
        selector.style.setProperty('display', 'none')
      } else {
        selector.style.removeProperty('display')
      }

      const standardSelect = selector.querySelector('[data-standard-quantity-select]')
      if (standardSelect) {
        standardSelect.disabled = isClearanceSingle
        if (isClearanceSingle) {
          standardSelect.removeAttribute('name')
        } else {
          standardSelect.setAttribute('name', 'quantity')
        }
      }

      const clearanceQuantity = selector.querySelector('[data-clearance-quantity-selector]')
      if (clearanceQuantity) {
        clearanceQuantity.hidden = !isClearanceSingle
      }

      const clearanceInputs = selector.querySelectorAll('[data-clearance-quantity-input]')
      clearanceInputs.forEach((input) => {
        input.disabled = !isClearanceSingle
        if (isClearanceSingle) {
          input.setAttribute('name', 'quantity')
        } else {
          input.removeAttribute('name')
        }
      })

      if (
        isClearanceSingle &&
        !selector.querySelector('[data-clearance-quantity-input]:checked')
      ) {
        const defaultInput = selector.querySelector('[data-clearance-quantity-input][value="1"]')
        if (defaultInput) defaultInput.checked = true
      }

      this.updateClearanceQuantityPrices(priceUi, isClearanceSingle)
    }

    updateClearanceQuantityPrices(priceUi = null, shouldUpdateFinalPrice = true) {
      const selector = this.pdpQuantitySelector
      const clearanceQuantity = selector?.querySelector('[data-clearance-quantity-selector]')
      if (!clearanceQuantity) return

      const productPrice = Number(priceUi?.product_price || this.dataset.productPrice || 0)
      const compareAtPrice = Number(
        priceUi?.compare_at_price || this.dataset.productCompareAtPrice || 0,
      )
      const productPriceCents = Math.round(productPrice * 100)
      const compareAtPriceCents = Math.round(compareAtPrice * 100)
      let basePriceCents =
        Number.isFinite(compareAtPriceCents) && compareAtPriceCents > productPriceCents
          ? compareAtPriceCents
          : productPriceCents

      if (!Number.isFinite(basePriceCents) || basePriceCents <= 0) return

      const quantityLabel = clearanceQuantity.dataset.quantityLabel || ''
      const priceTemplate = clearanceQuantity.dataset.priceTemplate || '__PRICE__'

      selector.querySelectorAll('[data-clearance-quantity-input]').forEach((input) => {
        const discountPercentage = Number(input.dataset.discountPercentage || 0)
        const discountAmountCents =
          discountPercentage > 0 ? Math.floor(basePriceCents * (discountPercentage / 100)) : 0
        const itemPriceCents = basePriceCents - discountAmountCents
        const formattedPrice = this.formatProductMoneyWithoutTrailingZeros(itemPriceCents)
        const priceText = priceTemplate.replace('__PRICE__', formattedPrice)
        const option = input.nextElementSibling
        const priceNode = option?.querySelector('[data-clearance-quantity-price]')
        const saveLabel = input.dataset.saveLabel || ''

        input.dataset.clearanceItemPrice = String(itemPriceCents)
        input.dataset.clearanceFormattedPrice = formattedPrice
        input.dataset.clearanceFinalPriceValue = formattedPrice
        input.dataset.clearanceFinalPriceSuffix =
          input.value === '1' ? '' : priceText.replace(formattedPrice, '')
        if (priceNode) priceNode.textContent = priceText
        input.setAttribute(
          'aria-label',
          `${quantityLabel} ${input.value}. ${priceText}${saveLabel ? `. ${saveLabel}` : ''}`,
        )
      })
      if (shouldUpdateFinalPrice) this.updateSelectedClearanceFinalPrice()
    }

    updateSelectedClearanceFinalPrice(priceBlock = this.productPriceBlock) {
      if (!this.isClearancePricedUi()) return

      const finalPrice = priceBlock?.querySelector('[data-clearance-final-price]')
      if (!finalPrice) return

      const selectedInput = this.pdpQuantitySelector?.querySelector(
        '[data-clearance-quantity-input]:checked:not(:disabled)',
      )

      // A variant change rebuilds this row from priceUi.product_price_formatted,
      // which carries no clearance discount. In quantity mode the checked option
      // holds the corrected price; tag mode renders no options, so resolve the
      // product's fixed rate here instead — otherwise the row would keep showing
      // the undiscounted price until some later event happened to repaint it.
      let formattedPrice = selectedInput?.dataset.clearanceFinalPriceValue
      let formattedSuffix = selectedInput?.dataset.clearanceFinalPriceSuffix || ''

      if (!formattedPrice && isClearanceTagModeSafe()) {
        // The discount comes off the product's own price, not its compare-at.
        const baseCents = Math.round(Number(this.dataset.productPrice || 0) * 100)
        const percentage = this.getClearanceDiscountPercentage()
        if (baseCents > 0 && percentage > 0) {
          formattedPrice = this.formatProductMoneyWithoutTrailingZeros(
            baseCents - Math.floor((baseCents * percentage) / 100),
          )
          formattedSuffix = ''
        }
      }

      if (!formattedPrice) return

      const finalPriceValue = finalPrice.querySelector('[data-clearance-final-price-value]')
      const finalPriceSuffix = finalPrice.querySelector('[data-clearance-final-price-suffix]')

      if (finalPriceValue) {
        finalPriceValue.textContent = formattedPrice
      } else {
        finalPrice.textContent = formattedPrice
      }

      if (finalPriceSuffix) {
        finalPriceSuffix.textContent = formattedSuffix
      }
    }

    // The clearance rate this product earns. In tag mode it comes from the
    // product's own discount tags (base / select) rather than the Clearance
    // tag, so this is 0 for a Clearance product carrying neither — and above 0
    // for a non-Clearance product carrying one. Mirrors the Liquid resolver in
    // snippets/clearance-tier-discount.liquid.
    getClearanceDiscountPercentage(priceUi = null, count = 0) {
      const readFlag = function (fromPriceUi, fromDataset) {
        if (fromPriceUi !== undefined && fromPriceUi !== null)
          return fromPriceUi === true || fromPriceUi === 'true'
        return fromDataset === 'true'
      }
      return resolveClearanceDiscount({
        hasSelectTag: readFlag(priceUi?.select_tagged, this.dataset.clearanceSelectTagged),
        hasBaseTag: readFlag(priceUi?.base_tagged, this.dataset.clearanceBaseTagged),
        isClearance: readFlag(priceUi?.is_clearance, this.dataset.productClearance),
        count: count,
      })
    }

    // Mirrors Liquid's is_clearance_priced: the product is shown at a clearance
    // price, which is what every clearance price placement below gates on.
    isClearancePricedUi(priceUi = null) {
      if (priceUi?.is_clearance_priced !== undefined && priceUi.is_clearance_priced !== null)
        return priceUi.is_clearance_priced === true || priceUi.is_clearance_priced === 'true'
      return this.getClearanceDiscountPercentage(priceUi) > 0
    }

    // Mirrors Liquid's show_manual_discount_price: page has a manual (non-auto)
    // promo code and the product isn't clearance/flash/excluded.
    isManualDiscountPriceUi(priceUi) {
      const isWeeklyDeal = priceUi.is_weekly_deal === true || priceUi.is_weekly_deal === 'true'
      const isClearance = priceUi.is_clearance === true || priceUi.is_clearance === 'true'
      if (isWeeklyDeal || isClearance || this.isClearancePricedUi(priceUi)) return false
      if (priceUi.pdp_discount_excluded) return false
      return this.getManualDiscountPercentage(priceUi) > 0
    }

    isAutoAppliedPromo() {
      return this.dataset.manualDiscountAutoApplied === 'true'
    }

    // Auto-apply promos can be tag-tiered, so the rate follows the product's price data;
    // manual-code promos use the page-level rate.
    getManualDiscountPercentage(priceUi) {
      if (this.isAutoAppliedPromo())
        return priceUi?.has_promo_discount ? Number(priceUi.promo_discount_percentage || 0) : 0
      return Number(this.dataset.manualDiscountPercentage || 0)
    }

    // Keeps an auto-applied box on the incoming product's rate until the section render lands
    syncAutoAppliedDiscountPercentage(priceUi) {
      const discountCode = this.discountCode
      if (!discountCode || !this.isAutoAppliedPromo()) return

      const percentage = this.getManualDiscountPercentage(priceUi)
      if (percentage <= 0) return

      discountCode.dataset.discountPercentage = String(percentage)
      discountCode.discountPercentage = percentage
    }

    isSingleProductPackSize(packSize = this.dataset.productPackSize) {
      const resolvedPackSize = packSize == null || packSize === '' ? 'Single' : String(packSize)
      return resolvedPackSize === 'Single'
    }

    // Mirrors Liquid's deal_row_active: automatic promo, or any clearance-priced
    // product (at any pack size, with or without compare-at — the rate comes
    // from the product's own discount tags). Flash deals render on one row.
    isDealRowPriceUi(priceUi) {
      if (this.isManualDiscountPriceUi(priceUi)) return false
      const isWeeklyDeal = priceUi.is_weekly_deal === true || priceUi.is_weekly_deal === 'true'
      if (isWeeklyDeal) return false
      if (priceUi.has_promo_discount === true) return true
      return this.isClearancePricedUi(priceUi)
    }

    buildProductPriceHtml(priceUi) {
      if (!priceUi) return ''

      // Manual-code mode mirrors Liquid's show_manual_discount_price branch:
      // only the base price renders here — the discounted price + promo name
      // live in the discount code box, so the automatic-discount placement
      // (discounted price + holiday name) must not flash in. Clearance and
      // flash deals are excluded by isManualDiscountPriceUi and keep their
      // own placements below.
      if (this.isManualDiscountPriceUi(priceUi)) {
        const parts = []
        if (priceUi.has_compare_at_row) {
          parts.push(
            `<span class="visually-hidden">${getPdpTranslation('regular_price', 'Regular price')}</span>`,
          )
          parts.push(`<s>${priceUi.compare_at_price_formatted || ''}</s>`)
        }
        parts.push(
          `<span class="visually-hidden">${getPdpTranslation('price_label', 'Price')}</span>`,
        )
        parts.push(`<span>${priceUi.product_price_formatted || ''}</span>`)
        return `<div class="pdp-product-price-inner">${parts.join('')}</div>`
      }

      const firstRowParts = []
      const secondRowParts = []
      const isWeeklyDeal = priceUi.is_weekly_deal === true || priceUi.is_weekly_deal === 'true'
      const isClearanceRow = !isWeeklyDeal && this.isClearancePricedUi(priceUi)

      if (isWeeklyDeal) {
        // Flash deal: one row — struck compare price, final flash price, ⚡.
        // Mirrors the is_weekly_deal branch in pdp-product-price.liquid.
        const comparePrice = priceUi.has_compare_at_row
          ? priceUi.compare_at_price_formatted || ''
          : priceUi.product_price_formatted || ''
        firstRowParts.push(
          `<span class="visually-hidden">${getPdpTranslation('regular_price', 'Regular price')}</span>`,
          `<s>${comparePrice}</s>`,
          `<span class="visually-hidden">${getPdpTranslation('discounted_price', 'Discounted price')}</span>`,
          `<span class="pdp-product-price-final">${priceUi.discounted_price_formatted || ''}</span>`,
          '<span class="pdp-product-price-flash-icon" aria-hidden="true">⚡</span>',
          `<span class="visually-hidden">${getPdpTranslation('flash_deal', '⚡ Flash Deal')}</span>`,
        )
      } else if (this.isDealRowPriceUi(priceUi)) {
        // Deal placements (promo / clearance): row 1 holds the
        // pre-deal pricing, the deal price + label render in the bordered
        // second row — mirrors the deal_row_active markup in
        // pdp-product-price.liquid.
        firstRowParts.push(
          `<span class="visually-hidden">${getPdpTranslation('regular_price', 'Regular price')}</span>`,
        )
        const regularPrice =
          isClearanceRow && priceUi.has_compare_at_row !== true
            ? priceUi.product_price_formatted || ''
            : priceUi.compare_at_price_formatted || ''
        firstRowParts.push(`<s>${regularPrice}</s>`)
        if (priceUi.has_compare_at_row && !isClearanceRow) {
          firstRowParts.push(
            `<span class="visually-hidden">${getPdpTranslation('discounted_price', 'Discounted price')}</span>`,
          )
          firstRowParts.push(`<span>${priceUi.product_price_formatted || ''}</span>`)
        }

        secondRowParts.push(
          `<span class="visually-hidden">${getPdpTranslation('discounted_price', 'Discounted price')}</span>`,
        )
        if (isClearanceRow) {
          // The clearance price straight from the price data, so the row never
          // paints the undiscounted price first and gets patched afterwards by
          // updateSelectedClearanceFinalPrice (which still corrects it for the
          // selected quantity option in quantity mode).
          const clearancePrice =
            priceUi.clearance_discounted_price_formatted || priceUi.product_price_formatted || ''
          secondRowParts.push(
            `<span class="pdp-product-price-final" data-clearance-final-price><span data-clearance-final-price-value>${clearancePrice}</span><span class="pdp-product-price-final__suffix" data-clearance-final-price-suffix></span></span>`,
          )
          secondRowParts.push(
            `<span class="pdp-product-price-promo-name">${getPdpTranslation('we_made_too_much', '🔥 We Made Too Much')}</span>`,
          )
        } else {
          secondRowParts.push(
            `<span class="pdp-product-price-final">${priceUi.discounted_price_formatted || ''}</span>`,
          )
          const promoLabel = this.buildPromoLabelHtml(priceUi)
          if (promoLabel) secondRowParts.push(promoLabel)
        }
      } else if (priceUi.has_compare_at_row) {
        firstRowParts.push(
          `<span class="visually-hidden">${getPdpTranslation('regular_price', 'Regular price')}</span>`,
        )
        firstRowParts.push(`<s>${priceUi.compare_at_price_formatted || ''}</s>`)
        firstRowParts.push(
          `<span class="visually-hidden">${getPdpTranslation('discounted_price', 'Discounted price')}</span>`,
        )
        const basePriceClass =
          Number(priceUi.promo_discount_percentage || 0) === 0
            ? ' class="pdp-product-price-final"'
            : ''
        firstRowParts.push(`<span${basePriceClass}>${priceUi.product_price_formatted || ''}</span>`)
      } else {
        firstRowParts.push(
          `<span class="visually-hidden">${getPdpTranslation('price_label', 'Price')}</span>`,
        )
        firstRowParts.push(`<span>${priceUi.product_price_formatted || ''}</span>`)
      }

      let html = `<div class="pdp-product-price-inner">${firstRowParts.join('')}</div>`
      if (secondRowParts.length) {
        html += `<div class="pdp-product-price-inner">${secondRowParts.join('')}</div>`
      }

      return html
    }

    updatePackPerItemPriceFromPriceUi(priceUi, packSizeOverride = null) {
      if (!priceUi) return

      // Manual-code mode (code not applied yet): the promo percentage is baked
      // into discounted_price, but the visible price is the base price, so PPI
      // must use it too. The applied case is recomputed by
      // applyManualDiscountPriceUi afterwards. Fall back to the page-level
      // signal when the discount box isn't in the DOM (e.g. coming from a
      // flash deal product, where it's suppressed).
      const manualDiscountCodeEl = this.discountCode
      const isManualDiscountPending = manualDiscountCodeEl
        ? manualDiscountCodeEl.dataset.applied !== 'true'
        : this.isManualDiscountPriceUi(priceUi) &&
          this.dataset.couponAlreadyApplied !== 'true' &&
          this.dataset.manualDiscountAutoApplied !== 'true'

      // A clearance-priced pack's per-item price comes off the clearance price
      // shown in the deal row, mirroring pack_per_item_base_price in
      // snippets/pdp-product-price.liquid.
      let price
      if (this.isClearancePricedUi(priceUi) && priceUi.clearance_discounted_price != null) {
        price = priceUi.clearance_discounted_price
      } else if (
        !isManualDiscountPending &&
        (priceUi.is_weekly_deal || priceUi.has_promo_discount)
      ) {
        price = priceUi.discounted_price
      } else {
        price = priceUi.product_price
      }
      const priceCents = Math.round(Number(price || 0) * 100)
      this.updatePackPerItemPrice(priceCents, packSizeOverride)
    }

    buildPromoLabelHtml(priceUi) {
      if (!priceUi) return ''

      if (priceUi.is_weekly_deal) {
        const weeklyHtml = priceUi.weekly_deal_ends_in_html?.trim()
        return `<span class="pdp-product-price-promo-name">${weeklyHtml || getPdpTranslation('flash_deal', '⚡ Flash Deal')}</span>`
      }

      if (priceUi.is_clearance || !priceUi.has_promo_discount || !priceUi.active_promo_holiday_name)
        return ''

      const promoEndsIn = priceUi.promo_ends_in_html ? ` ${priceUi.promo_ends_in_html}` : ''
      return `<span class="pdp-product-price-promo-name">${priceUi.active_promo_holiday_name}${promoEndsIn}</span>`
    }

    syncManualDiscountPriceUi() {
      if (this.dataset.pdpDiscountExcluded === 'true') return

      const discountCode = this.discountCode
      if (!discountCode) return
      if (discountCode.dataset.applied !== 'true') return

      const discountPercentage = Number(discountCode.dataset.discountPercentage || 0)
      if (discountPercentage <= 0) return

      this.applyManualDiscountPriceUi(discountPercentage)
    }

    applyManualDiscountPriceUi(discountPercentage) {
      const appliedDiscountPercentage = Number(discountPercentage || 0)
      const productPrice = Number(this.dataset.productPrice || 0)
      if (!this.productPriceBlock || appliedDiscountPercentage <= 0 || productPrice <= 0) return
      if (this.dataset.pdpDiscountExcluded === 'true') return
      if (this.dataset.productClearance === 'true') return
      // Flash deals take precedence over the manual-discount-code system.
      if (this.dataset.isWeeklyDeal === 'true') return

      this.productPriceBlock.classList.add('pdp-product-price-wrapper--manual-discount-active')

      const productPriceCents = Math.round(productPrice * 100)
      const manualDiscountedPriceCents = this.getDiscountedPriceCents(
        productPriceCents,
        appliedDiscountPercentage,
      )
      const compareAtPrice = Number(this.dataset.productCompareAtPrice || 0)
      const compareAtPriceFormatted = this.dataset.productCompareAtPriceFormatted || ''
      const productPriceFormatted =
        this.dataset.productPriceFormatted ||
        this.formatProductMoneyWithoutTrailingZeros(productPriceCents)
      const manualDiscountedPriceFormatted =
        this.formatProductMoneyWithoutTrailingZeros(manualDiscountedPriceCents)
      // Code applied: strike the pre-code price (compare-at when present) and
      // show the red manual-discounted total. Mirrors pdp-product-price.liquid.
      const strikethroughPriceFormatted =
        compareAtPrice > productPrice ? compareAtPriceFormatted : productPriceFormatted
      const appliedPriceHtml = `
        <div class="pdp-product-price-inner">
          <span class="visually-hidden">${getPdpTranslation('regular_price', 'Regular price')}</span>
          <s>${strikethroughPriceFormatted}</s>
          <span class="visually-hidden">${getPdpTranslation('discounted_price', 'Discounted price')}</span>
          <span class="pdp-product-price-manual-final">${manualDiscountedPriceFormatted}</span>
        </div>
      `

      this.productPriceBlock.innerHTML = appliedPriceHtml
      this.updateManualDiscountCodePrices(manualDiscountedPriceFormatted)
      const referencePriceCents =
        compareAtPrice > productPrice ? Math.round(compareAtPrice * 100) : productPriceCents
      this.updateManualDiscountCodeAppliedLabel(
        this.getCombinedDiscountPercentage(referencePriceCents, manualDiscountedPriceCents),
        this.formatProductMoneyWithoutTrailingZeros(referencePriceCents - manualDiscountedPriceCents),
      )

      this.updateManualDiscountDependentPriceUi(appliedDiscountPercentage)
    }

    // Full discount from the reference price (compare-at when present) down to the promo price
    getCombinedDiscountPercentage(referencePriceCents, discountedPriceCents) {
      if (referencePriceCents <= 0) return 0
      const savedPercentage = ((referencePriceCents - discountedPriceCents) * 100) / referencePriceCents
      // A .99-rounded promo price rounds the label down, so it never claims more than it saves
      return this.isPromoRoundedTo99Cents() ? Math.floor(savedPercentage) : Math.round(savedPercentage)
    }

    updateManualDiscountCodeAppliedLabel(percentage, savingsFormatted = '') {
      this.discountCode?.setAppliedLabelPercentage?.(percentage, savingsFormatted)
    }

    // Counterpart of applyManualDiscountPriceUi for a code the cart turned out not to carry:
    // back to the not-applied rendering (base price, per-item price without the code).
    // manual-discount-code flips data-applied to 'false' before calling this.
    resetManualDiscountPriceUi() {
      const productFound = this.getProductById(Number(this.dataset.productId))
      if (productFound?.price_ui) this.updateProductPriceFromProductData(productFound)
    }

    updateManualDiscountCodePrices(formattedPrice) {
      this.discountCode?.querySelectorAll('.js-discount-code-price').forEach((priceEl) => {
        priceEl.textContent = formattedPrice
      })
    }

    updateManualDiscountDependentPriceUi(discountPercentage) {
      const appliedDiscountPercentage = Number(discountPercentage || 0)
      if (this.dataset.pdpDiscountExcluded === 'true') return
      if (appliedDiscountPercentage <= 0 || this.dataset.productClearance === 'true') return
      // Flash deals take precedence over the manual-discount-code system.
      if (this.dataset.isWeeklyDeal === 'true') return

      const productPriceCents = Math.round(Number(this.dataset.productPrice || 0) * 100)
      if (productPriceCents > 0) {
        const manualDiscountedPriceCents = this.getDiscountedPriceCents(
          productPriceCents,
          appliedDiscountPercentage,
        )
        this.updatePackPerItemPrice(manualDiscountedPriceCents)
        this.updateInsiderPrice(manualDiscountedPriceCents)
      }

      this.updatePackSavingAmounts(appliedDiscountPercentage)
      // Auto-applied promos are already baked into the bar's server-rendered base price
      this.refreshUpgradeBarPpi(this.isAutoAppliedPromo() ? 0 : appliedDiscountPercentage)
    }

    // Manual discount codes apply client-side, so the bar's PPI (rendered by
    // upgrade-bar.liquid with promo + weekly-deal already baked in) needs a
    // top-up. Mirrors the floor math used by Liquid `divided_by` so the bar
    // PPI matches what the snippet would produce on a hard refresh.
    refreshUpgradeBarPpi(discountPercentage) {
      const bar = this.querySelector('[data-upgrade-bar]')
      if (!bar) return
      const ppiEl = bar.querySelector('[data-upgrade-bar-ppi]')
      if (!ppiEl) return

      const basePriceCents = Number(bar.dataset.upgradeBasePriceCents || 0)
      const packSize = Number(bar.dataset.upgradePackSize || 0)
      if (!Number.isFinite(basePriceCents) || basePriceCents <= 0) return
      if (!Number.isFinite(packSize) || packSize <= 0) return

      const appliedDiscountPercentage = Number(discountPercentage || 0)
      const discountedCents = appliedDiscountPercentage > 0
        ? this.getDiscountedPriceCents(basePriceCents, appliedDiscountPercentage)
        : basePriceCents
      // Floor (not round) to match the Liquid `divided_by` integer division in
      // upgrade-bar.liquid / pdp-product-price.liquid; otherwise a manual
      // discount refresh drifts the bar up by 1 cent (e.g. $14.99 -> $15.00).
      const perItemCents = Math.floor(discountedCents / packSize)
      const perItemLabel = bar.dataset.perItemLabel || '/item'
      ppiEl.textContent = `Just ${this.formatProductMoneyWithoutTrailingZeros(perItemCents)}${perItemLabel}`
    }

    getPackSizeNumber(packSizeValue) {
      if (packSizeValue === 'Single') return 1

      const normalizedPackSize = `${packSizeValue || ''}`.replace('-Pack', '')
      const packSizeNumber = Number(normalizedPackSize)

      return Number.isFinite(packSizeNumber) ? packSizeNumber : 0
    }

    updatePackPerItemPrice(discountedPriceCents, packSizeOverride = null) {
      const selectedPackSize = this.packSelectors?.querySelector('input:checked')
      const packSizeValue =
        packSizeOverride != null && packSizeOverride !== ''
          ? packSizeOverride
          : selectedPackSize?.dataset.size ?? this.dataset.productPackSize
      const packSize = this.getPackSizeNumber(packSizeValue)
      const visiblePerItemPrices = this.querySelectorAll('.pdp-price-and-discount__per-item-price')
      const priceCents = Number(discountedPriceCents || 0)

      if (packSize <= 1 || priceCents <= 0) {
        visiblePerItemPrices.forEach((perItemPrice) => {
          const perItemPriceValue = perItemPrice.querySelector('[data-per-item-price-value]')
          if (perItemPriceValue) perItemPriceValue.textContent = ''
          else perItemPrice.textContent = ''
          perItemPrice.hidden = true
        })
        return
      }

      // Mirror pdp-product-price.liquid's `{price}{per_item}` output (no space,
      // e.g. "$14.99/item") so client-only updates (manual discount, JSON
      // product swap) don't revert the label back to "<price> each".
      const perItemLabel =
        visiblePerItemPrices[0]?.dataset.perItemLabel ||
        this.packSelectors?.dataset.perItemLabel ||
        '/item'
      // Floor (not round) to match the server's Liquid `divided_by:
      // pack_size_label` integer division in pdp-product-price.liquid. Custom
      // packs whose total doesn't divide evenly would otherwise round up
      // (e.g. 1499.x -> $15.00 instead of $14.99).
      const perItemPriceCents = Math.floor(priceCents / packSize)
      const perItemPriceText = this.formatProductMoneyWithoutTrailingZeros(perItemPriceCents)

      visiblePerItemPrices.forEach((perItemPrice) => {
        const perItemPriceValue = perItemPrice.querySelector('[data-per-item-price-value]')
        const perItemLabelElement = perItemPrice.querySelector(
          '.pdp-price-and-discount__per-item-label',
        )

        if (perItemPriceValue && perItemLabelElement) {
          perItemPriceValue.textContent = perItemPriceText
          perItemLabelElement.textContent = perItemLabel
        } else {
          perItemPrice.textContent = `${perItemPriceText}${perItemLabel}`
        }
        // Price breakdown tooltip reads the final pack price + pack size from here
        perItemPrice.dataset.finalPriceCents = String(Math.round(priceCents))
        perItemPrice.dataset.packSize = String(packSize)
        perItemPrice.hidden = false
      })

      window.refreshPriceBreakdownTooltips?.()
    }

    updatePackSavingAmounts(discountPercentage) {
      const packSelector = this.packSelectors
      if (!packSelector) return

      packSelector
        .querySelectorAll('.liquid-pdp__pack-selector-buttons input[data-product-price-cents]')
        .forEach((input) => {
          if (input.classList.contains('js-is-clearance')) return

          const label = input.closest('label')
          if (!label) return

          const productPriceCents = Number(input.dataset.productPriceCents || 0)
          const compareAtPriceCents = Number(input.dataset.productCompareAtPriceCents || 0)
          if (productPriceCents <= 0 || compareAtPriceCents <= 0) return

          const discountedPriceCents = this.getDiscountedPriceCents(
            productPriceCents,
            discountPercentage,
          )
          const savingCents = Math.max(compareAtPriceCents - discountedPriceCents, 0)
          if (savingCents <= 0) return

          let savingAmount = label.querySelector(
            '.liquid-pdp__pack-selector-buttons__saving-amount',
          )
          if (!savingAmount) {
            if (!['USD', 'GBP', 'AUD', 'EUR', 'CAD'].includes(this.dataset.currencyCode)) return

            savingAmount = document.createElement('small')
            savingAmount.className = 'liquid-pdp__pack-selector-buttons__saving-amount'
            const loadingSpinner = label.querySelector('.loading__spinner')
            if (loadingSpinner) {
              loadingSpinner.before(savingAmount)
            } else {
              label.append(savingAmount)
            }
          }

          const roundedSavingCents = Math.floor(savingCents / 100) * 100
          const saveLabel = packSelector.dataset.saveLabel || getPdpTranslation('save', 'Save')
          savingAmount.textContent = `${saveLabel} ${this.formatProductMoneyWithoutTrailingZeros(roundedSavingCents)}`
        })
    }

    // Unlike the per-item price, the insider prices always sit on the promo-discounted
    // price (applied or not), mirroring discounted_price in the server render.
    updateInsiderPriceFromPriceUi(priceUi) {
      if (!priceUi) return
      const price =
        this.isClearancePricedUi(priceUi) && priceUi.clearance_discounted_price != null
          ? priceUi.clearance_discounted_price
          : priceUi.discounted_price
      this.updateInsiderPrice(Math.round(Number(price || 0) * 100))
    }

    updateInsiderPrice(discountedPriceCents) {
      const insiderWrapper = this.querySelector('.js-insider-club-upsell')
      const insiderPrice = insiderWrapper?.querySelector('.js-insiders-price')
      if (!insiderWrapper || !insiderPrice || discountedPriceCents <= 0) return

      const membershipDiscountMultiplier = Number(
        insiderWrapper.dataset.membershipDiscountMultiplier || 0,
      )
      if (membershipDiscountMultiplier <= 0) return

      const compareAtPrice = insiderPrice.querySelector(
        '.liquid-pdp__insider-prices__compare-at-price',
      )
      const discountPrice = insiderPrice.querySelector('.liquid-pdp__insider-discount-price')
      const membershipPriceCents = Math.round(discountedPriceCents * membershipDiscountMultiplier)

      if (compareAtPrice) {
        compareAtPrice.textContent =
          this.formatProductMoneyWithoutTrailingZeros(discountedPriceCents)
      }

      if (discountPrice) {
        discountPrice.textContent =
          this.formatProductMoneyWithoutTrailingZeros(membershipPriceCents)
      }
    }

    // Promo price math, mirroring snippets/promo-discounted-price.liquid
    getDiscountedPriceCents(priceCents, discountPercentage) {
      const discountAmountCents = Math.floor(priceCents * (Number(discountPercentage || 0) / 100))
      const discountedCents = priceCents - discountAmountCents
      if (discountAmountCents <= 0 || !this.isPromoRoundedTo99Cents()) return discountedCents
      // Whole-dollar base prices stay; a result landing on .00 (£99.99 -> £76.00) still rounds to .99
      if (priceCents % 100 === 0) return discountedCents
      return this.roundUpTo99Cents(discountedCents, priceCents)
    }

    isPromoRoundedTo99Cents() {
      return this.dataset.promoRoundToNinetyNineCents === 'true'
    }

    // $23.47 -> $23.99, $76.00 -> $76.99; never above the undiscounted price
    roundUpTo99Cents(cents, maxCents) {
      const centsPart = cents % 100
      return Math.min(cents - centsPart + 99, maxCents)
    }

    calculateDiscount(price, compareAtPrice) {
      const priceValue = this.extractPrice(price)
      const compareAtPriceValue = this.extractPrice(compareAtPrice)
      return Math.round(((compareAtPriceValue - priceValue) / compareAtPriceValue) * 100)
    }

    extractPrice(priceString) {
      // Step 1: Standardize European format (1.234,56 → 1234.56)
      let standardized = priceString.replace(/[^\d,.-]/g, '') // Remove non-numeric except commas/dots

      // Step 2: Detect if comma is used as decimal separator (EU style)
      const lastComma = standardized.lastIndexOf(',')
      const lastDot = standardized.lastIndexOf('.')

      if (lastComma > lastDot) {
        // EU format (e.g., "1.234,56" or "89,99")
        standardized = standardized.replace(/\./g, '') // Remove thousand separators
        standardized = standardized.replace(',', '.') // Convert decimal comma to dot
      } else if (lastDot > lastComma) {
        // USD/Standard format (e.g., "1,234.56" or "89.99")
        standardized = standardized.replace(/,/g, '') // Remove thousand separators
      }
      // If no commas/dots, leave as-is (e.g., "8999" → 8999)

      return parseFloat(standardized)
    }

    addSpaceToPrice(priceString) {
      // Return early if no price string
      if (!priceString || typeof priceString !== 'string') return priceString

      // Match multi-character currency codes followed by symbol (CA$, MX$, US$, etc.)
      const multiCharMatch = priceString.match(/^([A-Z]{2,3})(\$)/i)

      if (multiCharMatch) {
        const countryCode = multiCharMatch[1]
        const symbol = multiCharMatch[2]
        const remainingString = priceString.slice(countryCode.length + symbol.length)

        // If there's already a space before the symbol, return as-is
        if (priceString.includes(`${countryCode} ${symbol}`)) return priceString

        // Add space before the symbol
        return `${countryCode} ${symbol}${remainingString}`
      }

      // For single character currencies ($, €, £, etc.), return unchanged
      return priceString
    }

    // Recovers the market's currency format (symbol, separators, precision) from
    // data-money-probe: a price rendered by Liquid's `money` filter. Intl guesses separators
    // from language + country, so e.g. English in Germany would print "." instead of ",".
    // Same parser as moneyParts() in cart-drawer-clearance-addon.js. Memoized.
    getMoneyParts() {
      if (this.parsedMoneyParts !== undefined) return this.parsedMoneyParts
      this.parsedMoneyParts = this.parseMoneyProbe(
        this.dataset.moneyProbe || '',
        Number(this.dataset.moneyProbeCents),
      )
      return this.parsedMoneyParts
    }

    // Returns null when the probe is missing or unparseable
    parseMoneyProbe(probe, probeCents) {
      const firstDigit = probe.search(/\d/)
      const lastDigit = probe.length - 1 - probe.split('').reverse().join('').search(/\d/)
      if (!Number.isFinite(probeCents) || probeCents <= 0 || firstDigit < 0 || lastDigit < firstDigit)
        return null

      const core = probe.slice(firstDigit, lastDigit + 1)
      const precision = core.replace(/\D/g, '').length - String(Math.round(probeCents / 100)).length
      if (precision !== 0 && precision !== 2) return null

      // With a minor unit, the last two digits follow the decimal separator
      const decimal = precision === 2 ? core.charAt(core.length - 3) : ''
      if (precision === 2 && (!decimal || /\d/.test(decimal))) return null

      const whole = precision === 2 ? core.slice(0, core.length - 3) : core
      const separators = whole.replace(/\d/g, '')

      return {
        prefix: probe.slice(0, firstDigit),
        suffix: probe.slice(lastDigit + 1),
        thousands: separators ? separators.charAt(separators.length - 1) : '',
        decimal,
        precision,
      }
    }

    // Returns null when there is no usable probe
    formatMoneyFromProbe(cents) {
      const amountInCents = Number(cents)
      const parts = this.getMoneyParts()
      if (!parts || !Number.isFinite(amountInCents)) return null

      const [whole, minor] = (Math.round(amountInCents) / 100).toFixed(parts.precision).split('.')
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${parts.thousands}`)
      return parts.prefix + (minor ? grouped + parts.decimal + minor : grouped) + parts.suffix
    }

    formatProductMoney(cents) {
      const amountInCents = Number(cents)
      if (!Number.isFinite(amountInCents)) return ''

      const probeFormatted = this.formatMoneyFromProbe(amountInCents)
      if (probeFormatted !== null) return probeFormatted

      const locale =
        window.theme?.locale || `${this.dataset.locale || 'en'}-${this.dataset.country || 'US'}`
      const currencyCode = this.dataset.currencyCode || 'USD'

      try {
        return this.addSpaceToPrice(
          new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
          }).format(amountInCents / 100),
        )
      } catch (error) {
        if (window.Shopify?.formatMoney) {
          return this.addSpaceToPrice(window.Shopify.formatMoney(amountInCents))
        }

        return `${this.symbol || '$'}${(amountInCents / 100).toFixed(2)}`
      }
    }

    formatProductMoneyWithoutTrailingZeros(cents) {
      return this.formatProductMoney(cents).replace(/([.,])00(?!\d)/, '')
    }

    detectDecimalSeparator(priceString) {
      // Remove all non-numeric/separator characters (keep commas and dots)
      const cleanStr = priceString.replace(/[^\d,.]/g, '')

      // Find the last non-digit character (either comma or dot)
      for (let i = cleanStr.length - 1; i >= 0; i--) {
        const char = cleanStr[i]
        if (char === ',' || char === '.') {
          // Check if it's followed by exactly 2 digits (cents)
          const nextChars = cleanStr.slice(i + 1)
          return /^\d{2}$/.test(nextChars) ? char : 'unknown'
        }
      }

      // Fallback: No separator found → guess based on currency symbol
      return /[€£]/.test(priceString) ? ',' : '.'
    }

    showFreeShippingBadge(price) {
      const freeShippingThreshold = parseFloat(this.dataset.freeShippingThreshold || 0)
      return this.dataset.country === 'US' && price >= freeShippingThreshold
    }

    sendDataLayerInfo() {
      const packSize = this.dataset.productPackSize
      const pdpType = this.dataset.pdpType
      let cartSource

      if (pdpType === 'popup') {
        cartSource = 'Quick View'
      } else {
        cartSource = packSize === 'Single' ? 'PDP Singles' : 'PDP Curated Pack'
      }

      window.dataLayer = window.dataLayer || []
      dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'view_item',
        cart_source: cartSource,
        ...window.intelligemsMeta,
        membership_status: this.dataset.membershipStatus,
        userId: this.dataset.customerId || null,
        page_type: 'Product Detail Page',
        color_pack: this.dataset.colorPack,
        login_status: this.dataset.loginStatus,
        pdp_type: this.dataset.pdpType,
        pack_quantity: this.dataset.packQuantity,
        ecommerce: {
          currency: this.dataset.ecommerceCurrency,
          value: Number(this.dataset.productPrice),
          items: [
            {
              item_id: Number(this.dataset.productId),
              cart_source: cartSource,
              item_name: this.dataset.productTitle,
              coupon: '',
              discount: Number(this.dataset.productDiscount),
              index: 1,
              item_brand: this.dataset.productBrand,
              item_category: this.dataset.productCategory,
              item_category2: this.dataset.productCategory2,
              item_category3: Number(this.dataset.productCategory3),
              item_category4: this.dataset.selectedSize,
              item_category5: this.dataset.productCategory5,
              item_list_id: '',
              item_list_name: '',
              item_variant: this.dataset.colorPack,
              price: Number(this.dataset.productPrice),
              quantity: 1,
              style_number: this.dataset.styleNumber ? this.dataset.styleNumber : '',
              sku_id: this.dataset.sku ? this.dataset.sku : '',
              variant_id: this.dataset.variantId ? this.dataset.variantId : '',
            },
          ],
        },
      })
    }

    toFixedTruncate(value, decimals = 2) {
      const factor = 10 ** decimals
      // Multiply, truncate, then divide to force 2 decimal places
      return (Math.trunc(value * factor) / factor).toFixed(decimals)
    }

    updateStyleSelectionWithJs(target) {
      const selectedLabel = target.closest('label')
      if (!selectedLabel) return
      const selectedStyleText = selectedLabel.querySelector('span').textContent
      const closestProductStyle = target.closest('product-style')
      if (!closestProductStyle) return
      // label is absent in the alt-length AB test variant snippet
      // (was 'h3 strong'; heading demoted to <p> for a11y heading-order)
      const styleTitle = closestProductStyle.querySelector(
        '.liquid-pdp__style-toggle-label strong, h3 strong',
      )
      if (styleTitle) styleTitle.textContent = selectedStyleText
    }

    updateBelowAtcModals(html) {
      const belowAtcModals = html.querySelector('.below-a2c-modals')
      if (belowAtcModals && this.belowAtcModals) {
        HTMLUpdateUtility.viewTransition(this.belowAtcModals, belowAtcModals, [])
      }
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.section
    }

    getProductById(productId) {
      let productFound = this.jsonData.packs_data_json.find((product) => product.id === productId)
      if (!productFound) {
        productFound = this.jsonData.singles_data_json.find((product) => product.id === productId)
      }
      if (!productFound) {
        productFound = this.jsonData.families_data_json.find((product) => product.id === productId)
      }
      if (!productFound) {
        productFound = this.jsonData.alternative_length_family_data_json.find(
          (product) => product.id === productId,
        )
      }
      return productFound || null
    }

    getProductByHandle(productUrl) {
      const productHandle = String(productUrl || '')
        .split('?')[0]
        .replace(/\/$/, '')
        .split('/')
        .pop()
      if (!productHandle) return null

      const productCollections = [
        this.jsonData.packs_data_json,
        this.jsonData.singles_data_json,
        this.jsonData.families_data_json,
        this.jsonData.alternative_length_family_data_json,
      ]

      for (const collection of productCollections) {
        const productFound = collection?.find((product) => product.handle === productHandle)
        if (productFound) return productFound
      }

      return null
    }

    get variantSelectors() {
      return this.querySelector('variant-selects')
    }

    get productColors() {
      return this.querySelector('product-colors')
    }

    get productFamily() {
      return this.querySelector('product-style.family-selector')
    }

    get productFabric() {
      return this.querySelector('product-style.fabric-selector')
    }

    get productType() {
      return this.querySelector('product-type-selector')
    }

    get packSelectors() {
      return this.querySelector('product-pack-selector')
    }

    get packProgress() {
      return this.querySelector('.pack-progress')
    }

    get productTitle() {
      return this.querySelector('.js-product-title')
    }

    get productDescriptionVariety() {
      return this.querySelector('.js-product-description-variety')
    }

    get productStarRating() {
      return this.querySelector('.js-product-star-rating')
    }

    get productPrice() {
      return this.querySelector('.js-product-price')
    }

    get yourCutSelector() {
      return this.querySelector('.js-your-cut-selector')
    }

    get productForm() {
      return this.querySelector('product-form')
    }

    get collapsibleWrapper() {
      return this.querySelector('.liquid-pdp__collapsible-wrapper')
    }

    get afterpay() {
      return this.querySelector('.afterpay-widget')
    }

    get promoCountdownCallout() {
      return this.querySelector('.promo-countdown-callout--pdp')
    }

    get productGallery() {
      // Exclude the modal gallery inside t-custom-pack; it's owned by
      // the bundle modal.
      return (
        Array.from(this.querySelectorAll('product-gallery')).find(
          (el) => !el.closest('t-custom-pack'),
        ) || null
      )
    }

    get jsonData() {
      const jsonDataContext = this.querySelector('.js-pdp-product-context-json')
      if (!jsonDataContext) return null
      return JSON.parse(jsonDataContext.textContent)
    }

    get selectedSize() {
      return (
        Array.from(this.querySelectorAll('variant-selects input:checked'))
          .map((input) => input.value)
          .join(' / ') || null
      )
    }

    get selectedPackSize() {
      return this.querySelector('product-pack-selector input:checked')?.dataset.size || null
    }

    get insidersPrice() {
      return this.querySelector('.js-insiders-price')
    }

    get belowPdpAppContent() {
      return document.querySelector('below-pdp-app-content')
    }

    get modalWrapper() {
      return this.querySelector('.pdp-modal-wrapper')
    }

    get sizeChart() {
      return document.querySelector('#modal-size-chart')
    }

    get pdpProductContext() {
      return this.querySelector('pdp-product-context')
    }

    get productUso() {
      return this.querySelector('product-uso')
    }

    get uiFooter() {
      return this.querySelector('.js-liquid-pdp__ui-footer')
    }

    get bxgyPrice() {
      return this.querySelector('.js-bxgy-price')
    }

    get bxgyWrapper() {
      return this.querySelector('.js-bxgy-wrapper')
    }

    get sizeChartButton() {
      return this.querySelector('.js-size-chart-button-wrapper')
    }

    get sizeChartModal() {
      return this.querySelector('size-chart-modal, size-chart-modal-tiles')
    }

    get availableInTarget() {
      return this.querySelector('available-in-target')
    }

    get productDescription() {
      return this.querySelector('.js-product-description')
    }

    get productPriceBlock() {
      return this.querySelector('.pdp-product-price-wrapper')
    }

    get pdpQuantitySelector() {
      return this.querySelector('pdp-quantity-selector')
    }

    get discountCode() {
      return this.querySelector('manual-discount-code')
    }

    get bundleBuilderQuantitySelector() {
      return this.querySelector('bundle-builder-quantity-selector')
    }

    get belowAtcModals() {
      return this.querySelector('.below-a2c-modals')
    }

    get completeTheLook() {
      return this.querySelector('.js-complete-the-look')
    }

    get styleItWith() {
      return this.querySelector('.js-pdp-style-it-with')
    }

    get expectedShipping() {
      return this.querySelector('.js-expected-shipping')
    }

    openBxgyPopup() {
      const modal = document.querySelector('image-modal')
      if (modal) {
        modal.querySelector('.js-image').src = this.bxgyWrapper.dataset.popupImage
        // a11y: describe the promo image for screen readers
        modal.querySelector('.js-image').alt = this.bxgyWrapper.dataset.popupImageAlt || ''
        modal.querySelector('.js-image-link').href = this.bxgyWrapper.dataset.popupLink
        modal.querySelector('.js-image-link').dataset.closePopup =
          this.bxgyWrapper.dataset.closePopup
        modal.openImageModal()
      }
    }

    showOosSubstituteBubble(text) {
      const bubble = this.querySelector('.liquid-pdp__oos-substitute-bubble__text')
      if (bubble) bubble.textContent = text || ''
      // Toggle attribute off/on to replay CSS animation on re-substitute
      if (this.dataset.oosSubstituteActive === 'true') {
        this.dataset.oosSubstituteActive = 'false'
        requestAnimationFrame(() => (this.dataset.oosSubstituteActive = 'true'))
      } else {
        this.dataset.oosSubstituteActive = 'true'
      }
    }

    hideOosSubstituteBubble() {
      this._isOosSubstituteSwap = false
      if (this.dataset.oosSubstituteActive !== 'true') return
      this.dataset.oosSubstituteActive = 'false'
    }

    /**
     * Set up pay-later modal functionality
     * Listens for clicks on .pay-later-wrapper.js-modal-trigger elements
     * and triggers the dynamic-modal's renderDynamicModalIframe function
     */
    setupPayLaterModal() {
      // Add click event listener to the product-info element for event delegation
      this.addEventListener('click', (event) => {
        const target = event.target.closest('.pay-later-wrapper.js-modal-trigger')
        if (!target) return
        event.preventDefault()

        // Find the dynamic-modal element
        const dynamicModal = document.querySelector('dynamic-modal')
        if (!dynamicModal) {
          console.warn('Dynamic modal element not found')
          return
        }

        // Call the renderDynamicModalIframe function with the configuration
        dynamicModal.renderDynamicModalIframe({
          modalId: 'afterpay-modal',
          iframeUrl: 'https://static.afterpay.com/modal/en_US.html',
          iframeHeight: '800',
          iframeWidth: '400',
          iframeHeightMobile: '800',
          iframeWidthMobile: '400',
        })
      })
    }

    // Bundle builder methods + getters. Every entry point checks
    // this.isCustomPackEligible() so non-eligible families no-op cleanly.

    isCustomPackEligible() {
      // Clearance quick-view is never bundle-eligible (customize/BYO are hidden in CSS too).
      // The quick-add modal otherwise hosts the full builder (quick-add-modal.js moves
      // .t-custom-pack-wrapper in), so it follows the same server-driven rule as the PDP.
      if (this.dataset.showOnlyClearance === 'true') return false
      // Eligibility is server-driven: sections/liquid-pdp.liquid looks up
      // (family_handle, pack_size) against snippets/custom-pack-products-data.liquid
      // and sets data-bundle-product-handle only when a record matches.
      return Boolean(this.dataset.bundleProductHandle)
    }

    get isBundleMode() {
      return this.dataset.bundleMode === 'true'
    }

    // Cap on selections.length and the slot grid size. Sourced from the
    // current PDP's matched record packSize (e.g. 3 on the 3-Pack PDP).
    get bundleLimit() {
      return Number(this.dataset.bundleMaxItems || 6)
    }

    initializeBundleState() {
      if (!this.isCustomPackEligible()) return
      this.bundleSelections = Array.isArray(this.bundleSelections) ? this.bundleSelections : []
      // Bundle mode activates only AFTER a slot edit commits (see
      // custom-pack confirmSelection). Pre-fill alone doesn't qualify.
      this.updateBundleModeUi()
    }

    revealTCustomPack(packColorsRaw) {
      if (!this.isCustomPackEligible()) return

      // Pre-fill works sizeless: slots render icon+color, ATC stays
      // disabled with "Select a size" until sizes are chosen.
      this.preFillBundleFromPack(packColorsRaw)

      const wrapper = this.querySelector('.t-custom-pack-wrapper')
      if (!wrapper) return

      // enterBundleMode -> setBundleMode(true) flips --shown on.
      if (typeof this.enterBundleMode === 'function') this.enterBundleMode()
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    // BYO entry point. Same as revealTCustomPack but skips pre-fill and
    // wipes any existing slot state so the user starts from a clean
    // empty bundle at the currently-active pack-size.
    revealTCustomPackEmpty() {
      if (!this.isCustomPackEligible()) return

      this.bundleSelections = []
      const tcp = this.querySelector('t-custom-pack')
      if (tcp) {
        tcp.slotStyles = []
        if (typeof tcp.renderSlots === 'function') tcp.renderSlots()
      }

      const wrapper = this.querySelector('.t-custom-pack-wrapper')
      if (!wrapper) return

      if (typeof this.enterBundleMode === 'function') this.enterBundleMode()
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    // Pre-populates slots with singles matching the clicked pack's colors
    // at the main size. Only commits a non-empty array (partial failure
    // leaves the existing bundle alone).
    preFillBundleFromPack(explicitColorsRaw) {
      if (!this.isCustomPackEligible()) return false

      try {
        // Caller passes the clicked tile's color list when editing a
        // non-active pack; otherwise fall back to the active pack heading.
        let colorsRaw = (explicitColorsRaw || '').trim()
        if (!colorsRaw) {
          const colorEl = this.querySelector(
            '.liquid-pdp__product-colors-heading [data-color-list]',
          )
          colorsRaw = colorEl?.dataset.colorList?.trim() || ''
          if (!colorsRaw) return false
        }

        const packSizeRaw = this.dataset.productPackSize
        const packSize = parseInt(packSizeRaw, 10)
        if (!packSize) return false

        // Read size from the MAIN variant-selects only. this.selectedSize
        // joins all matching elements -> "M / M" because the modal mirrors
        // the same radios. Also skip viewTransition's display:none leftover.
        const mainVariantSelects = Array.from(this.querySelectorAll('variant-selects')).find(
          (el) => !el.closest('t-custom-pack') && el.style.display !== 'none',
        )
        const selectedSize = mainVariantSelects
          ? Array.from(mainVariantSelects.querySelectorAll('input:checked'))
              .map((input) => input.value)
              .join(' / ')
          : ''

        const singles = this.jsonData?.singles_data_json
        if (!Array.isArray(singles) || !singles.length) return false

        const tcp = this.querySelector('t-custom-pack')
        if (!tcp) return false

        const colorCounts = this.parsePackColorList(colorsRaw, packSize)

        // Match swatches in JS — colors with spaces ("Heather Gray")
        // trip up CSS.escape attribute selectors silently.
        const swatchInputs = Array.from(tcp.querySelectorAll('input[data-color-list]'))
        if (!swatchInputs.length) return false

        const findSwatch = (color) => {
          const target = color.toLowerCase().trim()
          return swatchInputs.find(
            (input) => (input.dataset.colorList || '').toLowerCase().trim() === target,
          )
        }

        // Preserve per-slot sizes when an existing bundle is in flight
        // (edit-different-pack-while-builder-open). Mixed stays mixed;
        // empty bundle falls back to the main size.
        const existingSelections = Array.isArray(this.bundleSelections) ? this.bundleSelections : []

        const selections = []
        const slotStyles = []

        for (const { color, count } of colorCounts) {
          const swatchInput = findSwatch(color)
          if (!swatchInput) continue

          const handle = swatchInput.value
          const single = singles.find((p) => p.handle === handle)
          if (!single) continue

          const swatchBg =
            swatchInput.closest('.liquid-pdp__product-color-swatch')?.querySelector('.color')?.style
              .background || ''

          for (let i = 0; i < count; i++) {
            const slotIndex = selections.length
            const existingSize = existingSelections[slotIndex]?.title || ''
            const targetSize = existingSize || selectedSize
            const variant = targetSize ? single.variants.find((v) => v.title === targetSize) : null

            const selection = variant
              ? { id: variant.id, title: variant.title, color, handle }
              : { id: null, title: '', color, handle }

            selections.push(selection)
            slotStyles.push(swatchBg)
          }
        }

        if (!selections.length) return false

        this.bundleSelections = selections
        tcp.slotStyles = slotStyles

        if (typeof this.updateBundleModeUi === 'function') this.updateBundleModeUi()
        if (typeof tcp.renderSlots === 'function') tcp.renderSlots()

        return true
      } catch (error) {
        console.error(
          '%c preFillBundleFromPack threw',
          'color: limegreen; font-weight: bold;',
          error,
        )
        return false
      }
    }

    // Parses the comma-separated colors string from a pack's data-color-list.
    //   "Black"                              → [{ color: "Black", count: packSize }]
    //   "Black, White, Red"                  → [{ Black, 1 }, { White, 1 }, { Red, 1 }]
    //   "2x Black, 2x White, 2x Heather Gray" → counts taken from the Nx prefix
    parsePackColorList(raw, packSize) {
      const items = (raw || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)

      if (!items.length) return []
      if (items.length === 1) return [{ color: items[0], count: packSize }]

      return items
        .map((item) => {
          const match = item.match(/^(\d+)\s*x\s+(.+)$/i)
          if (match) {
            const count = parseInt(match[1], 10)
            return { color: match[2].trim(), count: count > 0 ? count : 1 }
          }
          return { color: item, count: 1 }
        })
        .filter((entry) => entry.color)
    }

    // Pack-size change while builder is visible. Re-renders against the
    // new pack handle (keeps URL/pack-tile state correct), then re-applies
    // bundle mode so gallery/title/price swap to the matching custom-pack.
    handlePackSizeChangeWithCustomPack(target, targetSize) {
      if (!this.isCustomPackEligible()) return
      const { productUrl, variantId, label } = this.getPackSizeChangeContext(target)
      if (!productUrl) return

      // Slot grid resizes to target pack size, so trim filled selections
      // to fit. Going UP is a no-op for the trim itself — slice returns at
      // most existing.length items either way.
      const targetSizeNum = Number(targetSize) || 0
      // Switching from a complete bundle (10 → 6, or 10 → 3 → 10 where the trim left 3 of
      // 10 filled) must neither carry the old pack's colors over nor strand a larger pack
      // half-empty: once the new pack has rendered, every slot is filled from that pack's
      // own default colors. A bundle still being built from scratch (BYO, partially
      // filled) is left alone. The trim above stays as the fallback if the pre-fill fails.
      const previousPackSize = parseInt(this.dataset.productPackSize, 10) || 0
      const refillDefaults =
        previousPackSize > 0 &&
        targetSizeNum !== previousPackSize &&
        this.getBundleSelections().length >= previousPackSize
      if (targetSizeNum > 0) {
        const existing = Array.isArray(this.bundleSelections) ? this.bundleSelections : []
        if (existing.length > targetSizeNum) {
          this.bundleSelections = existing.slice(0, targetSizeNum)
          const tcp = this.querySelector('t-custom-pack')
          if (tcp && Array.isArray(tcp.slotStyles)) {
            tcp.slotStyles = tcp.slotStyles.slice(0, targetSizeNum)
          }
        }
      }

      this.deferSectionRender(() => {
        const updateCallback = this.handleDataUpdateBig(productUrl, '', label, null, {
          useFirstImageForPacks: true,
        })

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, [], false, variantId),
          // handleDataUpdateBig re-applies enterBundleMode at the end of its
          // update pipeline whenever bundle mode is active (which it stays
          // through this path), so no explicit re-apply is needed here —
          // calling it again would rebuild the gallery twice and flash the tile.
          callback: (html) => {
            updateCallback(html)
            // The new pack's colors heading is in the DOM now, which is what the
            // pre-fill reads; existing per-slot sizes are kept by slot index.
            if (refillDefaults) this.preFillBundleFromPack()
          },
        })
      })
    }

    // .t-custom-pack-wrapper is always in the DOM; <t-custom-pack> only
    // sits inside it for non-Single packs. Swapping the wrapper on every
    // pack-size change is what makes the slots-grid appear/disappear.
    updateTCustomPackWrapper(html) {
      if (!this.isCustomPackEligible()) return
      const fromHtml = html.querySelector('.t-custom-pack-wrapper')
      const current = this.querySelector('.t-custom-pack-wrapper')
      if (!fromHtml || !current) return

      // Carry --shown forward when in bundle mode (fresh server HTML never
      // ships with it). Reads from dataset so a stale class on `current`
      // can't drag --shown forward after bundle mode has already exited.
      if (this.isBundleMode) fromHtml.classList.add('t-custom-pack-wrapper--shown')

      HTMLUpdateUtility.viewTransition(current, fromHtml, [])
    }

    getBundleSelections() {
      if (!Array.isArray(this.bundleSelections)) {
        this.bundleSelections = []
      }
      return this.bundleSelections
    }

    getBundleSelectionCount() {
      return this.getBundleSelections().length
    }

    isBundleReady() {
      const selections = this.getBundleSelections()
      if (selections.length !== this.bundleLimit) return false
      // Picks belonging to a different family than the active product (the
      // family-switch race, or otherwise stale selections) keep ATC disabled
      // until the user updates their selection from the current family.
      if (this.hasBundleFamilyMismatch()) return false
      // Every slot needs a real, in-stock variant. Sizeless (id:null) and
      // main-size-OOS slots both fail this check.
      return selections.every((s) => {
        if (!Number.isFinite(Number(s?.id)) || Number(s.id) <= 0) return false
        return this.isSelectionAvailable(s)
      })
    }

    // True when any selected single's handle is absent from the current
    // family's singles_data_json — i.e. the picks belong to a different family
    // than the now-active product (the family-switch race, or stale picks).
    // Gates ATC until the user resets their selection to the current family.
    hasBundleFamilyMismatch() {
      const selections = this.getBundleSelections()
      if (!selections.length) return false
      const singles = this.jsonData?.singles_data_json
      if (!Array.isArray(singles) || !singles.length) return false
      const familyHandles = singles.map((p) => p.handle)
      return selections.some((s) => s?.handle && !familyHandles.includes(s.handle))
    }

    // A pack/family section render is mid-flight (product-form carries the
    // .loading class from switch start until the callback / fetch-failure path).
    // Used only to grey out bundle ATC during the transition.
    isBundleRenderInFlight() {
      return this.querySelector('product-form')?.classList.contains('loading') === true
    }

    // True when the selection's variant is findable in singles_data_json
    // and currently in stock.
    isSelectionAvailable(selection) {
      if (!selection || !Number.isFinite(Number(selection.id)) || Number(selection.id) <= 0) {
        return false
      }
      const singles = this.jsonData?.singles_data_json
      if (!Array.isArray(singles)) return true
      const single =
        (selection.handle && singles.find((p) => p.handle === selection.handle)) ||
        singles.find((p) => p.variants?.some((v) => v.id === selection.id))
      if (!single) return true
      const variant = single.variants?.find((v) => v.id === selection.id)
      if (!variant) return false
      return this.isSingleVariantPurchasable(variant)
    }

    // Selection's variant has custom.warehouse_inventory shipbob_fontana = false
    // (is_fontana_oos in singles_data_json). Non-US only: sold out + red X.
    isSelectionFontanaOos(selection) {
      if (!SHOW_FONTANA_OOS_STYLE) return false
      if (!selection || !this.isFontanaSoldOutRegion()) return false
      const singles = this.jsonData?.singles_data_json
      if (!Array.isArray(singles)) return false
      const single =
        (selection.handle && singles.find((p) => p.handle === selection.handle)) ||
        singles.find((p) => p.variants?.some((v) => v.id === selection.id))
      const variant = single?.variants?.find((v) => v.id === selection.id)
      // Fully sold out keeps the white X
      return variant?.available === true && variant.is_fontana_oos === true
    }

    getSubmitButtonText() {
      if (!this.isBundleMode) {
        const submitButton = this.querySelector('product-form form button[type="submit"]')
        return submitButton?.dataset.addToCartText || 'Add to cart'
      }

      if (this.isBundleReady()) {
        const submitButton = this.querySelector('product-form form button[type="submit"]')
        return submitButton?.dataset.addToCartText || 'Add to Cart'
      }

      const selections = this.getBundleSelections()

      // Family mismatch: picks are from a different family than the active
      // product. Prompt a reset before the OOS/count messaging.
      if (this.hasBundleFamilyMismatch()) {
        return 'Update Your Selection'
      }

      // Slots carried over a size this pack doesn't offer (main size got cleared)
      if (this.hasUnofferedBundleSize(selections)) {
        const submitButton = this.querySelector('product-form form button[type="submit"]')
        return submitButton?.dataset.selectSizeText || 'Select a size'
      }

      // OOS slots take priority: user can't ship their current config
      // regardless of count.
      const oosCount = selections.filter((selection) => {
        const id = Number(selection?.id)
        if (!Number.isFinite(id) || id <= 0) return false
        return !this.isSelectionAvailable(selection)
      }).length
      if (oosCount > 0) {
        const noun = oosCount === 1 ? 'Item Is' : 'Items Are'
        return `${oosCount} ${noun} Out of Stock`
      }

      return 'Choose Shirt Sizes'
    }

    // A slot's size isn't in the main size selector (e.g. 4XL slots on a 3-pack without 4XL)
    hasUnofferedBundleSize(selections) {
      const variantSelects = this.getMainVariantSelects()
      if (!variantSelects) return false
      const offeredSizes = new Set(
        Array.from(variantSelects.querySelectorAll('input')).map((input) => input.value),
      )
      if (!offeredSizes.size) return false
      return selections.some(
        (selection) => selection?.title && !offeredSizes.has(selection.title.split(' / ')[0]),
      )
    }

    updateBundleModeUi() {
      if (!this.isCustomPackEligible()) return

      const currentValue = this.querySelector('.liquid-pdp__pack-selector-current-value')
      if (currentValue) {
        // Show the actual pack-size, not "Bundle" — keeps the user's
        // chosen quantity visible.
        currentValue.textContent = this.selectedPackSize || 'Single'
      }

      const singlesRadio = this.querySelector('input[data-size="Single"]')
      const bundleRadio = this.querySelector('input[data-bundle-mode="true"]')
      if (bundleRadio && this.isBundleMode) {
        bundleRadio.checked = true
      } else if (singlesRadio && this.dataset.productPackSize === 'Single') {
        singlesRadio.checked = true
      }

      const productFound = this.getProductById(Number(this.dataset.productId))
      if (productFound) {
        this.updateFormState(productFound)
      }

      // Runs after updateFormState() above — that re-enables the button,
      // bundle mode wants it disabled until all slots are full.
      const submitButton = this.querySelector('product-form form button[type="submit"]')
      if (submitButton && this.isBundleMode) {
        const ready = this.isBundleReady()
        submitButton.disabled = !ready || this.isBundleRenderInFlight()
        const submitButtonText = submitButton.querySelector('.js-button-text')
        if (submitButtonText) submitButtonText.innerHTML = this.getSubmitButtonText()
        // Bundle product has no per-size inventory — force-clear any stale
        // 'false' (e.g. a curated-pack OOS variant having set it during a
        // pre-bundle code path) so the Notify Me form doesn't linger.
        this.dataset.productAvailable = 'true'
      }

      const productForm = this.querySelector('product-form')
      if (productForm?.renderBundleBuilderState) {
        productForm.renderBundleBuilderState()
      }
    }

    setBundleMode(isBundleMode) {
      if (!this.isCustomPackEligible()) return
      // No clearance gate: customize buttons are hidden on clearance tiles
      // in CSS, so bundle mode is never reachable for a clearance pack.
      const next = isBundleMode ? 'true' : 'false'
      // if (this.dataset.bundleMode !== next) {
      //   console.log(
      //     `%c[bundle-mode] ${next === 'true' ? 'ON' : 'OFF'}`,
      //     'color: limegreen; font-weight: bold;',
      //   )
      // }
      this.dataset.bundleMode = next
      const wrapper = this.querySelector('.t-custom-pack-wrapper')
      if (wrapper) wrapper.classList.toggle('t-custom-pack-wrapper--shown', isBundleMode)
      this.updateBundleModeUi()
    }

    // Enters bundle mode: active product swaps to the matching custom-pack
    // (3/6/10), gallery + title + price + dataset.productId update in place.
    // Pack tiles + pack-size selector keep their original product so the
    // user can exit by clicking one.
    async enterBundleMode() {
      if (!this.isCustomPackEligible()) return
      // Not idempotent on this.isBundleMode — pack-size re-renders wipe
      // the gallery/title/price DOM, so we re-apply even when
      // dataset.bundleMode already reads 'true'.

      const handle = this.dataset.bundleProductHandle
      if (!handle) {
        console.error(
          '%c enterBundleMode: missing data-bundle-product-handle',
          'color: limegreen; font-weight: bold;',
        )
        return
      }

      this.setBundleMode(true)
      this.uncheckPackTiles()
      this.setColorDisplayCustom()

      // Prefer pre-rendered, Markets-aware data from generate-json-data.liquid.
      // Avoids /products/<handle>.js (always shop-base currency, no Markets
      // conversion) for international users.
      const preRendered = this.getPreRenderedBundleProduct(handle)
      if (preRendered) {
        // console.log(
        //   '%c enterBundleMode: using pre-rendered bundle product data',
        //   'color: limegreen; font-weight: bold;',
        //   { handle, preRendered },
        // )
        this.applyBundleProductPresentation(preRendered)
        return
      }

      // console.log(
      //   '%c enterBundleMode: no pre-rendered entry, falling back to /products/<handle>.js',
      //   'color: limegreen; font-weight: bold;',
      //   { handle },
      // )

      try {
        const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`)
        if (!response.ok) throw new Error(`fetch failed: ${response.status}`)
        const productData = await response.json()

        // Staleness guard: bail if the world moved on while awaiting
        // (user exited, or pack-size changed to a different bundle
        // product). Otherwise stale data stomps title/price.
        if (!this.isBundleMode || this.dataset.bundleProductHandle !== handle) return

        this.applyBundleProductPresentation(productData)
      } catch (error) {
        console.error(
          '%c enterBundleMode: fetch threw',
          'color: limegreen; font-weight: bold;',
          error,
        )
      }
    }

    getPreRenderedBundleProduct(handle) {
      const list = this.jsonData?.bundle_products_data_json
      if (!Array.isArray(list) || list.length === 0) {
        // console.log(
        //   '%c getPreRenderedBundleProduct: bundle_products_data_json missing or empty',
        //   'color: limegreen; font-weight: bold;',
        //   { list },
        // )
        return null
      }
      const match = list.find((p) => p?.handle === handle)
      if (!match) {
        // console.log(
        //   '%c getPreRenderedBundleProduct: no match for handle',
        //   'color: limegreen; font-weight: bold;',
        //   { handle, available: list.map((p) => p?.handle) },
        // )
        return null
      }
      return match
    }

    // Exits without restoring product presentation — callers
    // (handleProductChange, handlePackSizeChange) trigger their own
    // re-render which replaces gallery/title/price.
    exitBundleMode() {
      if (!this.isCustomPackEligible()) return

      this.bundleSelections = []
      const tcp = this.querySelector('t-custom-pack')
      if (tcp) {
        tcp.slotStyles = []
        if (typeof tcp.renderSlots === 'function') tcp.renderSlots()
      }

      // Clear the empty-bundle opacity hide so the regular pack's gallery
      // is visible after re-render.
      const gallery = this.productGallery
      if (gallery) gallery.style.opacity = ''

      // Unconditional — re-asserts wrapper class even if dataset already
      // reads 'false', self-healing any prior drift.
      this.setBundleMode(false)
    }

    // Idempotent, eligibility-agnostic teardown of leftover bundle slot UI.
    // exitBundleMode/setBundleMode both gate on isCustomPackEligible(), which
    // is false once a non-bundle product (e.g. a single) has rendered — so
    // they can't clear a lingering builder. This no-ops when nothing is
    // showing, so it's safe to call unconditionally as a final check.
    ensureBundleUiHidden() {
      const shownWrappers = this.querySelectorAll('.t-custom-pack-wrapper--shown')
      const needsCleanup = shownWrappers.length > 0 || this.dataset.bundleMode === 'true'
      if (!needsCleanup) return

      this.dataset.bundleMode = 'false'
      shownWrappers.forEach((wrapper) =>
        wrapper.classList.remove('t-custom-pack-wrapper--shown'),
      )

      // Sync the product-form bundle builder (hidden = !bundleMode).
      const productForm = this.querySelector('product-form')
      if (productForm?.renderBundleBuilderState) productForm.renderBundleBuilderState()
    }

    uncheckPackTiles() {
      if (!this.isCustomPackEligible()) return
      // Pack tiles + modal swatches both live in <product-colors>. Skip
      // anything inside t-custom-pack so the modal keeps its selection.
      this.querySelectorAll('product-colors input[type="radio"]').forEach((input) => {
        if (input.closest('t-custom-pack')) return
        input.checked = false
      })
    }

    setColorDisplayCustom() {
      if (!this.isCustomPackEligible()) return
      const display = this.querySelector('[data-color-display]')
      if (!display) return
      const toggle = this.querySelector('#toggle-colors')
      if (toggle) toggle.remove()
      display.textContent = 'Custom Colors'
    }

    formatSizeLabel(value) {
      const t = getPdpTranslations()
      switch (value) {
        case 'XS':
        case 'XSM':
          return t.size_x_small || 'X-Small'
        case 'S':
        case 'SM':
          return t.size_small || 'Small'
        case 'M':
        case 'MD':
          return t.size_medium || 'Medium'
        case 'L':
        case 'LG':
          return t.size_large || 'Large'
        case 'XL':
        case 'XLG':
          return t.size_x_large || 'X-Large'
        case '2XL':
        case '2XLG':
          return t.size_2x_large || '2X-Large'
        case '3XL':
        case '3XLG':
          return t.size_3x_large || '3X-Large'
        case '4XL':
        case '4XLG':
          return t.size_4x_large || '4X-Large'
        default:
          return value
      }
    }

    // The page's primary <variant-selects> — skips t-custom-pack copies
    // and the display:none leftover viewTransition keeps for 500ms.
    getMainVariantSelects() {
      return (
        Array.from(this.querySelectorAll('variant-selects')).find(
          (el) => !el.closest('t-custom-pack') && el.style.display !== 'none',
        ) || null
      )
    }

    // Sync main size selector to bundle state. Called from
    // custom-pack.renderSlots after any slot mutation.
    //   All same size  -> check radio + show formatted label.
    //   Mixed/partial  -> uncheck all + show 'Mixed Sizes'.
    //   No selections  -> leave as-is.
    syncMainSizeFromBundle() {
      if (!this.isCustomPackEligible()) return
      const selections = Array.isArray(this.bundleSelections) ? this.bundleSelections : []
      if (!selections.length) return

      const sizes = new Set(selections.map((s) => s?.title).filter(Boolean))
      if (sizes.size === 0) return

      const main = this.getMainVariantSelects()
      if (!main) return

      const optionGroup = main.querySelector('.liquid-pdp__variant-selector-buttons')
      const header = optionGroup?.previousElementSibling
      const sizeDisplay = header?.querySelector('.js-selected-size-display')

      // Any non-uniform state (multiple sizes OR a sizeless slot) reads
      // as 'Mixed' — a single-slot pick mustn't leak to the main selector.
      const sizedCount = selections.filter((s) => s?.title).length
      const allSized = sizedCount === selections.length
      if (sizes.size > 1 || !allSized) {
        main.querySelectorAll('input[type="radio"]:checked').forEach((i) => {
          i.checked = false
        })
        if (sizeDisplay) sizeDisplay.textContent = 'Mixed Sizes'
        this.dataset.selectedSize = ''
        return
      }

      const [size] = sizes
      const radios = main.querySelectorAll('input[type="radio"]')
      let matched = false
      radios.forEach((input) => {
        const isMatch = input.value === size
        input.checked = isMatch
        if (isMatch) matched = true
      })
      if (matched && sizeDisplay) sizeDisplay.textContent = this.formatSizeLabel(size)
      this.dataset.selectedSize = matched ? size : ''
    }

    // Build gallery media from selected singles.
    //   One unique color    -> ALL images from that single.
    //   Multiple colors     -> ONE featured image per unique handle
    //                          (deduped, first-appearance order).
    getBundleSelectionGalleryMedia() {
      const selections = Array.isArray(this.bundleSelections) ? this.bundleSelections : []
      const singles = this.jsonData?.singles_data_json
      if (!selections.length || !Array.isArray(singles) || !singles.length) return []

      const resolvedSingles = []
      selections.forEach((selection) => {
        const byHandle = selection?.handle
          ? singles.find((p) => p.handle === selection.handle)
          : null
        const byVariant = !byHandle
          ? singles.find((p) => p.variants?.some((v) => v.id === selection?.id))
          : null
        const single = byHandle || byVariant
        if (single) resolvedSingles.push(single)
      })
      if (!resolvedSingles.length) return []

      const uniqueSingles = []
      const seenHandles = new Set()
      resolvedSingles.forEach((single) => {
        const handle = single.handle || ''
        if (!handle || seenHandles.has(handle)) return
        seenHandles.add(handle)
        uniqueSingles.push(single)
      })
      if (!uniqueSingles.length) return []

      function mapImageMedia(mediaItem) {
        const src = mediaItem?.src || mediaItem?.preview_image?.src || ''
        if (!src) return null
        return { media_type: 'image', src, alt: mediaItem?.alt || '' }
      }

      function getFeaturedImage(single) {
        const featured = single?.featured_image
        const src =
          featured?.src ||
          featured?.url ||
          (typeof featured === 'string' ? featured : '') ||
          single?.images?.[0]?.src ||
          single?.media?.[0]?.src ||
          ''
        if (!src) return null
        return { media_type: 'image', src, alt: single?.title || '' }
      }

      // Single-color bundle: every image from that single. No videos —
      // bundle mode is image-only by design.
      if (uniqueSingles.length === 1) {
        const single = uniqueSingles[0]
        const imageMedia = Array.isArray(single?.media)
          ? single.media
              .filter((item) => item?.media_type === 'image')
              .map(mapImageMedia)
              .filter(Boolean)
          : []
        if (imageMedia.length) return imageMedia
        const featured = getFeaturedImage(single)
        return featured ? [featured] : []
      }

      // Multi-color: one featured image per unique color/handle.
      return uniqueSingles.map((single) => getFeaturedImage(single)).filter(Boolean)
    }

    // Featured image of the custom-pack product as gallery media. Reads the
    // pre-rendered entry, else the copy cached by applyBundleProductPresentation
    // (/products/<handle>.js fallback). Returns null when unavailable.
    getBundleProductFeaturedImageMedia() {
      const handle = this.dataset.bundleProductHandle
      const cached = this.bundleProductFeaturedImage
      const cachedSrc = cached?.handle === handle ? cached.src : ''
      const src = this.getPreRenderedBundleProduct(handle)?.featured_image || cachedSrc
      if (!src) return null
      // Gallery appends "&width=..." so the URL needs an existing query string.
      const normalizedSrc = src.includes('?') ? src : `${src}?`
      return {
        media_type: 'image',
        src: normalizedSrc,
        alt: this.dataset.productTitle || 'Build your own pack',
      }
    }

    // While in bundle mode, keep the main gallery synced to selections.
    // Empty bundle = show the custom-pack product's featured image (falls
    // back to the BYO tile placeholder in snippets/product-color-buttons.liquid
    // if it's unavailable) so the gallery never goes blank while the user is
    // building. First slot fill swaps to selection-driven media.
    updateBundleGalleryFromSelections() {
      if (!this.isCustomPackEligible()) return
      if (!this.isBundleMode) return
      const gallery = this.productGallery
      if (!gallery || typeof gallery.updateGalleryMedia !== 'function') return

      const selectionMedia = this.getBundleSelectionGalleryMedia()
      const media = selectionMedia.length
        ? selectionMedia
        : [
            this.getBundleProductFeaturedImageMedia() || {
              media_type: 'image',
              src: 'https://cdn.shopify.com/s/files/1/0220/4008/4552/files/custom-pack-shirts-in-bag.jpg?v=1',
              alt: 'Build your own pack',
            },
          ]
      gallery.style.opacity = ''

      // null videoData prevents the gallery from splicing video back in
      // at video_position — bundle mode renders images only.
      gallery.updateGalleryMedia(media, null, null, null)
    }

    // Points the "Custom Pack" (BYO) tile at the custom-pack's featured image.
    // No-ops without an image so the server-rendered placeholder stays.
    updateBundleTileImage(featuredImageUrl) {
      if (!featuredImageUrl) return
      const tile = this.querySelector('product-colors [data-tcp-byo]')
      const tileImage = tile?.querySelector('img')
      if (!tileImage) return
      // Hides the "Custom Pack" bar (see product-color-buttons.liquid CSS).
      tile.setAttribute('data-has-product-image', '')
      const separator = featuredImageUrl.includes('?') ? '&' : '?'
      const nextSrc = `${featuredImageUrl}${separator}width=300`
      if (tileImage.getAttribute('src') !== nextSrc) tileImage.src = nextSrc
    }

    applyBundleProductPresentation(productData) {
      if (!this.isCustomPackEligible()) return
      if (!productData) return

      this.dataset.productId = productData.id
      this.dataset.productHandle = productData.handle
      this.dataset.productTitle = productData.title
      // Cache featured image for the empty-bundle gallery (fetch fallback path).
      this.bundleProductFeaturedImage = {
        handle: productData.handle,
        src: productData.featured_image || '',
      }
      this.updateBundleTileImage(productData.featured_image)

      // Leave the visible title as-is: product-title.liquid renders the
      // customer-facing family title for the current pack before bundle mode
      // swaps product identity. Overwriting with productData.title would surface
      // the raw custom-pack product name (e.g. "Custom Classic Crew 6-Pack")
      // instead. dataset.productTitle still tracks the real product for
      // analytics/cart identity.

      this.updateBundleGalleryFromSelections()

      if (productData.price_ui) {
        // Pre-rendered shape from generate-json-data.liquid: Markets-aware,
        // matches standard PDP price markup (compare-at, promo, weekly deal).
        // console.log(
        //   '%c applyBundleProductPresentation: rendering price from price_ui',
        //   'color: limegreen; font-weight: bold;',
        //   productData.price_ui,
        // )
        this.updateProductPriceFromProductData(productData)
        return
      }

      // Fallback: /products/<handle>.js raw-cents shape. Shop-base currency
      // only — used only when no pre-rendered entry exists.
      if (this.productPriceBlock && Number.isFinite(Number(productData.price))) {
        // console.log(
        //   '%c applyBundleProductPresentation: rendering price from raw cents (shop-base currency)',
        //   'color: limegreen; font-weight: bold;',
        //   { price: productData.price, compare_at_price: productData.compare_at_price },
        // )
        const priceFmt = this.formatPackProgressMoney(productData.price)
        const hasCompare = Number(productData.compare_at_price) > Number(productData.price)
        const compareFmt = hasCompare
          ? this.formatPackProgressMoney(productData.compare_at_price)
          : ''
        const priceUi = {
          has_compare_at_row: hasCompare,
          compare_at_price_formatted: compareFmt,
          product_price: productData.price / 100,
          product_price_formatted: priceFmt,
          discounted_price: productData.price / 100,
          discounted_price_formatted: priceFmt,
          is_weekly_deal: false,
          has_promo_discount: false,
          promo_discount_percentage: 0,
          is_clearance: false,
          is_final_sale: false,
          active_promo_holiday_name: '',
          weekly_deal_ends_in_html: '',
          promo_ends_in_html: '',
        }
        this.productPriceBlock.classList.remove('pdp-product-price-wrapper--manual-discount-active')
        this.productPriceBlock.classList.remove('pdp-product-price-wrapper--deal-row-active')
        this.productPriceBlock.innerHTML = this.buildProductPriceHtml(priceUi)
        this.updatePackPerItemPrice(productData.price)
        this.dataset.productPrice = (productData.price / 100).toString()
        this.dataset.productPriceFormatted = priceFmt
      }
    }
  }
  customElements.define('product-info', ProductInfo)
}

// Same endpoint + list as snippets/klaviyo-form.liquid (footer uses klaviyo_list_id LUzTHf).
const KLAVIYO_KMAIL_SUBSCRIBE_URL = 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe'
const KLAVIYO_FOOTER_MARKETING_LIST_ID = 'LUzTHf'

async function subscribeKlaviyoFooterMarketingList(email) {
  const body = new URLSearchParams({
    g: KLAVIYO_FOOTER_MARKETING_LIST_ID,
    email,
    $source: 'pdp-back-in-stock-notify-me',
  })
  const listResponse = await fetch(KLAVIYO_KMAIL_SUBSCRIBE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  })
  const responseText = await listResponse.text()
  if (!listResponse.ok) {
    const detail = responseText.slice(0, 500)
    const err = new Error(
      `HTTP ${listResponse.status} ${listResponse.statusText}${detail ? ` — ${detail}` : ''}`,
    )
    err.status = listResponse.status
    err.responseBody = detail
    throw err
  }
  return listResponse
}

if (!customElements.get('klaviyo-notify-me')) {
  class KlaviyoNotifyMe extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback() {
      this.init()
    }

    init() {
      this.form = this.querySelector('form')
      this.button = this.querySelector('button')
      this.emailInput = this.querySelector('input')
      this.successMessage = this.querySelector('.js-success-message')
      this.form.addEventListener('submit', async (event) => {
        event.preventDefault()

        this.button.classList.add('loading')
        this.button.setAttribute('aria-busy', 'true')
        this.emailInput.setAttribute('disabled', true)
        this.querySelector('.loading__spinner').classList.remove('hidden')

        const email = this.emailInput.value.trim()
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

        if (!emailRegex.test(email)) {
          this.emailInput.setCustomValidity(
            getPdpTranslations().valid_email_error || 'Please enter a valid email address',
          )
          this.emailInput.reportValidity()
          return
        }

        this.emailInput.setCustomValidity('')

        const variantId = this.form.querySelector('input[name="variant_id"]').value
        const options = {
          method: 'POST',
          headers: {
            accept: 'application/json',
            revision: '2024-06-15',
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: `${email}`,
                    },
                  },
                },
                channels: ['EMAIL'],
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${variantId}`,
                  },
                },
              },
            },
          }),
        }

        const response = await fetch(
          'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=Lj2344',
          options,
        )
        if (response.ok) {
          this.successMessage.classList.remove('visually-hidden')
          this.form.classList.add('visually-hidden')
          try {
            await subscribeKlaviyoFooterMarketingList(email)
          } catch (err) {
            console.error(
              '[KlaviyoNotifyMe] footer list subscribe failed (BIS succeeded; marketing list not updated)',
              err.message,
              err.responseBody != null ? err.responseBody : '',
            )
          }
        }
        this.button.classList.remove('loading')
        this.button.removeAttribute('aria-busy')
        this.emailInput.removeAttribute('disabled')
        this.querySelector('.loading__spinner').classList.add('hidden')
      })

      // Add input validation on email changes
      this.emailInput.addEventListener('input', () => {
        const email = this.emailInput.value.trim()
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

        if (!emailRegex.test(email)) {
          this.button.setAttribute('disabled', true)
          this.emailInput.setCustomValidity(
            getPdpTranslations().valid_email_error || 'Please enter a valid email address',
          )
        } else {
          this.button.removeAttribute('disabled')
          this.emailInput.setCustomValidity('')
        }
      })
    }
  }
  customElements.define('klaviyo-notify-me', KlaviyoNotifyMe)
}

if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    oldText = ''

    constructor() {
      super()
      this.observer = null
      this.mutationObserver = null
      this.productForm = null
    }

    connectedCallback() {
      this.init()
      this.setupMutationObserver()
    }

    setupMutationObserver() {
      this.mutationObserver = new MutationObserver((mutations) => {
        // Check if the product form was removed and re-added
        const productForm = document.querySelector('product-form')
        if (productForm !== this.productForm) {
          this.cleanupObserver()
          this.init()
        }
      })

      // Observe the entire document for changes to product-form
      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      })
    }

    cleanupObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    }

    init() {
      this.productForm = document.querySelector('product-form')
      if (!this.productForm) return

      this.button = this.querySelector('button')
      this.button.addEventListener('click', () => {
        const targetElement = document.querySelector('.liquid-pdp__header')
        const selectedVariant = document.querySelector('variant-selects input:checked')
        if (targetElement) {
          const offsetTop = targetElement.getBoundingClientRect().top + window.scrollY - 87
          targetElement.scrollIntoView({ top: offsetTop, behavior: 'smooth' })

          if (selectedVariant) {
            this.oldText = this.button.textContent
            this.button.textContent = getPdpTranslations().adding_to_cart || 'Adding to Cart...'
            document.querySelector('product-form button[type="submit"]').click()
            setTimeout(() => {
              this.button.textContent = this.oldText
            }, 1500)
          }
        }
      })

      // Set up the Intersection Observer
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const rect = entry.boundingClientRect
            // Show only after the entire product form has left the viewport (scrolled past the bottom)
            if (!entry.isIntersecting && rect.bottom <= 0) this.classList.remove('hidden')
            else this.classList.add('hidden')
          })
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0,
        },
      )

      // Start observing the product form
      this.observer.observe(this.productForm)
    }

    disconnectedCallback() {
      // Clean up both observers when the element is removed
      this.cleanupObserver()
      if (this.mutationObserver) {
        this.mutationObserver.disconnect()
        this.mutationObserver = null
      }
    }
  }
  customElements.define('sticky-add-to-cart', StickyAddToCart)
}

if (!customElements.get('open-size-chart')) {
  class OpenSizeChart extends HTMLElement {
    optionName = null
    constructor() {
      super()
    }

    connectedCallback() {
      this.optionName = this.dataset.optionName || null
      this.init()
    }

    init() {
      this.addEventListener('click', this.openModal.bind(this))
      // role="button" elements don't get synthesized clicks for Enter/Space.
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return
        e.preventDefault()
        this.openModal()
      })
    }

    openModal() {
      // Inside the quick-add modal, use its own chart (not the page PDP's) so it
      // stacks above the custom-pack sheet in the same stacking context.
      const sizeChartModal =
        this.closest('quick-modal, deal-quick-modal')?.querySelector('size-chart-modal, size-chart-modal-tiles') ||
        document.querySelector('size-chart-modal, size-chart-modal-tiles')
      if (sizeChartModal) {
        sizeChartModal.optionName = this.optionName
        sizeChartModal.open()
      }
    }
  }
  customElements.define('open-size-chart', OpenSizeChart)
}

if (!customElements.get('open-compare-modal')) {
  class OpenCompareModal extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback() {
      this.init()
    }

    init() {
      this.addEventListener('click', () => {
        const compareModal = document.querySelector('compare-modal')
        const style = this.dataset.style
        if (compareModal) {
          compareModal.open(style)
        }
      })
    }
  }
  customElements.define('open-compare-modal', OpenCompareModal)
}

// CompareModal - defined here so it's available for AJAX-loaded content (e.g., menu-with-pdp)
if (!customElements.get('compare-modal')) {
  class CompareModal extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback() {
      this.querySelectorAll('.js-compare-modal-radio').forEach((radio) => {
        radio.addEventListener('change', (e) => {
          this.querySelectorAll('.js-block-container').forEach((container) => {
            if (container.dataset.category === e.target.value) {
              container.classList.add('is-active')
            } else {
              container.classList.remove('is-active')
            }
          })
        })
      })
    }
  }
  customElements.define('compare-modal', CompareModal)
}

if (!customElements.get('pdp-style-it-with')) {
  // Owns two things for the PDP style-it-with carousel: the whole card is the quick
  // add trigger (the cards hold no links out to the product page), and clearance
  // prices are kept on the cart's current tier.
  //
  // Clearance prices scale with the number of clearance items in the cart, so the
  // tier snippets/pdp-style-it-with.liquid rendered can go stale as soon as the
  // shopper adds something. Liquid ships every tier's price on the card, so a cart
  // change only has to swap in the price for the tier the shopper would unlock —
  // no client-side money formatting, which would print USD on every market.
  class PdpStyleItWith extends HTMLElement {
    #clickHandler = this.handleClick.bind(this)

    connectedCallback() {
      this.addEventListener('click', this.#clickHandler)
      this.initSwiper()

      this.clearanceCards = Array.from(this.querySelectorAll('[data-clearance-card]'))
      if (!this.clearanceCards.length) return
      if (typeof ClearanceTierPricing === 'undefined') return

      this.unsubscribeClearancePricing = ClearanceTierPricing.subscribe((count) =>
        this.applyClearanceTierPricing(count),
      )
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#clickHandler)
      this.swiper?.destroy(true, true)
      this.swiper = null
      this.unsubscribeClearancePricing?.()
    }

    handleClick(event) {
      const arrow = event.target.closest('[data-sitw-prev], [data-sitw-next]')
      if (arrow) return

      // Image, title, and price all open the popup rather than navigating. The plus
      // button is its own <quick-add-button> and handles the click itself, so clicks
      // that started there are left alone instead of opening the modal twice.
      if (event.target.closest('quick-add-button')) return

      const card = event.target.closest('.pdp-style-it-with__item')
      card?.querySelector('quick-add-button')?.click()
    }

    initSwiper() {
      const swiperElement = this.querySelector('.pdp-style-it-with__swiper')
      this.prevArrow = this.querySelector('[data-sitw-prev]')
      this.nextArrow = this.querySelector('[data-sitw-next]')
      if (!swiperElement || typeof Swiper === 'undefined') return

      this.swiper = new Swiper(swiperElement, {
        slidesPerView: 3.5,
        slidesPerGroup: 3,
        spaceBetween: 10,
        loop: false,
        watchOverflow: true,
        a11y: { slideRole: 'listitem' },
        mousewheel: {
          enabled: true,
          forceToAxis: true,
          releaseOnEdges: true,
        },
        navigation:
          this.nextArrow && this.prevArrow
            ? {
                nextEl: this.nextArrow,
                prevEl: this.prevArrow,
              }
            : null,
      })

      window.enablePdpDesktopSwiperMousewheel?.(this.swiper)
    }

    // Adding this item puts the shopper at cart count + 1 — the same projection
    // pdp-product-price.liquid and clearance-tier-pricing.js price against.
    applyClearanceTierPricing(count) {
      const percentage = ClearanceTierPricing.getDiscountPercentage(Number(count || 0) + 1)

      this.clearanceCards.forEach((card) => {
        const tierPrice = card.getAttribute(`data-clearance-price-${percentage}`)
        if (!tierPrice) return

        const price = card.querySelector('[data-clearance-price]')
        if (price) price.textContent = tierPrice

        const discount = card.querySelector('[data-clearance-discount]')
        const template = card.dataset.percentOffTemplate
        if (discount && template) discount.textContent = template.replace('%PCT%', percentage)
      })
    }
  }
  customElements.define('pdp-style-it-with', PdpStyleItWith)
}

/**
 *
 * GLOBALS (Replace this on the new theme)
 *
 */

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  }
}

function handleNotReturningSwatches(productInfo = document.querySelector('product-info')) {
  if (!productInfo) return

  const productColors = productInfo.querySelector('product-colors')
  if (!productColors) return

  const product_color = productColors.querySelector('input:checked')
  if (!product_color) return

  if (product_color.dataset.notReturning === 'true' && product_color.dataset.disabled === 'true') {
    const isClearanceMode = productInfo.dataset.showOnlyClearance === 'true'
    const first_color = isClearanceMode
      ? productColors.querySelector(
          '.js-clearance-swatches input[data-is-clearance="true"]:not(.disabled):not([data-disabled="true"]):not(:checked)',
        )
      : productColors.querySelector('input')

    if (first_color) first_color.click()
  }
}

function dataLayerPageView() {
  const productInfo = document.querySelector('product-info')
  if (!productInfo) return
  const packSize = productInfo.dataset.productPackSize
  let cartSource
  if (productInfo.dataset.pdpType === 'popup') {
    cartSource = 'Quick View'
  } else {
    cartSource = packSize === 'Single' ? 'PDP Singles' : 'PDP Curated Pack'
  }
  window.dataLayer = window.dataLayer || []
  dataLayer.push({ ecommerce: null })
  window.dataLayer.push({
    event: 'view_item',
    cart_source: cartSource,
    membership_status: productInfo.dataset.membershipStatus,
    ...window.intelligemsMeta,
    userId: productInfo.dataset.customerId || null,
    page_type: 'Product Detail Page',
    color_pack: productInfo.dataset.colorPack,
    login_status: productInfo.dataset.loginStatus,
    pdp_type: productInfo.dataset.pdpType,
    pack_quantity: productInfo.dataset.packQuantity,
    ecommerce: {
      currency: productInfo.dataset.ecommerceCurrency,
      value: Number(productInfo.dataset.productPrice),
      items: [
        {
          item_id: Number(productInfo.dataset.productId),
          cart_source: cartSource,
          item_name: productInfo.dataset.productTitle,
          coupon: '',
          discount: Number(productInfo.dataset.productDiscount),
          index: 1,
          item_brand: productInfo.dataset.productBrand,
          item_category: productInfo.dataset.productCategory,
          item_category2: productInfo.dataset.productCategory2,
          item_category3: Number(productInfo.dataset.productCategory3),
          item_category4: productInfo.dataset.selectedSize,
          item_category5: productInfo.dataset.productCategory5,
          item_list_id: '',
          item_list_name: '',
          item_variant: productInfo.dataset.colorPack,
          price: Number(productInfo.dataset.productPrice),
          quantity: 1,
          style_number: productInfo.dataset.styleNumber ? productInfo.dataset.styleNumber : '',
          sku_id: productInfo.dataset.sku ? productInfo.dataset.sku : '',
          variant_id: productInfo.dataset.variantId ? productInfo.dataset.variantId : '',
        },
      ],
    },
  })
}

// Handle update of Insider Upsell on cart changes
window.addEventListener('DOMContentLoaded', () => {
  handleNotReturningSwatches()
  // checkShortSize()

  document.addEventListener('cartaddsuccess', handleCartUpdate)
  document.addEventListener('cartremovesuccess', handleCartUpdate)
  document.addEventListener('cartupdatesuccess', handleCartUpdate)
  document.addEventListener('cartmembershipaddsuccess', handleCartUpdate)

  async function handleCartUpdate() {
    const productInfo = document.querySelector('quick-modal.active product-info, deal-quick-modal.active product-info') ||
    document.querySelector('product-info')
    if (!productInfo) return

    const dataUrl = productInfo.dataset.url
    if (!dataUrl) return

    // Section responses embed cart state (free shipping, insider bar, discounts), so a
    // cart change invalidates every cached render.
    productInfo.responseCache?.clear()
    if (productInfo.responseCacheEpoch !== undefined) productInfo.responseCacheEpoch += 1

    try {
      const response = await fetch(`${dataUrl}?view=pdp-liquid-quick-add`)
      const htmlSection = await response.text()
      const html = new DOMParser().parseFromString(htmlSection, 'text/html')
      updateInsiderClubUpsell(html, productInfo)
      // Membership changes flip the "Ships Free" badge, so refresh it in the same pass.
      productInfo.updateExpectedShipping?.(html)
      updateCashbackMessage(html)
      updateStoreCreditInfo(html)
      updatePdpModal(html)
      refreshClearanceProgressBarCount(productInfo)
      checkRivoCashbackProfile()
    } catch (e) {
      console.error(e)
    }
  }
})

// Check for active auto-applicable promo and apply it
// window.addEventListener('DOMContentLoaded', () => {
//   const promoBar = document.querySelector('.liquid-pdp__discount-bar-wrapper')
//   if (!promoBar) return

//   const { autoApplyDiscount, isWeeklyDeal, useMetaobjectPromo, discountCodeName } = promoBar.dataset

//   if (autoApplyDiscount !== 'true') return

//   if (isWeeklyDeal === 'true' || useMetaobjectPromo === 'true')
//     applyDiscountCode({ code: discountCodeName })
// })

async function applyDiscountCode(args = {}) {
  const { code, openCart = false, refreshCart = true } = args
  if (!code) return

  try {
    const response = await fetch(`${window.Shopify.routes.root}discount/${code}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })

    if (response.ok) {
      document.dispatchEvent(
        new CustomEvent('cartaddsuccess', {
          detail: { openCart, refreshCart },
        }),
      )
    }
  } catch (error) {
    console.error('ERR:', error)
  }
}

function updateInsiderClubUpsell(html, root = document) {
  const insiderUpsell = html.querySelector('.js-insider-club-upsell')
  const productInfo = root.matches?.('product-info') ? root : root.querySelector('product-info')
  if (!productInfo) return
  const current = Array.from(productInfo.querySelectorAll('.js-insider-club-upsell'))
    .find((node) => node.style.display !== 'none')
  if (!insiderUpsell) {
    // Remove outgoing transition copies too: no hidden checkbox may add an
    // ineligible membership after switching to a product without the offer.
    productInfo.querySelectorAll('.js-insider-club-upsell').forEach((node) => node.remove())
    return
  }
  const reveal = revealAnimatedNode
  if (current) {
    const currentCheckbox = current.querySelector('input[name="insider-club-upsell"]')
    const nextCheckbox = insiderUpsell.querySelector('input[name="insider-club-upsell"]')
    // The section response starts unchecked. Preserve the visitor's choice
    // for the purchase experiment before viewTransition serializes the HTML.
    // No checkbox means membership is already in the cart: keep server state.
    if (
      productInfo?.hasAttribute('data-purchase-test-active') &&
      productInfo.querySelector('pdp-purchase-test')?.isEnabled?.() &&
      currentCheckbox &&
      nextCheckbox
    ) {
      nextCheckbox.checked = currentCheckbox.checked
      nextCheckbox.toggleAttribute('checked', currentCheckbox.checked)
    }
    HTMLUpdateUtility.viewTransition(
      current,
      insiderUpsell,
      [],
      [reveal],
    )
  } else {
    const slot = productInfo.querySelector('[data-insider-upsell-slot]')
    const quickAddSlot = productInfo.querySelector('.js-quick-modal-insider-wrapper')
    if (!slot && !quickAddSlot) return
    const next = insiderUpsell.cloneNode(true)
    if (quickAddSlot) quickAddSlot.appendChild(next)
    else slot.after(next)
    reveal(next)
  }

  productInfo.syncManualDiscountPriceUi?.()
  requestAnimationFrame(() => productInfo.syncManualDiscountPriceUi?.())
  window.setTimeout(() => productInfo.syncManualDiscountPriceUi?.(), 250)
}

function updateCashbackMessage(html) {
  const cashbackMessage = html.querySelector('cashback-message')
  const current = document.querySelector('cashback-message')
  if (cashbackMessage && current) {
    HTMLUpdateUtility.viewTransition(current, cashbackMessage, [])
  }
}

function updateStoreCreditInfo(html, root = document) {
  const storeCreditInfo = html.querySelector('.pdp-store-credit-info')
  const current = root.querySelector('.pdp-store-credit-info')
  if (storeCreditInfo && current) {
    HTMLUpdateUtility.viewTransition(
      current,
      storeCreditInfo,
      [],
      [
        (newNode) => {
          if (!newNode.classList.contains('anim-slide-up')) return
          newNode.setAttribute('data-animate-init', '')
          newNode.classList.add('is-visible')
        },
      ],
    )
  }
}

function updatePdpModal(html) {
  const pdpModal = html.querySelector('pdp-modal:not([is-static="true"])')
  const current = document.querySelector('pdp-modal:not([is-static="true"])')
  if (pdpModal && current) {
    HTMLUpdateUtility.viewTransition(
      current,
      pdpModal,
      [],
      [
        (newNode) => {
          if (!newNode.classList.contains('anim-slide-up')) return
          newNode.setAttribute('data-animate-init', '')
          newNode.classList.add('is-visible')
        },
      ],
    )
  }
}

function updateGalleryPromoText(html) {
  const promoText = html.querySelector('.gallery-promo-text-overlay')
  const current = document.querySelector('.gallery-promo-text-overlay')
  if (promoText && current) {
    HTMLUpdateUtility.viewTransition(current, promoText, [])
  }
}

function updateVdayBadge(html) {
  const productInfo = document.querySelector('product-info')
  const isVdayLp =
    productInfo?.dataset.isVdayLp === 'true' ||
    window.location.pathname.includes('/pages/lp/valentine')
  if (!isVdayLp) return

  const currentBadge = document.querySelector('.vday-badge')
  if (currentBadge) return // badge already exists, keep it

  // badge doesn't exist, create it
  const galleryMain = document.querySelector('.liquid-pdp__gallery-main')
  if (!galleryMain) return

  const department = productInfo?.dataset.department || ''
  const isWomens = department.toLowerCase() === 'womens'
  const badgeText = isWomens ? '💝 Great Gift for Her' : '🎁 Great Gift for Him'

  const badge = document.createElement('div')
  badge.className = 'vday-badge'
  badge.innerHTML = `<span class="vday-badge__label">${badgeText}</span>`
  galleryMain.appendChild(badge)
}
window.updateVdayBadge = updateVdayBadge

/**
 * Grab the OKE "rating-distribution" block from the page,
 * convert the counts ("51.2k" → 51200, "3k" → 3000, plain numbers as-is),
 * and return an array sorted from 1-star up to 5-star.
 * @returns {{rating: number, reviews: number}[]}
 */

function getRatingBreakdown(scope = document) {
  const block = scope.querySelector('.oke-w-breakdownModule-distribution')
  if (!block) return null

  const ratings = [...block.querySelectorAll('.oke-w-breakdownModule-distribution-label-number')]
  const counts = [...block.querySelectorAll('.oke-w-breakdownModule-distribution-count')]

  const toNumber = (txt) => {
    const clean = txt.trim().toLowerCase().replace(/,/g, '')
    if (clean.endsWith('k')) return Math.round(parseFloat(clean) * 1_000)
    if (clean.endsWith('m')) return Math.round(parseFloat(clean) * 1_000_000)
    return parseInt(clean, 10)
  }

  const data = ratings.map((rEl, i) => ({
    rating: parseInt(rEl.textContent.trim(), 10),
    reviews: toNumber(counts[i]?.textContent || '0'),
  }))

  if (data.length === 0) return null

  // Defensive: sort in case the markup isn't already 1→5 or 5→1
  return data.sort((a, b) => a.rating - b.rating)
}

document.addEventListener('DOMContentLoaded', () => updateOkendoReviewsRecommendations())

function updateOkendoReviewsRecommendations() {
  let count = 0
  const ratingTimeout = setInterval(() => {
    count++

    if (count > 10) {
      clearInterval(ratingTimeout)
      return
    }

    const ratingBreakdown = getRatingBreakdown()

    if (ratingBreakdown) {
      clearInterval(ratingTimeout)
      const ratingPercentage = percentAbove(ratingBreakdown)
      const recWrappers = document.querySelectorAll('.liquid-pdp__reviews-recommendations')
      if (recWrappers.length > 0) {
        recWrappers.forEach((recWrapper) => {
          const recPercentage = recWrapper.querySelector(
            '.liquid-pdp__reviews-recommendations-percentage',
          )
          if (recPercentage && ratingPercentage > 55) {
            recPercentage.innerHTML = `<span>${ratingPercentage}%</span>`
            revealRecommendations(recWrapper)
          }
        })
      }
    }
  }, 1000)
}

// Reveal recommendations after parent slide-up finishes (if not yet visible).
function revealRecommendations(el) {
  const parent = el.closest('.liquid-pdp__header')

  // If animations.js didn't load, no transition will fire — fade in without the pop animation
  if (!window.setupScrollAnimations) {
    el.style.animation = 'none'
    el.style.opacity = '0'
    el.classList.remove('hidden')
    requestAnimationFrame(() => {
      el.style.transition = 'opacity 0.4s ease'
      el.style.opacity = '1'
    })
    return
  }

  if (!parent || parent.classList.contains('is-visible')) {
    el.classList.remove('hidden')
    return
  }
  // Parent hasn't animated in yet — wait for its transition to finish + 100ms
  parent.addEventListener('transitionend', function onEnd(e) {
    if (e.propertyName !== 'opacity') return
    parent.removeEventListener('transitionend', onEnd)
    setTimeout(() => el.classList.remove('hidden'), 100)
  })
}

// animations.js only scans for [data-animate] on DOMContentLoaded, so nodes swapped in
// later stay at their hidden start state. Mark them as already revealed.
function revealAnimatedNode(node) {
  if (!node.classList.contains('anim-slide-up')) return
  node.setAttribute('data-animate-init', '')
  node.classList.add('is-visible')
}

const SHIPS_FREE_HIDDEN_CLASS = 'expected-shipping__ships-free--hidden'
const SHIPS_FREE_FADE_MS = 300

function getShipsFreeBadge(expectedShipping) {
  return expectedShipping?.querySelector('.js-ships-free')
}

// '' covers both a missing badge and a hidden one: no message is on screen.
function getShipsFreeBadgeState(badge) {
  if (!badge || badge.classList.contains(SHIPS_FREE_HIDDEN_CLASS)) return ''
  return badge.dataset.shipsFreeState || ''
}

// Carry the on-screen badge into the incoming row so the section swap itself is
// invisible; the state change is animated afterwards.
function carryOverShipsFreeBadge(currentRow, nextRow) {
  const current = getShipsFreeBadge(currentRow)
  const next = getShipsFreeBadge(nextRow)
  if (!current || !next) return
  next.replaceWith(current.cloneNode(true))
}

// Always re-read the badge: another update can swap the whole row between frames, which
// would otherwise leave the animation running on a detached node.
function getLiveShipsFreeBadge(productInfo) {
  return getShipsFreeBadge(productInfo.expectedShipping)
}

// The state attribute is left alone so the message keeps its color while fading out;
// getShipsFreeBadgeState already treats a hidden badge as stateless.
function hideShipsFreeBadge(productInfo) {
  requestAnimationFrame(() =>
    getLiveShipsFreeBadge(productInfo)?.classList.add(SHIPS_FREE_HIDDEN_CLASS),
  )
}

// Removing the hidden class clears its `animation: none`, restarting the keyframes.
function showShipsFreeBadge(productInfo, nextBadge) {
  const badge = getLiveShipsFreeBadge(productInfo)
  if (!badge) return
  badge.classList.add(SHIPS_FREE_HIDDEN_CLASS)
  badge.replaceChildren(...nextBadge.cloneNode(true).childNodes)
  badge.dataset.shipsFreeState = nextBadge.dataset.shipsFreeState || ''
  requestAnimationFrame(() =>
    getLiveShipsFreeBadge(productInfo)?.classList.remove(SHIPS_FREE_HIDDEN_CLASS),
  )
}

// Pops in with the reviews-recommendations animation, fades out on the way back.
function animateShipsFreeBadge(productInfo, nextBadge) {
  const badge = getLiveShipsFreeBadge(productInfo)
  if (!badge || !nextBadge) return
  const currentState = getShipsFreeBadgeState(badge)
  const nextState = getShipsFreeBadgeState(nextBadge)
  if (currentState === nextState) return
  if (!nextState) {
    hideShipsFreeBadge(productInfo)
    return
  }
  if (!currentState) {
    showShipsFreeBadge(productInfo, nextBadge)
    return
  }
  // Swapping one message for the other: fade the old one out first.
  hideShipsFreeBadge(productInfo)
  setTimeout(() => showShipsFreeBadge(productInfo, nextBadge), SHIPS_FREE_FADE_MS)
}

/**
 * Return % of reviews with rating ≥ threshold.
 * @param {Array<{rating:number, reviews:number}>} dist - e.g. [{rating:1,reviews:3000}, …]
 * @param {number} threshold - default 3.5
 * @returns {number} percentage, 0-100
 */
function percentAbove(dist, threshold = 3) {
  const total = dist.reduce((sum, { reviews }) => sum + reviews, 0)
  if (!total) return 0 // guard against empty / zero totals
  const qualifying = dist
    .filter(({ rating }) => rating >= threshold)
    .reduce((sum, { reviews }) => sum + reviews, 0)
  return Math.round((qualifying / total) * 100)
}

function logProductInfoDataAttributes() {
  if (!logPDP) return
  const productInfo = document.querySelector('product-info')
  if (!productInfo) return
}

// Blotout forwards ViewContent to Klaviyo as "Viewed Product"; an incomplete payload renders broken browse-abandonment emails.
function sendViewContent(currency, contents) {
  if (typeof edgetag !== 'function') return false
  const item = contents?.[0]
  if (!item?.id || !item.title || !item.handle || !(item.item_price > 0)) return false
  edgetag('tag', 'ViewContent', { currency, value: item.item_price, contents })
  return true
}

function initHomepageProductInfoViewContent() {
  if (window.location.pathname !== '/') return
  const productInfo = document.querySelector('product-info')
  if (!productInfo || typeof edgetag !== 'function') {
    // console.log('initHomepageProductInfoViewContent', 'not found')
    return
  }

  let hasFired = false
  const observer = new IntersectionObserver(
    (entries) => {
      if (hasFired) return
      const entry = entries[0]
      if (!entry?.isIntersecting || entry.intersectionRatio <= 0) return

      const imageUrl =
        document
          .querySelector('.liquid-pdp__gallery-main')
          ?.querySelector('.first-product-image img')?.src || null

      const contents = [
        {
          id: productInfo.dataset.productId,
          variantId: productInfo.dataset.variantId,
          sku: productInfo.dataset.sku,
          item_price: Number(productInfo.dataset.productPrice),
          title: productInfo.dataset.productTitle,
          handle: productInfo.dataset.productHandle,
          category: productInfo.dataset.productCategory,
          url: getProductUrl(productInfo.dataset.url),
          image: imageUrl,
          quantity: 1,
          type: 'product',
        },
      ]
      if (!sendViewContent(productInfo.dataset.ecommerceCurrency, contents)) return
      hasFired = true
      observer.disconnect()
      // console.log(
      //   'ProductInfoInView',
      //   contents[0].id,
      //   contents[0].handle,
      //   getProductUrl(productInfo.dataset.url),
      // )
    },
    { threshold: 0.1 },
  )
  observer.observe(productInfo)
}

function getProductUrl(datasetUrl) {
  if (datasetUrl) return `${window.location.origin}${datasetUrl}`
  return window.location.href
}

window.addEventListener('load', initHomepageProductInfoViewContent)

if (logPDP) document.addEventListener('DOMContentLoaded', logProductInfoDataAttributes)

// Singles color swatch inputs of the main PDP (excludes the custom pack builder's copies).
// `data-color-list` holds the color text exactly as the UI shows it (the color-pattern label
// for image swatches, otherwise the colorwaysV2 color) — see snippets/product-color-swatches.liquid
function getSinglesSwatchInputs() {
  const productInfo = document.querySelector('quick-modal.active product-info, deal-quick-modal.active product-info') ||
    document.querySelector('product-info')
  if (!productInfo) return []
  return Array.from(
    productInfo.querySelectorAll(
      '.liquid-pdp__product-color-swatch input[data-color-list], input[name="single"][data-color-list]',
    ),
  ).filter((input) => !input.closest('t-custom-pack'))
}

// Builds [{ color, handle }] for every single, de-duplicated by product handle
function getSinglesColorAndHandle() {
  const seenHandles = new Set()
  return getSinglesSwatchInputs()
    .filter((input) => {
      if (seenHandles.has(input.value)) return false
      seenHandles.add(input.value)
      return true
    })
    .map((input) => ({ color: input.dataset.colorList.trim(), handle: input.value }))
}

// Logs the singles array as JSON. Run from the console: logSinglesColorAndHandle()
function logSinglesColorAndHandle() {
  console.log(JSON.stringify(getSinglesColorAndHandle(), null, 2))
}
window.logSinglesColorAndHandle = logSinglesColorAndHandle
